import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import SignatureCanvas from "react-signature-canvas";
import { styled } from "styled-components";

import PhotoUploader, { type CameraFailure } from "./PhotoUploader/PhotoUploader";
import SuccessScreen from "./SuccessScreen";
import ErrorMessage from "./ErrorMessage";
// TW-0504 — the pieces the questionnaire and the consent draw, each memoized on its own props, and the steady doors they hold
import {
  AcknowledgmentItem,
  CashPreferredItem,
  MISSED_LINE,
  NoticeItem,
  NoticeSheet,
  OwnClauseItem,
  OwnQuestionItem,
  PhotoReleaseItem,
  PrivacyNoticeItem,
  QuestionnaireItem,
  StateFieldBox,
  useStableHandler,
} from "./FormItems";
import {
  guardianConditionsFor,
  MINOR_AGE,
  allowsEmancipatedAdult,
  minorRefusal,
  asksGuardianProof,
  asksEarPiercing,
  bothParentsPlace,
  earExemptionOf,
  floorEarlobeExceptionApplies,
  guardianAttestationsFor,
  minorSentence,
  minorsRuleFor,
  papersAsked,
  minorsTicked,
  stateSetsNoRule,
  requiresBothParents,
  requiresBothParentsPresent,
  requiresCoverupOnly,
  ruleAsksFor,
  signsAsAdult,
  stateNameOf,
  type Attestation,
  type Paper,
  type Refusal,
} from "./minors";
import { API_URL, SITE_PRIVACY_URL, SITE_TERMS_URL } from "./config";
import { hold, track, trackError } from "./beacon";
import { useApp } from "./App";
import { internalFields } from "./internal";
import { STATE_NAMES, answerRequiredBy, idPhotoLibraryStartsOn, medicationsWordsFor, noticeText, noticesFor, requirementsFor, sectionsFor, settingForcedBy, stateFieldsFor, withDemoQuery, wordedQuestionDetailsKeys, wordedQuestionKeys, wordedQuestionsFor, type Notice, type StateField } from "./stateForms";
// 2026-09-24 — the shop's own clauses, in the owner's words (Shop 53 Custom Tattoo); ownClauses.ts decides what the key means
import { ownClauseError, ownClausesOf } from "./ownClauses";
// TW-0412 — the shop's own questions, in the owner's words (Perfecto Bliss); ownQuestions.ts decides what the key means
import {
  firstOwnQuestionUnanswered,
  ownQuestionNoteShown,
  ownQuestionsOf,
  ownQuestionsPosted,
  type OwnAnswer,
  type OwnQuestion,
} from "./ownQuestions";
// 2026-10-02 — cash preferred, the card-fee box (Reckless Heart Tattoo); cashPreferred.ts decides what the key means
import { cashPreferredOf, cashPreferredSentence } from "./cashPreferred";
// 2026-10-08 — a shop in HIPAA mode: its notice of privacy practices and its photo release; hipaaForm.ts decides what they mean
import { PORTFOLIO_CLAUSE, photoReleaseOf, privacyNoticeOf, releaseWords, showsPortfolioClause } from "./hipaaForm";
import { composePrice, NO_PRICE_PARTS, PRICE_PART_MAX, prefillRate, priceStyleOf, shopRateOf, type PriceKind, type PriceParts } from "./price";
// TW-0275 — the longest each text box takes, the one number the API's signing door refuses past (fieldLimits.ts)
import { FIELD_LIMITS } from "./fieldLimits";
import { growWithText } from "./growWithText";
import { asksPaymentMethod, isPaymentMethod, PAYMENT_METHODS, type PaymentMethod } from "./payment";
import { asksPhysician, PHYSICIAN_FIELDS } from "./physician";
import { photoSourceFields, savedPhotoToDataUri } from "./savedPhoto";
import { ID_TYPES, idFieldsPosted, idNumberAsked, idNumberGiven, idTypeRequired, isIdType, type IdType } from "./idType";
import { isDemoShop } from "./subdomain";
import { suggestEmail, tidyEmail } from "./email-suggest";
import {
  SOMEONE_ELSE,
  artistNameFor,
  artistOptions,
  asksArtistFor,
  asksWhichArtist,
  choiceAfterListChange,
  pickerCopy,
  someoneElseLabel,
  withArtistPicked,
} from "./artists";
import { FLAG, type Lang, type Strings, detectLang, enabledLangs, setDocumentLang, strings, termsHref, withLanguage } from "./i18n";
// 2026-10-18 — a tattoo convention's artist field: a search over the show's roster (convention.ts, docs/CONVENTIONS.md)
import ArtistSearch from "./ArtistSearch";
import { conventionCopy, conventionOf, searchesArtists } from "./convention";
import { initials } from "./mark";
import {
  type AddressSuggestion,
  SUGGEST_DEBOUNCE_MS,
  applyPick,
  fetchAddressPick,
  fetchAddressSuggestions,
  newAddressSession,
  shouldSuggest,
} from "./address";
import {
  applyPrefill,
  DEMO_LOOKUP_PAIR,
  demoRecognition,
  emailLooksComplete,
  fetchRecognition,
  lookupKey,
  offersReturningLookup,
  phoneLooksComplete,
} from "./recognition";
import {
  JEWELRY_MATERIALS,
  PIERCING_LOCATIONS,
  PIERCING_QUESTIONS,
  ackError,
  ackLabel,
  ackOrderFor,
  asksWhichService,
  copyFor,
  initialService,
  jewelryMaterialLabel,
  fixedJewelryMaterial,
  locationLabel,
  piercingQuestionsFor,
  piercingSpotsFor,
  questionWords,
  sectionWithoutArtist,
  servicesFor,
  shopType,
  submissionForm,
  type AckKey,
  type ServiceType,
} from "./service";
// TW-0279 — a cosmetic tattoo's procedures, its visit, its questions and what the shop's state bars (cosmetic.ts)
import {
  COSMETIC_QUESTIONS,
  COSMETIC_VISITS,
  asksProviderInstructions,
  cosmeticAnswerRefusal,
  cosmeticBarsFor,
  cosmeticLiftsFor,
  cosmeticLabel,
  cosmeticProceduresOffered,
  cosmeticQuestionWords,
  cosmeticQuestionsFor,
  postedProcedures,
  providerInstructionsNeeded,
  providerInstructionsRuleFor,
  scalpNamed,
} from "./cosmetic";
import { doorReasonOf, goToRefusal, isNativeStop, nativeStop, refusalField, refusalStep, refusalTarget, stepOfBox, type RefusalStep } from "./refusal";
// 2026-10-31 — which of the two lot photos the shop requires, and which a submit is missing (White Lotus Body Arts, Ventura)
import { missingLotPhoto, requiredLotPhotosOf } from "./lotPhotos";
// TW-0503 (2026-10-29) — THE AGE IS READ OFF THE BIRTH DATE on the day of signing, the signing door's own rule mirrored
// (birthDate.ts; services/waiver-api/consolidated_api/birth_date.py), and the age box is gone
import { ageOn, compareDays, isoOf, signingDayOf, todayHere, type CalendarDay } from "./birthDate";
// 2026-10-01 — a health question left blank is refused, and the client taken to it (healthQuestions.ts has why)
import { DETAILS_BOX, firstHealthGap, hasDetails, healthQuestionsAsked, optionalHealthQuestions, type DetailsBox, type HealthQuestion } from "./healthQuestions";
// TW-0293 — the two questions every form used to ask, which follow the shop's state since 2026-10-15
import { COMMUNICABLE_DISEASE_SETTING, DRUGS_OR_ALCOHOL_SETTING, asksDefaultQuestion } from "./defaultQuestions";
// TW-0628 (2026-11-04) — a protection the form applies is the shop owner's switch, off only with the risk accepted
import { PREGNANCY_REFUSAL, protectionOff } from "./protections";
// 2026-10-08 — the client's own copy on the done screen, for a shop in HIPAA mode whose state's rule hands the client one
import { clientCopyOf, showsClientCopy, type ClientCopy } from "./clientCopy";
// TW-0500 — a short-lived draft in the tab, so a reload loses nothing typed (draft.ts says what it keeps, and what never)
import {
  DRAFT_WRITE_AFTER_MS,
  answersOf,
  draftOf,
  draftPage,
  forgetDraft,
  formVersion,
  neverDraftedFor,
  partsOf,
  readDraft,
  ticksOf,
  withDraft,
  writeDraft,
  type Draft,
} from "./draft";

const SignatureCanvasContainer = styled.div`
  .sigCanvas {
    border: 2px solid #e5e7eb;
    width: 100%;
    height: 100%;
  }
`;

const PreviewImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 300px;
`;

const PreviewImageContainerImg = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
`;

type FormState = {
  firstName: string;
  lastName: string;
  birthDateMonth: string;
  birthDateDay: string;
  birthDateYear: string;
  phoneNumber: string;
  email: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  communicableDiseaseDetails: string;
  skinConditionsDetails: string;
  medicalHistoryDetails: string;
  medicationAndBloodThinners: string;
  nameOfArtist: string;
  tattooDescription: string;
  locationOnBody: string;
  // TW-0059, a piercing only. `submissionForm` (service.ts) takes every one of these back out of
  // the posted body for a tattoo, so a tattoo shop posts what it has posted since 2023.
  jewelryMaterial: string;
  jewelryNotes: string;
  // TW-0279, a cosmetic tattoo only: the procedures picked (cosmetic.ts values, in the list's order) and what this visit
  // is. `submissionForm` takes both out of every other body (SERVICE_ONLY_FIELDS), as it does the jewelry above.
  cosmeticProcedures: string[];
  cosmeticVisit: string;
  // TW-0154 — what the work costs, asked only by a shop that turned `public_data.ask_price` on, and
  // optional even then. A STRING, deliberately unparsed: "$150", "150-200" and "TBD" are all things
  // a client types at a counter, and what the shop keeps is what they typed. `handleSubmit` takes it
  // back out of the posted body for every shop that never asked.
  price: string;
  // 2026-09-23 — how the client is paying: "cash" | "card" | "other", or "" before they pick (payment.ts). Asked only
  // by a shop that turned `public_data.ask_payment_method` on, and REQUIRED there; `handleSubmit` takes it back out
  // of the posted body for every shop that did not ask, so every other waiver's body is the one it has always been.
  paymentMethod: string;
  metalAllergyDetails: string;
  keloidScarringDetails: string;
  recentDentalWorkDetails: string;
  // TW-0279 — the box under each of a cosmetic tattoo's own questions (keloidScarringDetails above is shared), taken out of
  // every body whose page did not ask its question (service.ts submissionForm)
  isotretinoinDetails: string;
  diabetesDetails: string;
  immuneTreatmentDetails: string;
  autoimmuneDetails: string;
  earlierWorkDetails: string;
  botoxOrFillerDetails: string;
  peelOrLaserDetails: string;
  coldSoresDetails: string;
  contactsOrLashesDetails: string;
  eyeConditionDetails: string;
  surgeryOrRadiationDetails: string;
  // TW-0104 — the state's own questions, asked only where the shop's state requires them (stateForms.ts);
  // what is not asked is not posted, so every other shop's record is byte for byte what it was.
  allergiesDetails: string;
  bleedingDisordersDetails: string;
  // TW-0526 (M05, 2026-10-28) — the boxes Arkansas's and Hawaii's armed-forces questions open on a yes (stateForms.ts
  // wordedQuestionsFor `details`), stripped from the posted body where the question was not asked
  armedServicesDetails: string;
  armedForcesDetails: string;
  // WHO TO CALL (2026-09-11) — asked when the shop turned it on or its state's rule requires it, and
  // STRIPPED from the posted body otherwise, so every other waiver is the one it has always been.
  emergencyContactName: string;
  emergencyContactRelationship: string;
  emergencyContactPhone: string;
  emergencyContactAddress: string;
  // THE CLIENT'S PHYSICIAN (2026-09-25, Squirtin' Ink) — asked when the shop turned it on for the service being
  // signed (physician.ts), OPTIONAL, and STRIPPED from the posted body otherwise, so every other waiver is the
  // one it has always been.
  physicianName: string;
  physicianPhone: string;
  physicianAddress: string;
  // TW-0128 — a minor's piercing with a parent present. Filled in only when all three gates open
  // (the shop turned it on, the state hands the parent a route, this placement is one that state
  // allows), and STRIPPED from the posted body otherwise: an adult's waiver is byte for byte the
  // one this form has posted since 2023.
  guardianName: string;
  guardianRelationship: string;
  /** TW-0153 — "yes" | "no" | "" before they have answered. A string, like every other field here,
   *  so one handleInputChange covers it. */
  coveringExisting: string;
  /** TW-0153 — "yes" | "no" | "" (never asked). Asked only where the rule carries `emancipated_adult` (minors.ts). */
  emancipatedMinor: string;
  /** TW-0225 — "yes" | "no" | "" before they have answered: is this an ear piercing the state writes out of its minors
   *  rule (minors.ts asksEarPiercing)? Posted as a boolean where it was asked, and never anywhere else. A new spot asks
   *  again (handleInputChange), because an answer is about the spot it was given for. */
  earPiercing: string;
  /** TW-0279 slice 10 — "yes" | "no" | "" before they have answered: does this scalp work cover scars (cosmetic.ts
   *  scalpNamed), asked on a cosmetic signing whose form asks about the doctor's written instructions? A scar it covers is
   *  camouflage, whose instructions the record keeps. Posted as a boolean where it was asked, and stripped everywhere else
   *  (service.ts COSMETIC_ONLY_FIELDS). */
  scalpCoversScars: string;
  /** TW-0227 — the other parent's written permission, where the shop's city or county wants it from both parents
   *  (Hammond, East Chicago) or wants both there (Delaware County): "signed" (they sign here) | "photo" (a photo of the
   *  permission they signed) | "none" (there is no second parent) | "" before the parent who is there picks. Posted
   *  where asked, with the name only on "signed", and STRIPPED everywhere else. */
  secondParentChoice: string;
  secondParentName: string;
  /** TW-0488 (2026-10-26) — the number on the client's ID, asked only where the shop's state's rule keeps it (Maine, Missouri;
   *  idType.ts), taken as typed. STRIPPED from the posted body everywhere else, so every other waiver is the one it has been. */
  idNumber: string;
  /** TW-0526, the client-form side (2026-10-29) — THE BOXES A STATE'S RULE NAMES THAT NO YES OR NO CARRIES (stateForms.ts
   *  stateFieldsFor): the time the client last ate (Oklahoma, HH:MM from the phone's own picker), their sex as it appears on the ID
   *  (Mississippi, a value of the list), the parent's or guardian's phone (every minor's route, required in Utah). Posted ONLY
   *  where the page drew the box; STRIPPED everywhere else, so every other waiver is the body it has been. */
  lastAteAt: string;
  sexOnId: string;
  guardianPhone: string;
  questionnaire: {
    influenceOfDrugsOrAlcohol: string;
    pregnantOrNursing: string;
    communicableDisease: string;
    skinConditions: string;
    metalAllergy: string;
    keloidScarring: string;
    recentDentalWork: string;
    allergies: string;
    bleedingDisorders: string;
    // 2026-09-17 — Georgia's "eaten in the last four (4) hours" (r. 511-3-8-.08(3)(k)): yes or no, no details
    eatenRecently: string;
    // 2026-10-08 (TW-0279 slice 8) — Michigan's numbing product (R 333.13109(5)): yes or no, and a yes asks for a photo of
    // the doctor's prescription (photoOfNumbingPrescription, top-level), never a box
    numbingProduct: string;
    // TW-0270 slice 4 (2026-10-16) — the five a state's rule can list as its own questions (scripts/state-forms.py VOCAB), each
    // a yes or a no with no box, posted only where the shop's state names it; `diabetesHistory`, never `diabetes`, which is the
    // cosmetic form's own question below
    diabetesHistory: string;
    seizuresOrFainting: string;
    heartOrBloodPressure: string;
    bloodThinnersRecently: string;
    pregnantRecently: string;
    // TW-0279 — a cosmetic tattoo's own (cosmetic.ts COSMETIC_QUESTIONS; keloidScarring is above), each posted only
    // where the page asked it; `pregnant` is a state's own (New Jersey's pregnancy bar), asked only where its rule refuses on
    // it, and so are `barredArea` (the face question, Georgia's and South Carolina's) and `herpesAtSite` (California's),
    // asked only where their state's record asks them (TW-0279 slice 9)
    barredArea: string;
    pregnant: string;
    isotretinoin: string;
    diabetes: string;
    immuneTreatment: string;
    autoimmune: string;
    earlierWork: string;
    botoxOrFiller: string;
    peelOrLaser: string;
    coldSores: string;
    herpesAtSite: string;
    contactsOrLashes: string;
    eyeCondition: string;
    surgeryOrRadiation: string;
  };
  acknowledgmentAndWaiver: {
    explained: boolean;
    outcome: boolean;
    restrictions: boolean;
    permanentChange: boolean;
    photograph: boolean;
    refund: boolean;
    allergic: boolean;
    takeCare: boolean;
    infection: boolean;
    migration: boolean;
    indemnify: boolean;
    information: boolean;
    // TW-0279 — a cosmetic tattoo's own clauses (service.ts ACK_ORDER.cosmetic), on a cosmetic body only, in the order
    // shown; every other body loses them in the strip (ALL_ACK_KEYS is the union)
    permanentPigment: boolean;
    darkerAtFirst: boolean;
    fadesAndRefreshes: boolean;
    resultsVary: boolean;
    approvedShapeAndColor: boolean;
    pigmentNotApproved: boolean;
    normalHealing: boolean;
    pigmentAllergy: boolean;
    numbingReaction: boolean;
    numbingOregon: boolean;
    numbingMichigan: boolean;
    answeredTruthfully: boolean;
    laterTreatments: boolean;
    mriScan: boolean;
    aftercareGiven: boolean;
    changesWithAge: boolean;
    lipsColdSore: boolean;
    eyelinerNearEye: boolean;
    areolaTreatedSkin: boolean;
    scalpSessions: boolean;
    camouflageMatch: boolean;
    microbladingFades: boolean;
    termsAndConditions: boolean;
    // TW-0113 — the OPTIONAL boxes on this form. Everything above them is a waiver clause the
    // signature depends on; these two are consents the form never gates on. They are two because a
    // carrier pre-submit check refuses one box that mixes service follow-up with promotional
    // content: the client agrees to each independently, or to neither.
    hearFromShop: boolean;
    shopMarketing: boolean;
  };
  signedDate: string; // Change type according to datepicker library
};

/** TW-0504 — the questionnaire's keys and the waiver boxes' keys, the names the pieces that draw them carry (FormItems.tsx) */
export type QuestionName = keyof FormState["questionnaire"];
export type ClauseName = keyof FormState["acknowledgmentAndWaiver"];

/** 2026-10-26 — the refusals about a box the signature depends on: while one is up, every such box left unticked is marked */
const BOX_REFUSALS = ["acknowledgment_unticked", "notice_unticked", "own_clause_unticked", "cash_preferred_unticked", "terms_unticked"];
// …and the mark such a box wears lives with the boxes since TW-0504, in FormItems.tsx (MISSED_LINE, which the page's own
// marks share, is imported above)

/** TW-0504 — no cosmetic procedures: one list, the same on every render (Form's `procedures`) */
const NO_PROCEDURES: string[] = [];
// TW-0526, the client-form side (2026-10-29) — no statement and no box, one array each, so a page with none hands every piece the
// same list on every render
const NO_STATEMENTS: Attestation[] = [];
const NO_FIELDS: StateField[] = [];
/** Every FormState key of the kind, fixed: a page whose shop has no state never loads the contract's `items`, so the keys it takes
 *  off the body cannot be read from it (stateForms.ts stateFieldKeys, which a test holds this list to). */
export const STATE_FIELD_FORM_KEYS = ["lastAteAt", "sexOnId", "guardianPhone"] as const;
// …and what makes a box answered on the page: a time the phone's picker gave (HH:MM), one of the list's values, a phone with the
// digits of one (an emergency contact's rule: a number nobody can dial is not one; the door reads it as the client's own is read)
function fieldAnswered(box: StateField, value: string): boolean {
  const given = `${value ?? ""}`.trim();
  if (box.input === "time") return /^([01]\d|2[0-3]):[0-5]\d/.test(given);
  if (box.input === "select") return box.options.some((option) => option.value === given);
  return given.replace(/\D/g, "").length >= 7;
}

function EnteredCharactersCounter({
  value,
  maxLength,
}: {
  value: string;
  maxLength: number;
}) {
  const currentCount = value.length;
  // Grey until the limit is close, then dark red AND bold.
  //
  // It used to be grey → green → yellow, and that was wrong twice over. Neither colour was
  // readable: green-500 measured 2.28:1 on the white field and yellow-500 1.92:1, against the
  // 4.5:1 a person needs (`./run.sh contrast`, 2026-09-07). And green→yellow is a HUE-ONLY
  // change, which says nothing at all to a red-green colourblind reader — so the one state worth
  // noticing now carries weight as well as colour, which is legible to everyone.
  const nearlyFull = currentCount / maxLength > 0.8;
  return (
    <div
      className={`absolute text-sm top-0 right-0 ${nearlyFull ? "text-red-700 font-semibold" : "text-gray-500"}`}
    >
      {value.length}/{maxLength}
    </div>
  );
}

function FormPhotoUploader({
  takingPhoto,
  setTakingPhoto,
  photo,
  setPhoto,
  setSubmissionError,
  photoAltText,
  T,
  allowLibrary = false,
  onSource,
  libraryTestId = "id-photo-library-input",
  onCameraFailed,
}: {
  takingPhoto: boolean;
  setTakingPhoto: (takingPhoto: boolean) => void;
  photo: string | null;
  setPhoto: (photo: string | null) => void;
  setSubmissionError: (error: string) => void;
  photoAltText: string;
  T: Strings;
  /** TW-0171 — offer "Choose from library" under "Take photo": a SAVED photo, for an ID that lives on
   *  the phone. Only the two ID uploaders pass it, and only where the shop allows it. */
  allowLibrary?: boolean;
  /** Which way the photo on screen came in, told to the form each time a new one lands, so the posted
   *  body can say so (`idPhotoSource`). */
  onSource?: (source: "camera" | "library") => void;
  /** TW-0227 — the hidden library input's test id. Every uploader shared one until the fourth photo, so a test had to
   *  count its way down the page to the one it meant; the ID uploaders keep the old id, and a new one names itself. */
  libraryTestId?: string;
  /** TW-0501 — told when the camera screen's camera does not open (PhotoUploader.tsx), so the form can beacon it */
  onCameraFailed?: (reason: CameraFailure) => void;
}) {

  // It's really important for takingPhoto and setTakingPhoto to be set,
  // as otherwise the webcam photo assignment will fail.

  const onReceivedPhoto = useCallback(({
    dataUri,
  }: {
    dataUri: string;
  }) => {
    setTakingPhoto(false);
    setPhoto(dataUri);
    setSubmissionError("");
    onSource?.("camera");
  }, [setTakingPhoto, setPhoto, setSubmissionError, onSource]);

  const onCanceled = useCallback(() => {
    setTakingPhoto(false);
  }, [setTakingPhoto]);

  // TW-0171 — the library's own door: a file input WITHOUT `capture`, which is what makes a phone offer
  // its photo library (iOS lists "Photo Library" first; Android opens its picker). Hidden, and opened by
  // the visible button, so the page keeps one look.
  const libraryInput = useRef<HTMLInputElement>(null);
  const [openingSaved, setOpeningSaved] = useState(false);
  const onSavedPicked = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const file = input.files?.[0];
    // cleared so picking the SAME photo again, after a Clear, still fires a change
    input.value = "";
    if (!file) return;
    setOpeningSaved(true);
    const result = await savedPhotoToDataUri(file);
    setOpeningSaved(false);
    // No beacon event of its own: a waiver signed this way already says so on its row (`idPhotoSource`),
    // which is the count worth having, and the beacon's allowlist lives on two sides for one number.
    if (result.ok) {
      setPhoto(result.dataUri);
      setSubmissionError("");
      onSource?.("library");
    } else {
      setSubmissionError(result.reason === "too_big" ? T.savedPhotoTooBig : T.savedPhotoUnreadable);
    }
  }, [setPhoto, setSubmissionError, onSource, T]);

  return <>
    {takingPhoto ? (
      <PhotoUploader
        onReceivedPhoto={onReceivedPhoto}
        onCanceled={onCanceled}
        onCameraFailed={onCameraFailed}
        labels={{
          retake: T.retake,
          usePhoto: T.usePhoto,
          // 2026-10-26 — the camera screen's other words, in the client's language: the shutter's and the X's names, and what it
          // says when the camera does not open (PhotoUploader.tsx)
          shutter: T.cameraShutter,
          close: T.cameraClose,
          cameraBlocked: T.cameraBlocked,
          useCameraApp: T.cameraApp,
          preview: T.cameraPreview,
          unreadable: T.savedPhotoUnreadable,
        }}
      />
    ) : (
      <>
        {!photo ? (
          <div className="flex flex-col items-start space-y-2">
            <button
              className="bg-transparent hover:bg-blue-700 text-blue-700 font-semibold hover:text-white py-2 px-4 border border-blue-500 hover:border-transparent rounded"
              onClick={() => setTakingPhoto(true)}
              type="button"
              disabled={openingSaved}
            >
              {T.takePhoto}
            </button>
            {allowLibrary && (
              <>
                <button
                  className="bg-transparent text-blue-700 font-semibold underline py-2 px-1 rounded disabled:text-gray-600"
                  onClick={() => libraryInput.current?.click()}
                  type="button"
                  disabled={openingSaved}
                >
                  {openingSaved ? T.openingPhoto : T.chooseFromLibrary}
                </button>
                <input
                  ref={libraryInput}
                  className="hidden"
                  type="file"
                  accept="image/*"
                  aria-label={T.chooseFromLibrary}
                  data-testid={libraryTestId}
                  onChange={onSavedPicked}
                />
              </>
            )}
          </div>
        ) : (
          <div>
            <PreviewImageContainer>
              <PreviewImageContainerImg
                src={photo}
                alt={photoAltText}
              />
            </PreviewImageContainer>
            <div className="mt-2 flex space-x-4 justify-center">
              <button
                className="bg-transparent hover:bg-blue-700 text-blue-700 font-semibold hover:text-white py-2 px-4 border border-blue-500 hover:border-transparent rounded"
                onClick={() => {
                  setTakingPhoto(true);
                  setPhoto("");
                }}
                type="button"
              >
                {T.retakePhoto}
              </button>
              <button
                className="bg-transparent hover:bg-blue-700 text-blue-700 font-semibold hover:text-white py-2 px-4 border border-blue-500 hover:border-transparent rounded"
                onClick={() => {
                  setTakingPhoto(false);
                  setPhoto("");
                }}
                type="button"
              >
                {T.clearPhoto}
              </button>
            </div>
          </div>
        )}
      </>
    )}
  </>
}

/** The form as a page load opens it: every box empty, every tick off, today's date in the signed-date box. A function at the
 *  top of the file since TW-0500 (2026-10-26): the draft in the tab is what differs from it, and its shape is the draft's
 *  version (draft.ts). */
function blankForm(): FormState {
  return {
    firstName: "",
    lastName: "",
    birthDateMonth: "",
    birthDateDay: "",
    birthDateYear: "",
    phoneNumber: "",
    email: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    zip: "",
    communicableDiseaseDetails: "",
    skinConditionsDetails: "",
    medicalHistoryDetails: "",
    medicationAndBloodThinners: "",
    nameOfArtist: "",
    tattooDescription: "",
    locationOnBody: "",
    jewelryMaterial: "",
    jewelryNotes: "",
    cosmeticProcedures: [],
    cosmeticVisit: "",
    price: "",
    paymentMethod: "",
    metalAllergyDetails: "",
    keloidScarringDetails: "",
    recentDentalWorkDetails: "",
    isotretinoinDetails: "",
    diabetesDetails: "",
    immuneTreatmentDetails: "",
    autoimmuneDetails: "",
    earlierWorkDetails: "",
    botoxOrFillerDetails: "",
    peelOrLaserDetails: "",
    coldSoresDetails: "",
    contactsOrLashesDetails: "",
    eyeConditionDetails: "",
    surgeryOrRadiationDetails: "",
    allergiesDetails: "",
    bleedingDisordersDetails: "",
    armedServicesDetails: "",
    armedForcesDetails: "",
    emergencyContactName: "",
    emergencyContactRelationship: "",
    emergencyContactPhone: "",
    emergencyContactAddress: "",
    physicianName: "",
    physicianPhone: "",
    physicianAddress: "",
    // TW-0128 — see FormState. Empty for every adult, and never posted for one.
    guardianName: "",
    guardianRelationship: "",
    coveringExisting: "",
    emancipatedMinor: "",
    earPiercing: "",
    scalpCoversScars: "",
    secondParentChoice: "",
    secondParentName: "",
    idNumber: "",
    lastAteAt: "",
    sexOnId: "",
    guardianPhone: "",
    questionnaire: {
      influenceOfDrugsOrAlcohol: "",
      pregnantOrNursing: "",
      communicableDisease: "",
      skinConditions: "",
      metalAllergy: "",
      keloidScarring: "",
      recentDentalWork: "",
      allergies: "",
      bleedingDisorders: "",
      eatenRecently: "",
      numbingProduct: "",
      diabetesHistory: "",
      seizuresOrFainting: "",
      heartOrBloodPressure: "",
      bloodThinnersRecently: "",
      pregnantRecently: "",
      barredArea: "",
      pregnant: "",
      isotretinoin: "",
      diabetes: "",
      immuneTreatment: "",
      autoimmune: "",
      earlierWork: "",
      botoxOrFiller: "",
      peelOrLaser: "",
      coldSores: "",
      herpesAtSite: "",
      contactsOrLashes: "",
      eyeCondition: "",
      surgeryOrRadiation: "",
    },
    acknowledgmentAndWaiver: {
      explained: false,
      outcome: false,
      restrictions: false,
      permanentChange: false,
      photograph: false,
      refund: false,
      allergic: false,
      takeCare: false,
      infection: false,
      migration: false,
      indemnify: false,
      information: false,
      permanentPigment: false,
      darkerAtFirst: false,
      fadesAndRefreshes: false,
      resultsVary: false,
      approvedShapeAndColor: false,
      pigmentNotApproved: false,
      normalHealing: false,
      pigmentAllergy: false,
      numbingReaction: false,
      numbingOregon: false,
      numbingMichigan: false,
      answeredTruthfully: false,
      laterTreatments: false,
      mriScan: false,
      aftercareGiven: false,
      changesWithAge: false,
      lipsColdSore: false,
      eyelinerNearEye: false,
      areolaTreatedSkin: false,
      scalpSessions: false,
      camouflageMatch: false,
      microbladingFades: false,
      termsAndConditions: false,
      // unticked, and it stays unticked unless the client taps it — an opt-in that arrives
      // pre-ticked is not an opt-in. It is posted either way, as true or false, so a row can tell
      // "they said no" apart from "the form never asked".
      hearFromShop: false,
      shopMarketing: false,
    },
    // Today, in the phone's own zone, already in the box: the date a client signs is the day they
    // are standing at the counter, and until 2026-09-08 every one of them opened a date picker to
    // say so. Still a normal editable field, still posted as YYYY-MM-DD, which is what the date
    // input has always produced. "Submit another waiver" reloads the page, so the shop's own tablet
    // gets a fresh date per waiver. TW-0503 — and it is the day the age is read on (birthDate.ts), kept today past midnight
    // until the client moves it (Form's phoneToday below).
    signedDate: isoOf(todayHere()),
  };
}

/** The blank form once, for the draft's difference and its version; its signed date is never drafted, so its day is moot. */
const BLANK_FORM: FormState = blankForm();
/** The version a draft is written and read under: the blank form's shape and the bundle this page runs (draft.ts). */
const FORM_DRAFT_VERSION = formVersion(BLANK_FORM);
/** TW-0501 — the questionnaire's own keys: a box the browser stops a Submit at by one of these names is a health question
 *  (refusal.ts stepOfBox). */
const HEALTH_QUESTION_KEYS: readonly string[] = Object.keys(BLANK_FORM.questionnaire);
/** The two answers a shop's own question takes (ownQuestions.ts OwnAnswer): the only ones a draft puts back. */
const OWN_ANSWERS: readonly string[] = ["yes", "no"];
/** The price's own boxes (price.ts PriceParts). */
const PRICE_PART_KEYS: readonly string[] = Object.keys(NO_PRICE_PARTS);

function Form() {

  // TW-0505 — `rules`: where the page stands on its shop's state's rules, which arrive after the shop (stateRules.ts)
  const { client, rules } = useApp();
  const rulesHere = !rules || rules.status === "ready";

  // TW-0082 — the language this client reads the form in: the phone's own (or ?lang=) to start,
  // the switch at the top of the page for THIS visit. Never remembered; i18n.ts says why. ONLY among
  // the languages this shop offers (Chris, 2026-09-08: the picker is "a shop config that's off by
  // default. Enabled in settings"): a shop that has not turned Spanish on is English, whatever the
  // phone says. The shop arrives a beat after the first paint, so the language is re-read when it
  // lands — unless the client has already tapped the switch, whose choice stands.
  const enabled = enabledLangs(client?.public_data);
  const langTouched = useRef(false);
  const [lang, setLang] = useState<Lang>(() => detectLang(undefined, undefined, enabled));
  useEffect(() => {
    const offered = enabledLangs(client?.public_data);
    if (langTouched.current) {
      setLang((current) => (offered.includes(current) ? current : "en"));
      return;
    }
    setLang(detectLang(undefined, undefined, offered));
  }, [client]);
  // TW-0094 — and the document says so too, live with the switch: `<html lang>` is "en" from the
  // build, and a screen reader reads it to decide how to pronounce what follows.
  useEffect(() => setDocumentLang(lang), [lang]);
  const T = strings(lang);

  // TW-0059 — what this shop does, and what this one client is here for. A tattoo shop and a
  // piercing studio each have one service and never see a choice; a shop that does both asks, and
  // there is deliberately no default answer: a pre-selected tattoo on a piercing visit would sign
  // the wrong document. service.ts carries the reasoning and every word that changes.
  const type = shopType(client?.public_data?.shop_type);
  // TW-0279 — AND COSMETIC TATTOOING, THE THIRD, on its own key (`public_data.cosmetic`, service.ts servicesFor): what
  // this shop's form can produce. One service starts on it (a cosmetic studio's client is asked nothing); more than one
  // asks, with no default answer, for the reason above.
  const services = servicesFor(type, client?.public_data?.cosmetic);
  const servicesKey = services.join(",");
  const [service, setService] = useState<ServiceType | "">(() => initialService(services));
  // the shop arrives a beat after the first paint (App.tsx races a static file and the API), so
  // the one-service shops get their service the moment either source answers
  useEffect(() => {
    setService(initialService(services));
    // the list is read through its key: a new array every render is the same list
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [servicesKey]);
  const copy = copyFor(service, lang, services);
  const isPiercing = service === "piercing";
  const isCosmetic = service === "cosmetic";
  /** Each service the shop's form offers, as a noun in a sentence ("a tattoo or a cosmetic tattoo"). */
  const serviceNouns = services.map((s) => ({ tattoo: T.nounTattoo, piercing: T.nounPiercing, cosmetic: T.nounCosmetic })[s]);
  // 2026-09-25 — the client's physician (Squirtin' Ink: "For the piercing forms can we add a physician name ,
  // number and address."): the shop's own switch for THIS service, so a shop that does both can ask on a piercing
  // and not on a tattoo. Nothing before a both shop's client has picked. The API prints on the same read
  // (common.ask_physician_of), so what is asked is what prints.
  const askPhysician = asksPhysician(client?.public_data, service);
  // 2026-09-14 — a shop that uses one material (2 Ton Gallery: "we only use titanium") fixes it in
  // Settings; the form shows it as a line and posts it, so the signed waiver and the record read the
  // material under the key they always have. "" means ask, the select every piercing form has had.
  const fixedJewelry = fixedJewelryMaterial(client?.public_data);
  // TW-0422 — the piercings the shop does (service.ts piercingSpotsFor): the owner's list from Settings, "Somewhere else" always,
  // and every spot for a shop that never said
  const piercingSpots = piercingSpotsFor(client?.public_data);
  useEffect(() => {
    if (!isPiercing || !fixedJewelry) return;
    setForm((was) => (was.jewelryMaterial === fixedJewelry ? was : { ...was, jewelryMaterial: fixedJewelry }));
  }, [isPiercing, fixedJewelry]);

  // THE FORM'S QUESTIONS FOLLOW THE STATE (TW-0104). What the shop's state requires that the standard form
  // never asked — Oregon's sensitivities and bleeding-disorder questions — and the two shop settings a
  // state can force on. A shop with no state, or in a state that asks nothing extra, sees the form it
  // has always seen. The demo shop takes its state from `?state=` or its picker (App.tsx).
  const required = requirementsFor(client?.state);
  // TW-0279 — a cosmetic form asks the allergies and the bleeding questions in every state (the proposal's B10 and B4
  // reuse them), and the medications box (B3, which the API prints on every cosmetic record), whatever the shop's switch
  // says. A tattoo's and a piercing's page asks exactly what it did.
  const askAllergies = required.has("allergies") || isCosmetic;
  const showLicense = required.has("artist_license_number");
  // TW-0488 (2026-10-26) — WHICH ID, AND ITS NUMBER (idType.ts): the choice is on every page and required where the state's rule
  // keeps which ID (Kansas, Texas) or its number (Maine, Missouri), where the number's box is drawn too
  const mustPickIdType = idTypeRequired(required);
  const askIdNumber = idNumberAsked(required);
  const askBleeding = required.has("bleeding_disorders") || isCosmetic;
  const askEaten = required.has("eaten_recently");
  // TW-0293 (2026-10-15) — THE TWO QUESTIONS EVERY FORM ASKED SINCE 2023 FOLLOW THE STATE (defaultQuestions.ts has the
  // whole of it): asked when the shop's own switch is on, where its state's record names the question, or when the shop
  // has no state at all; left off the page otherwise, and then never posted, never refused, never printed.
  const askInfluence = asksDefaultQuestion(client?.public_data, client?.state, DRUGS_OR_ALCOHOL_SETTING);
  const askCommunicable = asksDefaultQuestion(client?.public_data, client?.state, COMMUNICABLE_DISEASE_SETTING);
  // TW-0628 (2026-11-04) — THE PREGNANCY REFUSAL IS THE SHOP OWNER'S PROTECTION (protections.ts): on for every shop, so a yes is
  // stopped as it always was, until the owner turns it off in Settings with the risk accepted; then the yes signs, nothing new is
  // said to the client, and the signed waiver says the refusal was off. The API's door reads the same switch off the same row.
  const pregnancyRefusalOff = protectionOff(client?.public_data, PREGNANCY_REFUSAL);
  // 2026-10-08 (TW-0279 slice 8) — Michigan's numbing rule (R 333.13109(5)), on every service there: whether a numbing product
  // is on the skin or will be used, and on a yes a photo of the doctor's prescription (healthQuestions.ts PHOTO_ON_YES)
  const askNumbing = required.has("numbing_prescription");
  // TW-0270 slice 4 (2026-10-16) — THE FIVE A STATE'S RULE LISTS AS ITS OWN QUESTIONS (the all-states audit's M04): each asked where
  // the shop's state names its id, a yes or a no with no box, never optional, and a yes recorded, never refused. A cosmetic form
  // asks its own diabetes question on every signing (cosmetic.ts), so the state's is a tattoo's and a piercing's: one question,
  // never two.
  const askDiabetes = required.has("diabetes") && !isCosmetic;
  const askSeizures = required.has("seizures_or_fainting");
  const askHeart = required.has("heart_or_blood_pressure");
  const askBloodThinners = required.has("blood_thinners_recently");
  const askPregnantRecently = required.has("pregnant_recently");
  // TW-0526 (2026-10-28, the audit's M04) — AND THE QUESTIONS A STATE'S RULE LISTS THAT THE CONTRACT CARRIES IN THEIR OWN WORDS
  // (stateForms.ts wordedQuestionsFor): the five's shape, drawn after them by their words, for this signing's service
  const wordedQuestions = wordedQuestionsFor(client?.state, service);
  // TW-0526, the client-form side (2026-10-29) — AND THE BOXES A STATE'S RULE NAMES THAT NO YES OR NO CARRIES (stateForms.ts
  // stateFieldsFor), by the part of the page each sits in: Mississippi's sex as on the ID beside the birth date, Oklahoma's hour
  // after the state's questions. The parent's box waits for the parent's block (guardianFields, below)
  const youFields = stateFieldsFor(client?.state, "you");
  const healthFields = stateFieldsFor(client?.state, "health");
  // 2026-11-04 (a California shop's ask) — THE PREGNANCY QUESTION, A YES OR A NO WHERE THE STATE'S RULE NAMES IT (stateForms.ts
  // answerRequiredBy: California's "Whether the client may be pregnant.", § 119303(b)(1)): `required` on its radios and refused
  // blank at the submit like every state question; its words, and what its yes does, are every form's
  const optionalQuestions = optionalHealthQuestions((key) => answerRequiredBy(client?.state, key));
  const pregnancyOptional = optionalQuestions.includes("pregnantOrNursing");
  // 2026-11-04 — AND THE MEDICATIONS BOX IN THE STATE'S WORDS where its record words it (stateForms.ts medicationsWordsFor:
  // California's "history of medication use or is currently using medication", § 119303(b)(3)), the page's own elsewhere
  const medicationsLabel = medicationsWordsFor(client?.state, service, lang) ?? T.medications(service);
  const askMedications =
    Boolean(client?.public_data?.ask_more_questions_about_medications_and_blood_thinners) ||
    settingForcedBy(client?.state, "ask_more_questions_about_medications_and_blood_thinners") ||
    isCosmetic;
  const wantLotPhotos =
    Boolean(client?.public_data?.include_needle_and_ink_batch_number_pictures) ||
    settingForcedBy(client?.state, "include_needle_and_ink_batch_number_pictures");
  // 2026-10-31 — WHICH OF THE TWO THE FORM WILL NOT POST WITHOUT (lotPhotos.ts; White Lotus Body Arts, Ventura: "make it
  // mandatory that they cannot submit the form without doing the photo of the needle cartridge"): the shop's own map, read
  // only while the ask is on. The API refuses the same body (NEEDLE_PHOTO_MISSING, INK_PHOTO_MISSING), and the refusal takes
  // the client to the box, which is what keeps this from being the wall the 2026-09-10 rule was earned on.
  const requiredLotPhotos = requiredLotPhotosOf(wantLotPhotos ? client?.public_data?.lot_photos_required : undefined);
  // TW-0154 — the shop's own switch, and the whole of it: no state forces this one on, because it is
  // a shop's bookkeeping and not anybody's rule. Off for every shop that has not asked.
  const askPrice = Boolean(client?.public_data?.ask_price);
  // 2026-09-11 — HOW the client enters it: the choices (a decided fee, an estimate range, an hourly rate), the
  // default for a shop that asks, or the one box for a shop that chose "in their own words" (price.ts). The posted
  // `price` stays one string, composed from the choice and the numbers by the effect below. The shop's own rate is a
  // DEFAULT: picking the hourly choice fills it into the rate box, and the client can change it (prefillRate).
  const priceStyle = priceStyleOf(client?.public_data);
  const shopRate = shopRateOf(client?.public_data);
  const [priceKind, setPriceKind] = useState<PriceKind>("");
  const [priceParts, setPriceParts] = useState<PriceParts>(NO_PRICE_PARTS);
  // 2026-09-23 — how the client is paying (Piercinks): the shop's own switch and the whole of it, like the price, and
  // no state forces it. Unlike the price, a pick is required once it is asked (handleSubmit).
  const askPaymentMethod = asksPaymentMethod(client?.public_data);
  // Who to call (2026-09-11): the shop's own switch, OR its state's rule — South Carolina's wants
  // "Emergency contact information for the client in case of emergency, including name, address, phone
  // number, and other pertinent contact information" (S.C. Code Regs. 60-111 § 801.B.6) and New
  // Jersey's "an emergency telephone number and contact individual" (N.J.A.C. 8:27-4.2(b)1). The API
  // decides with the same two reads (common.form_setting_on), so what is asked here is what prints.
  const askEmergency =
    Boolean(client?.public_data?.ask_emergency_contact) || settingForcedBy(client?.state, "ask_emergency_contact");
  // TW-0170 — THE STATE'S OWN SENTENCES ON THE CONSENT: South Carolina's blood-donor notice, which "shall be
  // signed by the client" (S.C. Code Regs. 60-111 § 500.C), and its grievance procedure with the Department's
  // address and phone (§ 1002). Only for the signing they belong to (a tattoo, there) and only once the client
  // has said what they are here for. The ticks live apart from the waiver's own clauses, because which boxes
  // exist is the state's to say, not the form's.
  const notices = noticesFor(client?.state, service);
  const [noticeTicks, setNoticeTicks] = useState<Record<string, boolean>>({});
  // TW-0504 — WHICH AFTERCARE SHEETS THE CLIENT HAS OPENED (FormItems.tsx NoticeSheet), by the notice's id: the page's own
  // state, so no keystroke, tick or answer anywhere can fold one; the client's tap on a sheet's line is the only thing that
  // opens or folds it. Until this card the sheet's <details> held it alone, in a piece made anew on every render, and the
  // next keystroke folded it shut mid-read (docs/FORM-AUDIT-2026-10-26.md § 10). Never drafted, never posted.
  const [sheetsOpen, setSheetsOpen] = useState<Record<string, boolean>>({});
  const toggleSheet = useCallback((id: string, open: boolean) => {
    setSheetsOpen((was) => (Boolean(was[id]) === open ? was : { ...was, [id]: open }));
  }, []);
  // 2026-09-24 — THE SHOP'S OWN CLAUSES, in the owner's words (Shop 53 Custom Tattoo, La Plata MD: "I need to add
  // I hereby release any and all employees, agents or persons representing ..."). Boxes the client ticks like the
  // waiver's own clauses, drawn after the state's notices and before the terms, in the shop's words whichever
  // language the client picked. The ticks live apart from the waiver's clauses for the notices' reason: which
  // boxes exist is the shop's to say, and the API prints each from the shop's row by the id posted here.
  const ownClauses = ownClausesOf(client?.public_data?.own_clauses);
  const [ownClauseTicks, setOwnClauseTicks] = useState<Record<string, boolean>>({});
  // TW-0412 — THE SHOP'S OWN QUESTIONS, in the owner's words (Perfecto Bliss, Maryland: "We'd like to add: 'Have you eaten in
  // the last 3 hours?'"). A Yes / No each, drawn among the health questions after every one this page asks of its own, in the
  // shop's words whichever language the client picked, with the owner's line under the answer it names. The answers live
  // apart from the questionnaire for the clauses' reason: which questions exist is the shop's to say, and the API keeps each
  // answer beside the words on the shop's row by the id posted here.
  const ownQuestions = ownQuestionsOf(client?.public_data?.own_questions);
  const [ownQuestionAnswers, setOwnQuestionAnswers] = useState<Record<string, string>>({});
  // 2026-10-02 — CASH PREFERRED, the card-fee box (Reckless Heart Tattoo, Greenville SC: "a box acknowledging that
  // there is a 2% fee if they pay with credit/debit card, and to avoid that fee they can pay with cash"). One box
  // the client ticks, after the shop's own clauses and before the terms, in the words the row carries in the
  // language they picked; the API prints the row's sentence by the id posted here. Off for every shop that has not
  // turned it on in Settings → Waiver form → Cash preferred.
  const cashPreferred = cashPreferredOf(client?.public_data?.cash_preferred);
  const [cashPreferredTicked, setCashPreferredTicked] = useState(false);
  // 2026-10-08 — A SHOP IN HIPAA MODE (hipaaForm.ts; docs/HIPAA.md → What changes for a shop on the plan): the practice's Notice
  // of Privacy Practices with the one box the signature depends on, and the photo release, a HIPAA authorization the client may
  // sign or leave alone. Neither exists for any other shop, whose form is the one it has always been.
  const privacyNotice = privacyNoticeOf(client);
  const [privacyNoticeTicked, setPrivacyNoticeTicked] = useState(false);
  const photoRelease = photoReleaseOf(client);
  const [photoReleaseSigned, setPhotoReleaseSigned] = useState(false);
  // TW-0171 — "Choose from library" on the ID photo, for an ID that lives on the phone. The owner's own
  // boolean on the row (Settings → Waiver form) is their word and wins either way; with no word, it STARTS
  // ON (idPhotoLibraryStartsOn: a saved photo is the copy every state's rule takes, the camera's equal; Chris,
  // 2026-10-01: "i think we should have it on by default for states that support it." · "it sounds like the 21
  // can be expanded if it's allowed?", replacing his 2026-09-11 "we don't auto-opt states in"). The API reads
  // the same two things the same way (common.id_photo_from_library_of).
  const libraryPreference = client?.public_data?.id_photo_from_library;
  const libraryOn = typeof libraryPreference === "boolean" ? libraryPreference : idPhotoLibraryStartsOn();
  // and which way each ID photo on screen came in, for the posted body (`idPhotoSource`)
  const [idPhotoSource, setIdPhotoSource] = useState<"camera" | "library">("camera");
  const [minorIdPhotoSource, setMinorIdPhotoSource] = useState<"camera" | "library">("camera");
  const [guardianProofSource, setGuardianProofSource] = useState<"camera" | "library">("camera");
  // TW-0279 slice 10 — and the doctor's written instructions' photo, the same honesty (savedPhoto.ts photoSourceFields)
  const [providerInstructionsSource, setProviderInstructionsSource] = useState<"camera" | "library">("camera");
  const [secondParentPermissionSource, setSecondParentPermissionSource] = useState<"camera" | "library">("camera");
  const [numbingPrescriptionSource, setNumbingPrescriptionSource] = useState<"camera" | "library">("camera");

  // Who did the work. A shop that keeps a list of its artists sends it on `public_data.artists` and
  // its client picks a name; a shop with no list sees the plain field it has always had and posts
  // the same body it has posted since 2023. artists.ts carries the reasoning and every decision.
  // 2026-09-14 — and only the artists listed on THIS form: a `both` shop that marked its piercers
  // shows a client getting pierced just the piercers (artists.ts, artistOptions).
  const artists = artistOptions(client?.public_data, service);
  // TW-0422 — and whether this service's page asks who did the work at all (artists.ts asksArtistFor): the owner's switch, one
  // per service, the state's rule winning where it puts the artist on every record. Off, neither the picker nor the box is drawn,
  // nothing is refused for it, and the body posts `nameOfArtist` as "".
  const askArtist = asksArtistFor(client?.public_data, service, required);
  const artistsKey = [service, ...artists.map((a) => a.name)].join("\u001f");
  const artistCopy = pickerCopy(service, lang);
  // 2026-10-18 — A TATTOO CONVENTION (convention.ts): the show's roster runs to hundreds, so its field is a search with the
  // typed name taken as typed, never a select. Only a show's /shop answer carries the block, so every shop is untouched
  const artistSearch = searchesArtists(conventionOf(client), artists);
  const showCopy = conventionCopy(service, lang);
  const [artistChoice, setArtistChoice] = useState<string>("");
  // The shop arrives a beat after the first paint, so a name CAN be typed before the list shows up.
  // When it does, put the picker where that name already is. And when the LIST changes under a
  // choice — the client switched service and the name they picked is not on this form's list —
  // the choice and the box empty together (artists.ts, choiceAfterListChange, says why). A shop
  // with no list for this service and nothing picked keeps the plain field it has always had.
  useEffect(() => {
    const picked = Boolean(artistChoice) && artistChoice !== SOMEONE_ELSE;
    if (!artists.length && !picked) return;
    const next = choiceAfterListChange(artistChoice, artists, form.nameOfArtist);
    if (next !== artistChoice) setArtistChoice(next);
    if (picked && next === "") setForm((was) => ({ ...was, nameOfArtist: "" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artistsKey]);

  // read once at mount, as it always has been: nothing ever set it, and `npm run build` with
  // CI=true refuses to compile over the dead setter
  const [isMobile] = useState<boolean>(window.innerWidth < 640);

  const [submissionError, setSubmissionError] = useState<string>("");
  // which refusal the sentence above is (refusal.ts, 2026-09-29): the choice between a tattoo and a
  // piercing says its sentence again under the two choices, and only while it is the one refused
  const [refusedFor, setRefusedFor] = useState<string>("");
  // TW-0505 — the state's rules came: the sentence under the button that said they were loading, or had not loaded, is over
  useEffect(() => {
    if (rulesHere && (refusedFor === "state_rules_loading" || refusedFor === "state_rules_failed")) {
      setSubmissionError("");
      setRefusedFor("");
    }
  }, [rulesHere, refusedFor]);
  /** TW-0501 — the box a door refusal took the client to, by its name ("age"), so its sentence is said there too */
  const [doorPlace, setDoorPlace] = useState<string>("");
  // …and which question within it, where the reason alone does not say (the `detail` a refusal names): the health
  // question left blank says its sentence again under that question, and only while it is still blank (2026-10-01)
  const [refusedDetail, setRefusedDetail] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);

  const signatureRef = useRef<any>(null);
  // TW-0128 — the parent or guardian's own signature, on its own canvas. The minor still signs
  // theirs: several states want both names on the consent (Texas says so outright), and none of
  // them wants the child's signature to stand in for the adult's.
  const guardianSignatureRef = useRef<any>(null);
  // TW-0227 — and the OTHER parent's, where a city's or county's rule wants both parents' written permission and the
  // other parent is here to give it. Its own canvas, mounted only while that is the way picked.
  const secondParentSignatureRef = useRef<any>(null);

  // The birth date's three boxes: two digits typed in Month move the cursor to Day, two in Day to
  // Year, and nothing moves on a single digit, on Year, or on a correction (Chris, 2026-09-07:
  // "i wonder if entering 2 digits in month should move someone into day, and 2 there into year.
  // not sure because sometimes assuming everyone does a thing the same is confusing and jarring").
  // The fixed-width pair is the one case the pattern is standard for (a card's expiry, a code);
  // a tap back into Month always works, and a phone's autofill landing all three at once just
  // ends with the cursor in Year.
  const birthDayRef = useRef<HTMLInputElement>(null);
  const birthYearRef = useRef<HTMLInputElement>(null);
  const advanceAfterTwoDigits = (next: React.RefObject<HTMLInputElement>) => (e: React.ChangeEvent<HTMLInputElement>) => {
    handleInputChange(e);
    if (/^\d{2}$/.test(e.target.value)) next.current?.focus();
  };

  // TW-0084 — the address from Google Places (address.ts carries the rules). The street box is the
  // search box: from the third character, a beat after the last keystroke, a short list of matches
  // drops under it; a tap fills street · city · state · ZIP and moves the cursor to the apartment
  // line; typing on without tapping posts exactly what was typed. The list is asked for only while
  // city and ZIP are still empty, so a phone's autofill (all five boxes at once) and a returning
  // client's prefill (TW-0037) never see one. The browser talks only to our API — Google never sees
  // the client — and nothing here can block a signing: no answer, a slow answer, no key, and the
  // four boxes are the four boxes they have always been. (Chris, 2026-09-07: "what about using
  // Google places for the address? … could save people time and cost us pennies.")
  const aptRef = useRef<HTMLInputElement>(null);
  /** TW-0152 — DID YOU MEAN. 43 of the 6,739 client records this form has taken since 2023 carry an
   *  address that cannot receive mail: `@gmail.con`, `@gnail.com`, `@iloud.com`, six with no TLD at
   *  all (which the browser's own type=email check waves straight through). The shop's copy of the
   *  waiver still lands — the client's never does, and neither does anything the shop sends them
   *  later. Offered on blur and offered ONCE on the button; a second press goes through with
   *  whatever they typed, because this is an offer and never a correction. */
  const emailRef = useRef<HTMLInputElement>(null);
  const [didYouMean, setDidYouMean] = useState<string | null>(null);
  const emailReoffered = useRef(false);
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [highlighted, setHighlighted] = useState<number>(-1);
  const addressSession = useRef<string>(newAddressSession());
  const suggestAbort = useRef<AbortController | null>(null);
  const suggestTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const closeSuggestions = () => {
    if (suggestTimer.current) clearTimeout(suggestTimer.current);
    suggestAbort.current?.abort();
    setSuggestions([]);
    setHighlighted(-1);
  };

  const handleStreetChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleInputChange(e);
    const next = { ...form, address1: e.target.value };
    if (suggestTimer.current) clearTimeout(suggestTimer.current);
    suggestAbort.current?.abort();
    // 2026-10-08 — a shop in HIPAA mode asks nothing (address.ts shouldSuggest): no request leaves the phone for its street box
    if (!client || !shouldSuggest(next, client)) {
      setSuggestions([]);
      setHighlighted(-1);
      return;
    }
    const abort = new AbortController();
    suggestAbort.current = abort;
    suggestTimer.current = setTimeout(() => {
      fetchAddressSuggestions(client.subdomain, next.address1, addressSession.current, abort.signal).then((found) => {
        if (abort.signal.aborted) return;
        setSuggestions(found);
        setHighlighted(-1);
      });
    }, SUGGEST_DEBOUNCE_MS);
  };

  const pickSuggestion = (picked: AddressSuggestion) => {
    if (!client) return;
    const session = addressSession.current;
    addressSession.current = newAddressSession(); // the next search is a new session
    closeSuggestions();
    // The tapped line goes in the box at once and the cursor moves now, inside the tap, which is what
    // keeps a phone's keyboard up; the other boxes land when the API answers, a beat later.
    setForm((current) => ({ ...current, address1: picked.main }));
    aptRef.current?.focus();
    fetchAddressPick(client.subdomain, picked.id, session).then((address) => {
      if (!address) return;
      setForm((current) => applyPick(current, address));
    });
  };

  const handleStreetKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!suggestions.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlighted((h) => (h + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlighted((h) => (h <= 0 ? suggestions.length - 1 : h - 1));
    } else if (e.key === "Enter" && highlighted >= 0) {
      e.preventDefault();
      pickSuggestion(suggestions[highlighted]);
    } else if (e.key === "Escape") {
      closeSuggestions();
    }
  };

  const [takingPhotoOfDriversLicense, setTakingPhotoOfDriversLicense] = useState(false);
  const [photoOfDriversLicense, setPhotoOfDriversLicense] = useState<
    string | null
  >(null);
  // TW-0488 — which ID is in that photo: its own state rather than FormState's (a choice, never a typed box), so a body whose
  // client picked nothing is the body it has always been (idFieldsPosted). Its number, a typed box, is FormState's `idNumber`.
  const [idType, setIdType] = useState<IdType | "">("");

  // TW-0153 — the MINOR's own photo ID, where the state or county wants both. Its own state
  // because it is its own document: the one uploader above stays the parent's on a guardian route,
  // which is what every state that allows this asks for first.
  const [takingPhotoOfMinorId, setTakingPhotoOfMinorId] = useState(false);
  const [photoOfMinorId, setPhotoOfMinorId] = useState<string | null>(null);
  // 2026-09-24 — the birth certificate or guardianship papers, where the shop or the state asks for them
  // (minors.ts asksGuardianProof)
  const [takingPhotoOfGuardianProof, setTakingPhotoOfGuardianProof] = useState(false);
  const [photoOfGuardianProof, setPhotoOfGuardianProof] = useState<string | null>(null);
  // TW-0270 slice 5 — a photo of each paper the state's rule keeps with a minor's record (minors.ts papersAsked), by the
  // paper's id, and which way each came in; one camera is open at a time, so `takingPhotoOfPaper` is the id whose camera is
  const [takingPhotoOfPaper, setTakingPhotoOfPaper] = useState<string | null>(null);
  const [paperPhotos, setPaperPhotos] = useState<Record<string, string>>({});
  const [paperSources, setPaperSources] = useState<Record<string, "camera" | "library">>({});
  // TW-0279 slice 10 — the client's doctor's written instructions on a cosmetic signing, where they are wanted
  // (cosmetic.ts providerInstructionsNeeded)
  const [takingPhotoOfProviderInstructions, setTakingPhotoOfProviderInstructions] = useState(false);
  const [photoOfProviderInstructions, setPhotoOfProviderInstructions] = useState<string | null>(null);
  // TW-0227 — a photo of the other parent's signed permission, where the rule lets it come in writing from elsewhere
  const [takingPhotoOfSecondParentPermission, setTakingPhotoOfSecondParentPermission] = useState(false);
  const [photoOfSecondParentPermission, setPhotoOfSecondParentPermission] = useState<string | null>(null);
  // 2026-10-08 (TW-0279 slice 8) — the doctor's prescription for a numbing product, asked under a yes to the state's
  // numbing question (askNumbing): the guardian proof's uploader, the camera or a saved photo where the shop allows one
  const [takingPhotoOfNumbingPrescription, setTakingPhotoOfNumbingPrescription] = useState(false);
  const [photoOfNumbingPrescription, setPhotoOfNumbingPrescription] = useState<string | null>(null);

  const [takingPhotoOfNeedleBatchNumber, setTakingPhotoOfNeedleBatchNumber] = useState(false);
  const [photoOfNeedleBatchNumber, setPhotoOfNeedleBatchNumber] = useState<
    string | null
  >(null);

  const [takingPhotoOfInkBatchNumber, setTakingPhotoOfInkBatchNumber] = useState(false);
  const [photoOfInkBatchNumber, setPhotoOfInkBatchNumber] = useState<
    string | null
  >(null);

  const [signatureIsEmpty, setSignatureIsEmpty] = useState<boolean>(true);
  const [guardianSignatureIsEmpty, setGuardianSignatureIsEmpty] = useState<boolean>(true);
  const [secondParentSignatureIsEmpty, setSecondParentSignatureIsEmpty] = useState<boolean>(true);

  // This is to keep error messages from being shown when the user is typing
  const [anyAgeInputIsFocused, setAnyAgeInputIsFocused] =
    useState<boolean>(false);

  // TW-0131 — has this client touched a field yet? A ref and not state: nothing renders from it,
  // and a re-render on the first keystroke of a form this size buys nothing.
  const startedRef = useRef(false);

  const [submittedSuccessfully, setSubmittedSuccessfully] =
    useState<boolean>(false);
  const [downloadLink, setDownloadLink] = useState<string | null>(null);
  // 2026-10-08 — the client's own copy of what they signed (clientCopy.ts), for a shop in HIPAA mode whose state's rule hands
  // the client one: built at the submit from the body posted, held here, drawn by the done screen. Null for every other shop.
  const [clientCopy, setClientCopy] = useState<ClientCopy | null>(null);

  const [form, setForm] = useState<FormState>(blankForm);

  // ── THE DAY OF SIGNING (TW-0503) ───────────────────────────────────────────────────────────────────────────────────
  //
  // The age is read off the birth date on the signed date's own day (birthDate.ts signingDayOf), the date the record prints
  // beside the birth date, so the record's age agrees with its two dates and the signing door reads the same number. The box
  // opens on this phone's today, and stays today while the page is open: a client filling the form in before midnight on their
  // 18th birthday and signing after it reads 18 at midnight, as the old typed age did, and the record says the day they signed.
  // A client who moves the box keeps their date (signedDateMoved), and a date ahead of every clock on Earth reads today.
  const [phoneToday, setPhoneToday] = useState<CalendarDay>(() => todayHere());
  const signedDateMoved = useRef(false);
  useEffect(() => {
    // a beat past the next midnight here, again each new day; a phone back from sleep asks at once
    const now = new Date();
    const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 1);
    const timer = window.setTimeout(() => setPhoneToday(todayHere()), Math.max(1000, midnight.getTime() - now.getTime()));
    const back = () => {
      if (document.visibilityState !== "visible") return;
      const today = todayHere();
      setPhoneToday((was) => (compareDays(was, today) === 0 ? was : today));
    };
    document.addEventListener("visibilitychange", back);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", back);
    };
  }, [phoneToday]);
  useEffect(() => {
    if (signedDateMoved.current) return;
    const today = isoOf(phoneToday);
    setForm((was) => (was.signedDate === today ? was : { ...was, signedDate: today }));
  }, [phoneToday]);

  // ── A COSMETIC TATTOO'S PROCEDURES (TW-0279) ──────────────────────────────────────────────────────────────────────
  //
  // The picker offers every procedure the SHOP's state allows (cosmetic.ts, read off the shop's row and the contract,
  // never the client's address): in Georgia no eyeliner, and powder, ombré and combination brows only once the owner has
  // said its county board allows them (`cosmetic_shaded_brows`, a literal true). The reasons stand under the list, in
  // the state's own words. What is picked is held in the list's order, and only what is offered is ever posted: a pick
  // the list no longer offers (the demo's state dial moved under it) is dropped here and again by submissionForm.
  const proceduresOffered = cosmeticProceduresOffered(client?.state, client?.public_data);
  const offeredValues = proceduresOffered.map((p) => p.value);
  const offeredKey = offeredValues.join("\u001f");
  const cosmeticBars = isCosmetic ? cosmeticBarsFor(client?.state, client?.public_data) : [];
  // TW-0279 slice 7 — and a bar the shop's own switch lifts says why it is lifted, in the rule's own words (South Carolina's
  // physician-directed studio: § 44-34-90 and its medical board's 2005 policy), where the bar's reason would have stood
  const cosmeticLifts = isCosmetic ? cosmeticLiftsFor(client?.state, client?.public_data) : [];
  // TW-0504 — the same list from render to render while the picks and the offered list are what they were, so the aftercare
  // sheet, memoized on its own props (FormItems.tsx NoticeSheet), is not drawn again on every keystroke for a list that did not
  // move; the offered list is read through its key, as the effect below reads it
  const procedures = useMemo(
    () => (isCosmetic ? postedProcedures(form.cosmeticProcedures, offeredValues) : NO_PROCEDURES),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isCosmetic, form.cosmeticProcedures, offeredKey]
  );
  /** A sheet's site after its line, the way NoticeSheet and the client's copy both name it (TW-0173): a cosmetic tattoo's
   *  procedures (TW-0279), a piercing's placement as the client's own list showed it (TW-0526: "Lóbulo de la oreja" on a
   *  Spanish page, though the form posts "Earlobe", which the signed waiver prints, a piercer's records reading the same in
   *  either language), and a tattoo's site as typed. */
  const sheetSite = (notice: Notice): string => {
    if (!notice.site) return "";
    if (isCosmetic) return procedures.join("; ");
    const typed = form.locationOnBody.trim();
    const spot = isPiercing ? PIERCING_LOCATIONS.find((option) => option.value === typed) : undefined;
    return spot ? locationLabel(spot, lang) : typed;
  };
  useEffect(() => {
    setForm((was) => {
      const kept = postedProcedures(was.cosmeticProcedures, offeredValues);
      return kept.length === was.cosmeticProcedures.length ? was : { ...was, cosmeticProcedures: kept };
    });
    // the list is read through its key, as the artists' is
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offeredKey]);
  // TW-0279 slice 9 — the cosmetic questions this visit's page asks (cosmetic.ts cosmeticQuestionsFor): the picked
  // procedures', and the shop's state's own (California's herpes question, the face question, New Jersey's pregnancy bar) in
  // its record's words, none the shop's own switch takes off the page
  const cosmeticQuestions = isCosmetic ? cosmeticQuestionsFor(procedures, client?.state, client?.public_data) : [];
  // …and an answer the shop's state bars the work on (New Jersey's isotretinoin, the face question's yes), refused at the
  // question, and only a question the page still asks: a yes left behind by an unticked procedure is never posted
  const cosmeticAnswerBar = isCosmetic
    ? cosmeticAnswerRefusal(client?.state, form.questionnaire, cosmeticQuestions.map((question) => question.name))
    : null;
  // TW-0279 slice 10 — THE CLIENT'S HEALTH CARE PROVIDER'S WRITTEN INSTRUCTIONS (cosmetic.ts has the rule): asked about on a
  // cosmetic signing where the shop's state's rule names them (New Jersey's N.J.A.C. 8:27-4.4(a)) or the shop's own switch is
  // on; a scalp pick first asks whether the work covers scars, and the photo is wanted for areola or camouflage work, or
  // scalp work that does. Read off the shop's row and this visit's picks, the API's way (app.provider_instructions_wanted).
  const askProviderInstructions = isCosmetic && asksProviderInstructions(client?.state, client?.public_data);
  const providerRule = askProviderInstructions ? providerInstructionsRuleFor(client?.state) : null;
  const askScalpScars = askProviderInstructions && scalpNamed(procedures);
  const scalpScarsAnswer: boolean | null = askScalpScars
    ? form.scalpCoversScars === "yes"
      ? true
      : form.scalpCoversScars === "no"
        ? false
        : null
    : null;
  const wantProviderInstructions = askProviderInstructions && providerInstructionsNeeded(procedures, scalpScarsAnswer);
  // the sentence under it: the state's rule in its record's plain words, or the shop's own ask
  const providerInstructionsWhy = providerRule
    ? lang === "es"
      ? providerRule.why.es
      : providerRule.why.en
    : T.providerInstructionsByShop(client?.name ?? "");
  // THE CLAUSES THIS VISIT SHOWS, the ones its submit walks and its body carries (service.ts ackOrderFor): the service's
  // own, and for a cosmetic tattoo the shop's state's lines and the picked procedures' own. Nothing before a pick.
  // 2026-10-08 — and never the portfolio clause for a shop in HIPAA mode (hipaaForm.ts: a photo use conditioned on the signing,
  // which 45 CFR 164.508(b)(4) forbids a covered entity); its photo release is the one way a photo leaves the client's care
  const acksShown: AckKey[] = service
    ? ackOrderFor(service, procedures, client?.state ?? null).filter((key) => showsPortfolioClause(client) || key !== PORTFOLIO_CLAUSE)
    : [];

  // TW-0037 — a client who has signed here before gets what they typed last time filled in. The
  // ask happens once, when BOTH the phone and the email are there, and nothing about a first-time
  // client is different: no line, no flash, no error. Only empty fields are filled, so anything
  // already typed stays theirs, and every filled field is a normal editable input.
  const [welcomeBack, setWelcomeBack] = useState<boolean>(false);
  const alreadyAskedAbout = useRef<string>("");
  const latestForm = useRef(form);
  latestForm.current = form;

  // ── "BEEN HERE BEFORE?" (TW-0195) ─────────────────────────────────────────────────────────────
  //
  // A shop owner under the ad, 2026-09-17: "do clients have to do the same waiver for each
  // appointment or does it save their name?" It did — the recognition above has filled the contact
  // block since TW-0037 — but only after the phone and the email, which sit SIXTH and SEVENTH on
  // this page, so by the time it could fire the client had typed everything but the address.
  // Asking for those two FIRST is what makes the capability already here save the whole block.
  //
  // It is a SETTING, off until the shop turns it on (Chris, 2026-09-21: "the form works so well
  // already that tacking more onto it seems ... risky in some respects"), and the flag gates the
  // ACTION only: the effect above is unchanged for every shop, on or off.
  //
  // WHAT THIS MUST NOT LEAK. The API answers a half match — a phone on file with the wrong email —
  // byte for byte the way it answers a miss, on purpose, so that nobody can stand at a counter and
  // ask a shop's form "is this number one of your clients?". So every no here is ONE line, the same
  // line: no match, half a match, rate limited, the network gone. `lookupMissed` is written once
  // and used once, and the test asserts the four paths render the identical string.
  const offersLookup = offersReturningLookup(client?.public_data);
  const [lookupOpen, setLookupOpen] = useState(false);
  const [lookupPhone, setLookupPhone] = useState("");
  const [lookupEmail, setLookupEmail] = useState("");
  const [lookupBusy, setLookupBusy] = useState(false);
  const [lookupMissed, setLookupMissed] = useState(false);
  const firstNameRef = useRef<HTMLInputElement>(null);
  const lookupReady = phoneLooksComplete(lookupPhone) && emailLooksComplete(lookupEmail);
  // not on the done screen: "Been here before?" over a signed waiver's thank-you is a question
  // about a form that is no longer on the page
  const showLookupAction = offersLookup && !submittedSuccessfully;

  // ON THE DEMO IT DEMOS (TW-0195, 2026-09-21). The setting is on for demo.waivers.tattoo so a
  // prospect can see the action — and a prospect has never signed at the demo shop, so the first
  // thing the founder saw when he tapped it was "We did not find you.", which is the right answer
  // and a useless demo. Chris, verbatim: "for the demo mode i wonder if we need a better demo ...
  // this is "accurate" for the real form but how do we want to do this?" · "a demo banner saying
  // "enter any valid phone / email" and maybe making it not required, and then filling out the form
  // with complete fake data?"
  //
  // ⚠ IT IS THE SHOP, NOT THE SETTING. Every line of it is gated on `isDemoShop`, the same gate the
  // state picker and the pairing ride — a real shop with the setting on asks the API exactly as it
  // did before, sees no hint, and opens the panel on empty boxes. There is nothing here a printed
  // QR code on a counter can reach.
  const demoLookup = Boolean(client) && isDemoShop(client!.subdomain);

  /** Open it with whatever is already in the form, so nothing is typed twice in either direction.
   *  On the demo, a box that would open empty opens on the made-up pair instead: the point is that
   *  a prospect taps "Find me" and types nothing. Anything else valid they type still works. */
  const toggleLookup = () => {
    if (lookupOpen) {
      setLookupOpen(false);
      return;
    }
    if (!lookupPhone) setLookupPhone(form.phoneNumber || (demoLookup ? DEMO_LOOKUP_PAIR.phone : ""));
    if (!lookupEmail) setLookupEmail(form.email || (demoLookup ? DEMO_LOOKUP_PAIR.email : ""));
    setLookupMissed(false);
    setLookupOpen(true);
  };

  /** Find me. The two values go into the form's own phone and email whatever the answer is — the
   *  client typed them, and a miss must not cost them a second typing — and the pair is marked as
   *  ASKED ABOUT, which is what stops the effect above asking the API the same question again the
   *  moment those two fields fill in. */
  const findMe = async () => {
    if (!client || lookupBusy || !lookupReady) return;
    // TW-0500 — what the client typed in the panel lands in the form's own boxes: their touch, for the draft's clock
    noteTouch();
    // THE DEMO NEVER ASKS THE API. Not a stubbed answer and not a fake 200 — no request at all, so
    // the demo cannot put a stranger's typed pair to a real shop's recognition endpoint, and the
    // rate limit that protects that endpoint is never spent on a prospect playing with the demo.
    // It goes through the SAME `applyPrefill` a real match goes through, so every rule that path
    // has holds here too: nothing already typed is overwritten, the age is derived from the birth
    // date, and the welcome-back line is the one a real client sees. Marking the pair as ASKED
    // ABOUT is what stops the in-form effect below asking the API about it the moment the panel
    // carries it into the form's own two boxes.
    if (demoLookup) {
      alreadyAskedAbout.current = lookupKey(lookupPhone, lookupEmail);
      setForm(
        (was) =>
          applyPrefill({ ...was, phoneNumber: lookupPhone, email: lookupEmail }, demoRecognition())
            .form
      );
      setWelcomeBack(true);
      setLookupMissed(false);
      setLookupOpen(false);
      firstNameRef.current?.focus();
      firstNameRef.current?.scrollIntoView?.({ block: "center", behavior: "smooth" });
      return;
    }
    setLookupBusy(true);
    alreadyAskedAbout.current = lookupKey(lookupPhone, lookupEmail);
    let recognized: Awaited<ReturnType<typeof fetchRecognition>> | null = null;
    try {
      recognized = await fetchRecognition(client.subdomain, lookupPhone, lookupEmail);
    } finally {
      setLookupBusy(false);
    }
    setForm((was) => {
      const carried = { ...was, phoneNumber: lookupPhone, email: lookupEmail };
      return recognized ? applyPrefill(carried, recognized).form : carried;
    });
    setWelcomeBack(Boolean(recognized));
    setLookupMissed(!recognized);
    setLookupOpen(false);
    // back to the top of the block they came to confirm — the same gesture the did-you-mean offer
    // makes. `?.()` on the call because jsdom has no scrollIntoView and this runs under it in test.
    firstNameRef.current?.focus();
    firstNameRef.current?.scrollIntoView?.({ block: "center", behavior: "smooth" });
  };

  useEffect(() => {
    if (!client) return;
    if (!phoneLooksComplete(form.phoneNumber) || !emailLooksComplete(form.email)) return;
    const key = lookupKey(form.phoneNumber, form.email);
    if (alreadyAskedAbout.current === key) return;

    const abort = new AbortController();
    // a beat after they stop typing: the last keystroke of an email arrives one character at a time
    const timer = setTimeout(() => {
      alreadyAskedAbout.current = key;
      fetchRecognition(client.subdomain, form.phoneNumber, form.email, abort.signal).then(
        (recognized) => {
          if (!recognized || abort.signal.aborted) return;
          const { form: prefilled, filled } = applyPrefill(latestForm.current, recognized);
          if (!filled.length) return;
          setForm(prefilled);
          setWelcomeBack(true);
          // TW-0195 — a "we did not find you" from the panel is about a pair they have since
          // changed; the page must not carry both answers at once.
          setLookupMissed(false);
        }
      );
    }, 600);

    return () => {
      clearTimeout(timer);
      abort.abort();
    };
  }, [client, form.phoneNumber, form.email]);

  // ── A MINOR'S PIERCING WITH A PARENT PRESENT (TW-0128) ────────────────────────────────────────
  //
  // Three gates, and all three must open: this shop turned it on, the client's STATE hands a parent
  // a route, and the placement they picked is one that state allows a minor to have. minors.ts
  // is the rule and the API runs the same one again on the posted body — a browser can post
  // anything, so nothing here is a security boundary; it is the screen.
  //
  // ASKED TWICE, and the two answers land in two different places. Without a placement it is the
  // AGE-level question, which belongs under the birth date and decides whether the guardian block
  // opens at all; with the placement it is the whole question, which belongs under the placement
  // picker, because a barred site is not a dead end — picking another spot fixes it.
  // ONE SETTING PER SERVICE (TW-0150). A studio that pierces a 16-year-old's earlobe with their
  // mother standing there very often will not tattoo them, and 18 states make the second a crime
  // whatever the parent signs — so this reads the switch for what the client is here for TODAY, and
  // an unknown service reads as neither.
  const shopAllowsMinors = Boolean(
    service === "piercing"
      ? client?.public_data?.pierce_minors
      : service === "tattoo"
        ? client?.public_data?.tattoo_minors
        : false
  );
  // TW-0503 (2026-10-29) — THE AGE IS READ OFF THE BIRTH DATE, never typed: one source per fact, the number the signing door
  // reads off the same boxes the same way (birthDate.ts), so a typo can no longer make a minor an adult or an adult a minor.
  // The page reads a four-digit year as it is typed, and two digits typed for four once the client has left the three boxes:
  // read as they type, "19" on the way to "1996" would be 2019, and the parent's block would open for an adult and close again.
  // A year of one or three digits is no year. Every minors route below reads `age`.
  const signingDay = signingDayOf(form.signedDate, phoneToday);
  const birthDateTyped = [form.birthDateMonth, form.birthDateDay, form.birthDateYear].every((box) => `${box ?? ""}`.trim() !== "");
  const ageReadOff = (stillTyping: boolean): number | null => {
    const year = `${form.birthDateYear ?? ""}`.trim();
    const digits = /^[0-9]+$/.test(year) ? year.length : 0;
    if (!(digits === 4 || (digits === 2 && !stillTyping))) return null;
    return ageOn(form.birthDateYear, form.birthDateMonth, form.birthDateDay, signingDay);
  };
  /** the age the page reads now, and the one it reads once the client has left the three boxes (the submit's) */
  const age = ageReadOff(anyAgeInputIsFocused);
  const ageOnceLeft = ageReadOff(false);
  // the date's own problem: three boxes typed that make no day, or one after the day of signing, or a year that is no year
  const birthDateProblem = birthDateTyped && ageOnceLeft === null ? T.invalidBirthDate : null;
  const isMinor = age !== null && age < MINOR_AGE;
  // TW-0225 — AN EAR PIERCING THE STATE WRITES OUT OF ITS MINORS RULE. Michigan's body piercing is "the perforation of
  // human tissue other than an ear", Texas writes the earlobe out, Florida only an earlobe pierced with a mechanized
  // system. Asked of a minor's piercing at a shop that pierces minors, once the spot picked is one the state's
  // carve-out reaches (minors.ts asksEarPiercing): a navel never is, and ear cartilage never is under a lobe carve-out.
  // The picker says WHERE; the question is where the form learns the rest (the state's words for how far, and in a
  // stud-and-clasp state HOW), and the ANSWER is what the rule reads, here and in the API. A yes takes the state's own
  // requirements, its floor and its flat no off this signing; the parent still consents and signs, and the shop's own
  // proof switch still asks.
  // 2026-10-08 — as THIS shop meets it: the stud-gun question only where the owner turned it on (minors.ts ruleForShop)
  const piercingRule = isPiercing ? minorsRuleFor("piercing", client?.state, client?.public_data ?? null) : undefined;
  const earCarveOut = earExemptionOf(piercingRule);
  const askEar =
    isMinor && isPiercing && shopAllowsMinors && Boolean(client?.state) && asksEarPiercing(piercingRule, form.locationOnBody);
  const earAnswer: boolean | null = askEar
    ? form.earPiercing === "yes"
      ? true
      : form.earPiercing === "no"
        ? false
        : null
    : null;
  const earOutside = askEar && earAnswer === true;
  // TW-0595 — AND A FLOOR WHOSE OWN WORDS EXCEPT THE EARLOBE. Arkansas: "except: ... (B) When piercing the earlobe." (17 CAR §
  // 54-306(g)(4)); Kentucky: "with the exception of piercing the lobe of the ear." (902 KAR 45:070 Section 7(2)). Neither names
  // a tool, so nothing is asked: the Earlobe picked lifts the state's floor (minors.ts floorEarlobeExceptionApplies), and
  // everything else the rule asks of a minor's piercing stays, the parent, the photos, the papers and the statements.
  const floorLifted = isMinor && isPiercing && floorEarlobeExceptionApplies(piercingRule, form.locationOnBody);
  const earQuestion = T.earAsk(earCarveOut.scope, `${earCarveOut.method ?? ""}`);
  const minorAsk = {
    state: client?.state,
    // TW-0153 — and the jurisdiction inside it, so a shop in Baltimore City is not handed
    // Maryland's answer. One place: every refusal below reads this ask.
    county: client?.county,
    countyKind: client?.countyKind,
    // TW-0227 — and the city inside it, because Hammond's rule is not Lake County's
    city: client?.city,
    // TW-0503 — the age read off the birth date (0 is an age: a baby's ear piercing), null before there is one
    age,
    shopAllows: shopAllowsMinors,
    service,
    // TW-0153 — null until they answer, which is not the same as "no"
    coveringExisting:
      form.coveringExisting === "yes" ? true : form.coveringExisting === "no" ? false : null,
    // TW-0225 — the ear answer, where it was asked; null before it is, and everywhere it is not
    earPiercing: earAnswer,
    // 2026-10-08 — the shop's stud-gun switch, always a boolean here: a real shop's row, never the contract's rule
    studGun: client?.public_data?.ask_stud_gun === true,
  };
  // IS THE ROUTE OPEN AT ALL, setting the cover-up question aside? Asked with `coveringExisting`
  // TRUE on purpose (TW-0153): the cover-up question is asked INSIDE the guardian block, so letting
  // its unanswered state close the route would hide the very question that opens it. The answer is
  // judged separately below, and again on submit, and again by the API.
  // TW-0225 — …and with the SPOT when it is an ear the state writes out and the client said so: the carve-out is about
  // that spot, so it is the one call where the age-level question needs it (Mississippi's flat no and Idaho's floor do
  // not reach an earlobe). A spot a carve-out reaches is never one a state bars, so nothing else changes by it.
  // TW-0595 — and the Earlobe where the floor's own words except it (Arkansas's, Kentucky's), for the same reason: the
  // exception is about that spot, and the generator refuses a bar on a spot it reaches.
  const ageRefusal: Refusal | null = isMinor
    ? minorRefusal({ ...minorAsk, placement: earOutside || floorLifted ? form.locationOnBody : "", coveringExisting: true })
    : null;
  // TW-0153 — AN EMANCIPATED MINOR IS AN ADULT WHERE THE STATE'S RULE SAYS SO, each in its own words (the rule
  // carries `emancipated_adult`: minors.ts allowsEmancipatedAdult names today's states). Asked only
  // where the route is otherwise OPEN (`ageRefusal === null`), which is what stops it from ever
  // becoming the escape hatch: a state that refuses a minor outright, a county that bans it and a
  // floor age all keep refusing, because those refuse EVERYONE rather than every minor.
  const askEmancipated =
    isMinor && ageRefusal === null && allowsEmancipatedAdult(minorsRuleFor(service, client?.state, client?.public_data ?? null));
  const emancipated = signsAsAdult(
    minorsRuleFor(service, client?.state, client?.public_data ?? null),
    ageRefusal,
    form.emancipatedMinor === "yes"
  );
  // the guardian route: a minor, a birth date that agrees, nothing at the age level saying no — and
  // nobody to ask for a parent when the client has told us there is not one to ask
  const guardianRoute = isMinor && ageRefusal === null && !emancipated;
  // TW-0153 — Florida and Nevada want the minor's OWN photo ID as well as the parent's, and so does
  // El Paso County CO where Colorado does not. Only on a guardian route: an emancipated minor is
  // the client, and the one uploader is already theirs.
  // TW-0225 — the rule's own asks (minors.ts ruleAsksFor): none of them for an ear piercing the rule does not reach.
  const ruleAsks = ruleAsksFor(minorsRuleFor(service, client?.state, client?.public_data ?? null), {
    placement: form.locationOnBody,
    earPiercing: earAnswer,
    county: client?.county,
    countyKind: client?.countyKind,
    // TW-0227 — and the city inside the county, as every requirement reader takes it (Hammond's rule is not Lake County's)
    city: client?.city ?? null,
  });
  const askMinorId = guardianRoute && ruleAsks.includes("both_ids");
  // 2026-09-24 — AND THE PAPER THAT SAYS THIS ADULT IS THE PARENT OR GUARDIAN: the minor's birth certificate,
  // or the adoption, custody or guardianship order — where the shop turned it on, or the state's rule names
  // it for this service (minors.ts asksGuardianProof). Only on a guardian route, for the minor's ID's reason.
  const askGuardianProof =
    guardianRoute &&
    asksGuardianProof(
      client?.public_data,
      minorsRuleFor(service, client?.state, client?.public_data ?? null),
      client?.county,
      client?.countyKind,
      client?.city ?? null,
      undefined,
      earOutside
    );
  // TW-0270 slice 5 (2026-10-16) — AND EACH PAPER THE STATE'S RULE KEEPS WITH A MINOR'S RECORD (minors.ts papersAsked): on a
  // guardian route the notarized consent, the department's form or the affidavit the rule names; on the emancipated route the
  // certified copy of the decree (Nevada). Asked by the rule for the service being signed, in the shop's own jurisdiction,
  // whatever the shop's switches say, and none for an ear piercing the rule does not reach. The API reads the same lists and
  // refuses a signing missing one (MINOR_PAPER_MISSING). 2026-11-03 — but for a paper that waits on the shop's box (New York's
  // and Wisconsin's state form), asked only once the shop ticked it under this service's switch (minorsTicked).
  const papers: Paper[] =
    guardianRoute || (askEmancipated && emancipated)
      ? papersAsked(minorsRuleFor(service, client?.state, client?.public_data ?? null), {
          placement: form.locationOnBody,
          earPiercing: earAnswer,
          county: client?.county,
          countyKind: client?.countyKind,
          city: client?.city ?? null,
          route: guardianRoute ? "guardian" : "emancipated",
          ticked: minorsTicked(service, client?.public_data),
        })
      : [];
  // TW-0227 — THE OTHER PARENT'S WRITTEN PERMISSION. Indiana asks one parent (IC 35-45-21-4(e)); Hammond and East Chicago
  // ask for "written permission from both parents or guardians, if not deceased" (Hammond Code § 127.27, East Chicago
  // Code § 118.003), and Delaware County for both parents "present and provide consent in writing" (Ord. 2017-031 §
  // 3-8-5-1(j)). So on a guardian route in one of them the parent who is there answers ONE of three ways: the other parent
  // signs here, a photo of the permission the other parent signed, or there is no second parent. Where both have to be
  // there, a permission signed somewhere else is not the rule met, so the photo is not offered (and the API refuses a
  // body carrying it). Read for the service being signed, in the shop's own jurisdiction, city included; the ear answer
  // does not touch it, because it is a signature and not one of the rule's photos.
  const signingRule = minorsRuleFor(service, client?.state, client?.public_data ?? null);
  const askSecondParent =
    guardianRoute && requiresBothParents(signingRule, client?.county, client?.countyKind, client?.city ?? null);
  const secondParentHere =
    askSecondParent && requiresBothParentsPresent(signingRule, client?.county, client?.countyKind, client?.city ?? null);
  const secondParentWays: ("signed" | "photo" | "none")[] = secondParentHere ? ["signed", "none"] : ["signed", "photo", "none"];
  // whose rule it is, named the way the refusals name a jurisdiction: the city's or county's own, else the state's
  const secondParentWho = askSecondParent
    ? bothParentsPlace(signingRule, client?.county, client?.countyKind, client?.city ?? null) ||
      stateNameOf(client?.state ?? undefined)
    : "";
  // the way picked, and only a way this signing offers: a photo picked before the page knew both had to be here is no pick
  const secondParentChoice = secondParentWays.find((way) => askSecondParent && way === form.secondParentChoice) ?? "";
  // TW-0267 — THE STATEMENT THE STATE'S RULE HAS THE PARENT OR GUARDIAN SIGN, IN ITS EXACT WORDS. Robert, owner of 2 Ton
  // Gallery in Kingsport, 2026-10-01: "I noticed on the minor waiver it doesn't have the required state paragraph that
  // needs to be in for the parent to check." Tennessee's body piercing rule has the parent sign "a statement with the exact
  // wording as follows" (Tenn. Comp. R. & Regs. 1200-23-06-.03(3)(c)). On a guardian route, for the service whose rule
  // names one (minors.ts guardianAttestationFor): a box above the parent's signature, the words verbatim and in English in
  // both languages, required here and by the API (GUARDIAN_CERTIFICATION_NOT_ACCEPTED). The ear answer does not touch it:
  // the carve-out takes the rule's photos off a signing, never the parent's own consent, which this is. The tick lives
  // apart from the waiver's clauses, for the notices' reason, and is posted only where the box was shown.
  // TW-0526, the client-form side (2026-10-29) — EVERY STATEMENT THE RULE HAS THE PARENT TICK on this signing (minors.ts
  // guardianAttestationsFor), in the rule's order: Illinois's piercing rule has three, the last kept for a piercing in the mouth (the
  // spot picked). The first is the box this page has always drawn, posted by the pair it always was; each after it is a box of its
  // own, posted as a tick in `guardianStatements` by its id
  const statements = guardianRoute ? guardianAttestationsFor(signingRule, form.locationOnBody) : NO_STATEMENTS;
  const certification = statements[0] ?? null;
  const furtherStatements = statements.slice(1);
  // TW-0526 (M01) — a statement in our words carrying the rule's is said in the page's language; a rule's exact words in English,
  // and (the client-form side) a rule's own words with a Spanish rendering of ours beside them in the page's language too
  const statementWords = (statement: Attestation): string =>
    (statement.exact === false || statement.translated === true) && lang === "es" && statement.text_es ? statement.text_es : statement.text;
  const statementLead = (statement: Attestation): string =>
    statement.exact === false
      ? T.guardianPresenceLead(stateNameOf(client?.state ?? undefined))
      : statement.translated === true
        ? T.guardianTranslatedLead(stateNameOf(client?.state ?? undefined))
        : T.guardianCertificationLead(stateNameOf(client?.state ?? undefined));
  const certificationWords = certification ? statementWords(certification) : "";
  const [guardianCertified, setGuardianCertified] = useState<boolean>(false);
  const [statementTicks, setStatementTicks] = useState<Record<string, boolean>>({});
  // TW-0526, the client-form side — the parent's box, in the parent's block on a minor's route: their phone, in every state
  const guardianFields = guardianRoute ? stateFieldsFor(client?.state, "guardian") : NO_FIELDS;
  /** A box's answer as the form holds it, by its body key (every key of the kind is a text key of FormState) */
  const fieldValue = (key: string): string => {
    const value = (form as unknown as Record<string, unknown>)[key];
    return typeof value === "string" ? value : "";
  };
  const placementRefusal: Refusal | null = guardianRoute
    ? minorRefusal({ ...minorAsk, placement: form.locationOnBody, coveringExisting: true })
    : null;
  // The state's own sentence, when it has one. `no_service`, `shop_off` and `no_state` are silent
  // by design (speaksForItself): there is nothing useful to say about a rule we are not applying,
  // and a sentence there would advertise the setting to every shop that never asked for it.
  const minorRefusalMessage = minorSentence(ageRefusal, T, lang);
  const placementRefusalMessage = minorSentence(placementRefusal, T, lang);
  // What the parent is consenting to, in the state's own words — quoted on the screen and printed
  // on the signed waiver. Never summarised: every state's list is different and it is the shop who
  // has to satisfy it.
  const guardianConditions = guardianRoute ? guardianConditionsFor(service, client?.state) : "";
  // 2026-11-03 — and whose they are: where the state sets no rule for this service, the words are the shop's own house rule
  const guardianConditionsHeading = stateSetsNoRule(service, client?.state)
    ? T.guardianShopWants
    : T.guardianStateWants(stateNameOf(client?.state ?? undefined));
  // TW-0153 — does this state allow a minor's tattoo ONLY as a cover-up (TX, TN)? Asked inside the
  // guardian block, because it only matters once the minor route is open at all.
  const askCoverup = guardianRoute && requiresCoverupOnly(minorsRuleFor(service, client?.state, client?.public_data ?? null));
  const coverupRefusal: Refusal | null = askCoverup
    ? minorRefusal({ ...minorAsk, placement: "" })
    : null;
  const coverupRefusalMessage = minorSentence(coverupRefusal, T, lang);

  // What shows under the birth date: the date's own problem if it has one, else the 18+ refusal
  // this form has shown since 2023 — unless the guardian route took it over, or the state has its
  // own sentence to say in its place (rendered separately, below the same field).
  const birthDateIsValidErrorMessage =
    birthDateProblem ||
    // …and an emancipated minor is not refused: they have no guardian route BECAUSE they do not
    // need one, so the 18+ sentence would be the opposite of what their state says
    // TW-0505 — …and not while the state's rules are on their way or never came: the route a minor's state opens is not known
    // yet, so the 18+ sentence would be a no the page has not been told
    (isMinor && rulesHere && !guardianRoute && !emancipated && !minorRefusalMessage ? copy.ageError : null);

  // TW-0227 — the other parent's pad mounts only while "signed" is the way picked, and a pad that mounts is empty
  useEffect(() => {
    setSecondParentSignatureIsEmpty(true);
  }, [secondParentChoice]);

  // the posted `price` follows the choice and the numbers, in the words of the language the client is reading, so a
  // flip to Spanish after typing re-says it in Spanish. Only in the choices; the one box writes `price` itself.
  useEffect(() => {
    if (!askPrice || priceStyle !== "choices") return;
    const composed = composePrice(priceKind, priceParts, T);
    setForm((current) => (current.price === composed ? current : { ...current, price: composed }));
  }, [askPrice, priceStyle, priceKind, priceParts, T]);

  const handleInputChange = (
    // TW-0059 added two <select>s (where a piercing goes, what the jewelry is made of); they set a
    // named field on the form exactly as every input here does.
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    // TW-0503 — a signed date the client moved is theirs: the page stops keeping it today
    if (e.target.name === "signedDate") signedDateMoved.current = true;
    setForm({
      ...form,
      [e.target.name]: e.target.value,
      // TW-0225 — an ear answer is about the spot it was given for, so a new spot asks again
      ...(e.target.name === "locationOnBody" && e.target.value !== form.locationOnBody ? { earPiercing: "" } : {}),
    });
  };

  /** TW-0279 — a cosmetic procedure ticked or unticked: the picks held in the list's order, only ones the list offers, and
   *  the procedures' own refusal taken away once there is one. */
  const toggleProcedure = (value: string, on: boolean) => {
    setForm((was) => {
      const picked = new Set(was.cosmeticProcedures);
      if (on) picked.add(value);
      else picked.delete(value);
      return { ...was, cosmeticProcedures: postedProcedures(Array.from(picked), offeredValues) };
    });
    if (on && refusedFor === "no_procedure") {
      setSubmissionError("");
      setRefusedFor("");
    }
  };

  // TW-0225 — the demo's fifth dial (?ear=1, App.tsx withDemo): the demo client is here for an ear piercing, so a
  // piercing's spot starts at Earlobe. Only the demo shop's client ever carries it, and only an empty spot is filled.
  useEffect(() => {
    if (!client?.demoEar || service !== "piercing") return;
    setForm((current) => (current.locationOnBody ? current : { ...current, locationOnBody: "Earlobe" }));
  }, [client?.demoEar, service]);

  // ── THE DRAFT IN THE TAB (TW-0500; draft.ts has the call, what is kept and what never is) ───────────────────────────
  //
  // Put back ONCE, when the shop's own answer is in (the API's: the static file can be minutes behind it, and a draft put
  // back against its config could be undone the moment the API's lands), and only into a page nobody has typed on yet: a
  // client who started typing first is a new start, and what they type is the draft from then on. Declared after every
  // effect that fills a box when the shop arrives, so what it puts back lands last. Written a beat after each change and at
  // once when the page is hidden, stamped with the client's own last keystroke, so putting a draft back never restarts its
  // 30 minutes. Gone on the signing, on "Start over", and on a crash (App.tsx).
  const shopIsTheTruth = Boolean(client && !client.placeholder && !client.fromFile);
  /** the draft was looked for, once: until then nothing is written, so this load's empty form cannot write over one */
  const draftDecided = useRef(false);
  /** signed, or started over: nothing more is written on this page load */
  const draftOver = useRef(false);
  /** TW-0501 — a post is out and the door has not answered: nothing is written (pauseDraft has why) */
  const draftPosted = useRef(false);
  /** the client's last keystroke or tick on this page load, or the draft's that was put back: what a draft is stamped with */
  const touchedAt = useRef(0);
  const pendingDraft = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [draftRestored, setDraftRestored] = useState(false);
  const noteTouch = () => {
    touchedAt.current = Date.now();
  };

  // what the draft holds now: the typed boxes and the choices (their difference from the blank form, never a key
  // NEVER_DRAFTED names), the ticks beside them, a tick whose words carry their own fingerprint by that fingerprint (the
  // card-fee box's id, the practice's notice's version), and the language only where the client switched it. The fixed
  // jewelry is the shop's, filled by the page, so it is no reason to keep a draft.
  // TW-0501 — a shop in HIPAA mode's draft keeps no health answer (draft.ts neverDraftedFor has the call and its why)
  const hipaaDraft = client?.hipaa === true;
  const neverDrafted = neverDraftedFor(hipaaDraft, BLANK_FORM);
  const draftForm = draftOf(form, BLANK_FORM, neverDrafted);
  if (fixedJewelry && draftForm.jewelryMaterial === fixedJewelry) delete draftForm.jewelryMaterial;
  const draftTicks = {
    notices: ticksOf(noticeTicks),
    // TW-0526, the client-form side — the parent's ticks on the statements after the first, by their ids
    guardianStatements: ticksOf(statementTicks),
    ownClauses: ticksOf(ownClauseTicks),
    // the shop's own questions sit among the health questions on the page, so a HIPAA shop's draft keeps none of them
    ownQuestions: hipaaDraft ? {} : answersOf(ownQuestionAnswers, OWN_ANSWERS),
    priceParts: partsOf(priceParts, PRICE_PART_KEYS),
  };
  const draftChoices = {
    cashPreferred: cashPreferred && cashPreferredTicked ? cashPreferred.id : undefined,
    privacyNotice: privacyNotice && privacyNoticeTicked ? privacyNotice.version : undefined,
    guardianCertified: guardianCertified || undefined,
    idType: idType || undefined,
    artistChoice: artistChoice || undefined,
    priceKind: priceKind || undefined,
  };
  const draftValues: Record<string, unknown> = {
    form: draftForm,
    service: service || undefined,
    lang: langTouched.current ? lang : undefined,
    ...draftTicks,
    ...draftChoices,
  };
  const draftHasContent =
    Object.keys(draftForm).length > 0 ||
    Object.values(draftTicks).some((map) => Object.keys(map).length > 0) ||
    Object.values(draftChoices).some(Boolean);
  const draftJson = JSON.stringify(draftValues);
  const draftNow = useRef({ shop: "", state: "", values: draftValues, hasContent: false });
  draftNow.current = { shop: client?.subdomain ?? "", state: client?.state ?? "", values: draftValues, hasContent: draftHasContent };

  const writeDraftNow = useCallback(() => {
    const now = draftNow.current;
    if (!draftDecided.current || draftOver.current || draftPosted.current || !touchedAt.current || !now.shop) return;
    // nothing typed or ticked any more (every box emptied again): nothing to keep
    if (!now.hasContent) {
      forgetDraft(now.shop, FORM_DRAFT_VERSION);
      return;
    }
    writeDraft({ v: FORM_DRAFT_VERSION, shop: now.shop, state: now.state, at: touchedAt.current, values: now.values });
  }, []);
  const scheduleDraft = useCallback(() => {
    if (!draftDecided.current || draftOver.current || draftPosted.current || !touchedAt.current) return;
    if (pendingDraft.current) clearTimeout(pendingDraft.current);
    pendingDraft.current = setTimeout(() => {
      pendingDraft.current = null;
      writeDraftNow();
    }, DRAFT_WRITE_AFTER_MS);
  }, [writeDraftNow]);

  // a beat after each change to what the draft holds (the JSON is the trigger, so a render that changed nothing is none)
  useEffect(() => {
    scheduleDraft();
  }, [draftJson, scheduleDraft]);

  // …and at once when the page is hidden: the client went to the camera or a message, which is when a phone's browser drops
  // a tab, and a write still waiting on its beat would be lost with it
  useEffect(() => {
    const flush = () => {
      if (!pendingDraft.current) return;
      clearTimeout(pendingDraft.current);
      pendingDraft.current = null;
      writeDraftNow();
    };
    const onHidden = () => {
      if (document.visibilityState === "hidden") flush();
    };
    document.addEventListener("visibilitychange", onHidden);
    window.addEventListener("pagehide", flush);
    return () => {
      document.removeEventListener("visibilitychange", onHidden);
      window.removeEventListener("pagehide", flush);
      // an unmount writes nothing: the page is still open (the choice the QR code opens took the form's place, before anyone
      // typed), and a test's cleanup leaves no draft for the next
      if (pendingDraft.current) clearTimeout(pendingDraft.current);
      pendingDraft.current = null;
    };
  }, [writeDraftNow]);

  /** Put a draft back, every part checked against what this page asks now; true when something typed or ticked came back. */
  const putDraftBack = (draft: Draft): boolean => {
    const values = draft.values;
    let back = withDraft(BLANK_FORM, BLANK_FORM, values.form, neverDrafted).restored;
    if (back) setForm((was) => withDraft(was, BLANK_FORM, values.form, neverDrafted).next);
    if (typeof values.service === "string" && (services as string[]).includes(values.service)) {
      setService(values.service as ServiceType);
    }
    if (typeof values.lang === "string" && (enabled as string[]).includes(values.lang)) {
      langTouched.current = true;
      setLang(values.lang as Lang);
    }
    const notices = ticksOf(values.notices);
    if (Object.keys(notices).length) {
      setNoticeTicks(notices);
      back += 1;
    }
    const statementsTicked = ticksOf(values.guardianStatements);
    if (Object.keys(statementsTicked).length) {
      setStatementTicks(statementsTicked);
      back += 1;
    }
    const clauses = ticksOf(values.ownClauses);
    if (Object.keys(clauses).length) {
      setOwnClauseTicks(clauses);
      back += 1;
    }
    const answers = hipaaDraft ? {} : answersOf(values.ownQuestions, OWN_ANSWERS);
    if (Object.keys(answers).length) {
      setOwnQuestionAnswers(answers);
      back += 1;
    }
    // a tick on the card-fee box or the practice's notice comes back only beside the words it was given on
    if (cashPreferred && values.cashPreferred === cashPreferred.id) {
      setCashPreferredTicked(true);
      back += 1;
    }
    if (privacyNotice && values.privacyNotice === privacyNotice.version) {
      setPrivacyNoticeTicked(true);
      back += 1;
    }
    if (values.guardianCertified === true) {
      setGuardianCertified(true);
      back += 1;
    }
    if (isIdType(values.idType)) {
      setIdType(values.idType);
      back += 1;
    }
    // the artist list's own effect checks the pick against the list it has (artists.ts choiceAfterListChange)
    if (typeof values.artistChoice === "string" && values.artistChoice) {
      setArtistChoice(values.artistChoice);
      back += 1;
    }
    if (values.priceKind === "fixed" || values.priceKind === "range" || values.priceKind === "hourly") {
      setPriceKind(values.priceKind);
      back += 1;
    }
    const parts = partsOf(values.priceParts, PRICE_PART_KEYS);
    if (Object.keys(parts).length) {
      setPriceParts({ ...NO_PRICE_PARTS, ...parts });
      back += 1;
    }
    return back > 0;
  };

  useEffect(() => {
    if (draftDecided.current || !shopIsTheTruth || !client) return;
    draftDecided.current = true;
    const draft = readDraft(client.subdomain, FORM_DRAFT_VERSION);
    if (draft) {
      // a client already typing is a new start, and a shop whose state moved under the draft is a changed form
      const putBack = !touchedAt.current && draft.state === (client.state ?? "") && putDraftBack(draft);
      if (putBack) {
        touchedAt.current = draft.at;
        setDraftRestored(true);
        // TW-0501 — and counted, once per load: `shop`, `lang`, `service`, never a value it put back (beacon.ts)
        track("form_draft_restored", { shop: client.subdomain, lang, service: service || undefined });
      } else {
        forgetDraft(client.subdomain, FORM_DRAFT_VERSION);
      }
    }
    // what was typed before the shop's answer came in is written now, not at the next keystroke
    scheduleDraft();
    // once, when the shop's own answer is in: what it reads is that render's
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopIsTheTruth, client]);

  /** The draft is over: signed, or started over. Nothing more is written on this page load, and what was written is gone. */
  const endDraft = () => {
    draftOver.current = true;
    if (pendingDraft.current) clearTimeout(pendingDraft.current);
    pendingDraft.current = null;
    if (client) forgetDraft(client.subdomain, FORM_DRAFT_VERSION);
  };
  /** TW-0501 — THE DRAFT IS SET ASIDE WHILE A POST IS OUT (Grace's note on the card, 2026-10-26: "the draft is cleared the
   *  moment the signing door ACCEPTS the post ... so a confirmation lost on the way back never leaves a draft that re-signs").
   *  The draft went at the signing's 200 since TW-0500; what that left was the answer that never arrives: the door signs, the
   *  phone loses the line before the 200 reaches it, the page says the waiver did not send, and a reload put the whole draft
   *  back to be signed a second time. So the moment the press posts, the stored draft is gone and nothing more is written. The
   *  door's answer settles it: a signing ends it (endDraft), a refusal or a failure on our side gives it back at once, written
   *  from the page, which holds every box (resumeDraft), and a post that never came back gives it back with the client's next
   *  keystroke, because the door may have signed it. The door's half of the same defect is its repeat guard (app.py). */
  const pauseDraft = () => {
    draftPosted.current = true;
    if (pendingDraft.current) clearTimeout(pendingDraft.current);
    pendingDraft.current = null;
    if (client) forgetDraft(client.subdomain, FORM_DRAFT_VERSION);
  };
  /** The door answered without signing (`now`: written at once), or the post never came back (written at the next change). */
  const resumeDraft = (now: boolean) => {
    draftPosted.current = false;
    if (now) writeDraftNow();
  };
  /** "Start over": the draft gone, then the page loaded again, the way "Submit another waiver" opens the next client's form. */
  const startOver = () => {
    endDraft();
    draftPage.reload();
  };

  const handleRadioChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    key: keyof typeof form.questionnaire
  ) => {
    setForm({
      ...form,
      questionnaire: { ...form.questionnaire, [key]: e.target.value },
    });
  };

  const handleAcknowledgmentAndWaiverChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    key: keyof typeof form.acknowledgmentAndWaiver
  ) => {
    setForm({
      ...form,
      acknowledgmentAndWaiver: {
        ...form.acknowledgmentAndWaiver,
        [key]: e.target.checked,
      },
    });
  };

  /** 2026-10-26 (docs/FORM-AUDIT-2026-10-26.md) — A CLAUSE REFUSAL MARKS EVERY BOX STILL UNTICKED, not only the one it names.
   *  The submit names the first unticked box, and a client who had skipped six pressed Submit six more times to learn the next
   *  one each time: 572 clause refusals in 219 visits from 2026-09-23 to 10-25, two seconds apart at the median, one visit 48
   *  presses long and never signed. While one of the box refusals is the sentence on the page, every box the signature depends
   *  on and nobody ticked says so in red, and each one's mark goes the moment it is ticked. */
  const boxRefusalUp = BOX_REFUSALS.includes(refusedFor) && Boolean(submissionError);
  // TW-0541 — a box the browser's own stop named (the `invalid` listener below), marked while it is still empty or wrong:
  // the mark goes the moment the client fills it, and the line under it says what to do in the page's words
  const nativeMissedHere = (name: string, stillSo: boolean) =>
    isNativeStop(refusedFor) && refusedDetail === name && stillSo && Boolean(submissionError);
  const nativeLine = (box: "pick" | "fill") =>
    refusedFor === "native_missing" ? (box === "pick" ? T.pickOneHere : T.fillThisIn) : T.fixThisBox;
  /** 2026-10-26 — one of the two birth date refusals is the sentence on the page (TW-0503: birth_date_not_valid, minor_not_taken) */
  const ageRefusalUp = (refusedFor === "birth_date_not_valid" || refusedFor === "minor_not_taken") && Boolean(submissionError);
  /** The refusal's sentence, said under the details box it is about until something is typed in it (2026-10-01): the
   *  sentence under the question X1 says, for a yes whose box was left empty. "" everywhere else. */
  const detailsRefusedHere = (box: DetailsBox): string =>
    refusedFor === "health_details" && refusedDetail === box && !hasDetails(form[box]) ? submissionError : "";

  /** A health question's own words on this page, in the client's language: what its refusal names (2026-10-01). On a
   *  cosmetic form, the cosmetic words (TW-0279): its own questions, and the skin and allergies questions as it asks them;
   *  its keloid question is its own words, not the piercing's. */
  const healthQuestionLabel = (key: HealthQuestion): string => {
    // TW-0526 (M04) — a question the contract carries in its rule's words is named by those words
    const worded = wordedQuestions.find((question) => question.key === key);
    if (worded) return lang === "es" ? worded.es : worded.en;
    // the page's own words for it: a state's question in its record's (TW-0279 slice 9), else the list's
    const cosmetic = isCosmetic
      ? (cosmeticQuestions.find((question) => question.name === key) ?? COSMETIC_QUESTIONS.find((question) => question.name === key))
      : undefined;
    if (cosmetic) return cosmeticQuestionWords(cosmetic, lang).label;
    const piercing = PIERCING_QUESTIONS.find((question) => question.name === key);
    if (piercing) return questionWords(piercing, lang).label;
    const words: Record<string, string> = {
      influenceOfDrugsOrAlcohol: T.qInfluence,
      pregnantOrNursing: T.qPregnant,
      communicableDisease: T.qCommunicable,
      skinConditions: isCosmetic ? T.qSkinCosmetic : T.qSkin,
      allergies: isCosmetic ? T.qAllergiesCosmetic : T.qAllergies,
      bleedingDisorders: T.qBleeding,
      eatenRecently: T.qEaten,
      numbingProduct: T.qNumbing,
      diabetesHistory: T.qDiabetesHistory,
      seizuresOrFainting: T.qSeizuresOrFainting,
      heartOrBloodPressure: T.qHeartOrBloodPressure,
      bloodThinnersRecently: T.qBloodThinnersRecently,
      pregnantRecently: T.qPregnantRecently,
    };
    return words[key] ?? "";
  };

  /** Refuse the submission AND count it. Nothing else in this function may call
   *  setSubmissionError directly on a path that returns.
   *
   *  A client stopped here never reaches the API, so no 5xx fires, no alarm mails and no card is
   *  made — the Texas batch-photo wall stood for two days in exactly this silence (TW-0129). Every
   *  refusal below is legitimate today; the point is that we can now SEE how often each one stops a
   *  real person, and a destructive one shows up as a spike instead of as a lost shop.
   *
   *  `reason` is a fixed code from this file, never the message: messages are built from the shop's
   *  name and the client's own answers.
   *
   *  AND IT TAKES THE PERSON TO THE QUESTION (2026-09-29, refusal.ts has the morning it was earned:
   *  a new shop's owner pressed Submit seven times under "Tell us whether today is a tattoo or a
   *  piercing." with the two choices most of a page above him). `detail` says WHICH question when
   *  the reason alone does not: the health question's own name, the clause's own key. */
  const refuse = (reason: string, message: string, detail = "") => {
    setSubmissionError(message);
    setRefusedFor(reason);
    setRefusedDetail(detail);
    // 2026-10-26 — and WHICH box (the question's key, the clause's, never what is in it), WHERE on the page, the SHOP's
    // state and the language the page was showing (`formLang`: the intake writes the browser's own over `lang`), so the
    // read of refusals at the signing door (`./run.sh refusals`) can say what a client met (refusal.ts has why)
    track("form_refused", {
      shop: client?.subdomain,
      lang,
      formLang: lang,
      service: service || undefined,
      reason,
      field: refusalField(reason, detail),
      step: refusalStep(reason),
      state: client?.state ?? undefined,
    });
    goToRefusal(reason, detail);
  };

  /** TW-0501 — A CAMERA THAT DID NOT OPEN on one of the photo screens (PhotoUploader.tsx), beaconed with the photo it was for
   *  (its body key) and where on the page, the refusal's keys beside it: the next read of the ID photo's refusals can say how
   *  many were a camera the browser kept from the page. Nothing here is awaited. */
  /** TW-0628 — A PROTECTION THE SHOP'S OWNER TURNED OFF LET THE CLIENT THROUGH where the page would have stopped them: beaconed
   *  in a refusal's shape (`reason` the protection's id, `field` the question, `step`), once a visit per protection, so a read
   *  can count how often one did. Never a refusal: nothing is said to the client, and nothing here is awaited. */
  const protectionsLetThrough = useRef<Set<string>>(new Set());
  const letThrough = (id: string, field: string, step: RefusalStep) => {
    if (protectionsLetThrough.current.has(id)) return;
    protectionsLetThrough.current.add(id);
    track("form_protection_off", {
      shop: client?.subdomain,
      lang,
      formLang: lang,
      service: service || undefined,
      reason: id,
      field,
      step,
      state: client?.state ?? undefined,
    });
  };

  const cameraFailed = (field: string, step: RefusalStep) => (reason: CameraFailure) =>
    track("form_camera_failed", {
      shop: client?.subdomain,
      lang,
      formLang: lang,
      service: service || undefined,
      reason,
      field,
      step,
      state: client?.state ?? undefined,
    });

  // TW-0501 — THE BROWSER'S OWN STOP, COUNTED (refusal.ts nativeStop). A Submit the browser stops at an empty or malformed box
  // never reaches handleSubmit, so the form's own refusals never saw it. The `invalid` event does not bubble, so it is heard in
  // the capture phase on the form; the browser fires one for every box it finds wrong, top to bottom, and names the first, so
  // the first of each press is the row, and the rest of that press are the same press. A listener that reads the box's name and
  // its validity, never its value.
  const nativeNow = useRef({ shop: "", lang: "en", service: "", state: "" });
  nativeNow.current = { shop: client?.subdomain ?? "", lang, service: service || "", state: client?.state ?? "" };
  const detachNative = useRef<(() => void) | null>(null);
  /** the form element's own ref: the listener goes on when the form is drawn and comes off when it goes */
  const formRef = useCallback((el: HTMLFormElement | null) => {
    detachNative.current?.();
    detachNative.current = null;
    if (!el) return;
    let thisPress = false;
    const heard = (event: Event) => {
      try {
        if (thisPress) return;
        thisPress = true;
        setTimeout(() => {
          thisPress = false;
        }, 0);
        const box = event.target as HTMLInputElement | null;
        const name = (box?.getAttribute?.("name") || box?.id || "").trim();
        const now = nativeNow.current;
        const kind = nativeStop(box?.validity);
        track("form_refused", {
          shop: now.shop || undefined,
          lang: now.lang,
          formLang: now.lang,
          service: now.service || undefined,
          reason: kind,
          field: name || undefined,
          step: name ? stepOfBox(name, HEALTH_QUESTION_KEYS) : undefined,
          state: now.state || undefined,
        });
        // TW-0541 (2026-10-29) — AND THE CLIENT IS TAKEN TO THE BOX, THE PAGE'S OWN WORDS UNDER IT. Measured on 2 Ton
        // Gallery's form at the iPhone profile (390x844): a Submit pressed at the bottom with the artist box empty lands
        // the page on that box with the browser's focus ring and, in WebKit, no message of any kind (Chromium draws
        // "Please select an item in the list."); 391 of the 432 presses the browser stopped in the 7 days to 10-29 were
        // iPhones, 218 of them at the artist box, 5 presses a visit, one visit 51: a client who could not see what the
        // tap was asking for. So the stop is answered the way the form answers its own refusals (refusal.ts): the box
        // centred, marked, its sentence under it, and the sentence under the Submit button for the client who scrolls
        // back down. The browser's `required` stays (native first; Chris, 2026-10-10: "I prefer to build with native
        // props"); this is the backstop where the browser says nothing. The browser's own focus follows this scroll, and
        // a box already in view is not scrolled again.
        if (name) {
          const words = strings(now.lang as Lang);
          setSubmissionError(kind === "native_missing" ? words.aboveStillEmpty : words.aboveNeedsAFix);
          setRefusedFor(kind);
          setRefusedDetail(name);
          goToRefusal(kind, name);
        }
      } catch {
        /* the browser's own stop stands, counted or not */
      }
    };
    el.addEventListener("invalid", heard, true);
    detachNative.current = () => el.removeEventListener("invalid", heard, true);
  }, []);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!client) {
      refuse("no_shop", T.invalidClient);
      return;
    }
    if (submitting) {
      return;
    }
    // TW-0505 — THE SHOP'S STATE'S RULES ARRIVE AFTER THE SHOP (stateRules.ts), and nothing is posted before they are here: a
    // page drawn without them is one the door refuses after the photo and the signature, for a question it never showed. On
    // its way, one moment; never came, the page says so and asks again.
    if (rules?.status === "loading") {
      refuse("state_rules_loading", T.stateRulesLoading);
      return;
    }
    if (rules?.status === "failed") {
      rules.retry();
      refuse("state_rules_failed", T.stateRulesFailed);
      return;
    }
    // TW-0059 — a shop that does both asks, and there is no default answer. The browser's own
    // `required` is not trusted here (it is dropped on mobile all through this form), so the gate
    // is this line: without a pick, nothing is posted and the client is told which tap is missing.
    if (!service) {
      refuse("no_service", T.tellUsService(serviceNouns));
      return;
    }
    // TW-0503 (2026-10-29) — THE BIRTH DATE IS THE AGE, so it is asked FIRST, right after the choice of service (the form audit's
    // order, docs/FORM-AUDIT-2026-10-26.md: the first box with a question on the page, before the photos and the signature). An
    // age typed beside a birth date that disagreed was refused here since that morning (`birth_date_disagrees`, about 2 clients a
    // day) and, before it, posted: the door read the typed age, and signed five minors' piercings with no parent on them. There is
    // no typed age now, so nothing can disagree. A two-digit year still in its box is read the way it is once the client leaves
    // it: the press settles it and the page draws what that age opens (the parent's block, a sentence) before anything is asked.
    // Then three boxes typed that make no day, a day after the day of signing, or a year of one or three digits: "Please enter a
    // valid birth date.", at the birth date. The page showed that sentence before and posted anyway beside a typed adult age;
    // with no other age to read it is asked now. A box left empty is the browser's own stop, as it always was (the three are
    // `required`, and TW-0541 says the page's words where the browser says none): nothing new is asked of it here.
    if (age !== ageOnceLeft) {
      (document.activeElement as HTMLElement | null)?.blur?.();
      setAnyAgeInputIsFocused(false);
      return;
    }
    if (birthDateProblem) {
      refuse("birth_date_not_valid", birthDateProblem);
      return;
    }
    // …AND A CLIENT UNDER 18 WHERE THE SHOP TAKES NO MINORS FOR THIS SERVICE, OR THE FORM KNOWS NO STATE: the 18+ sentence under
    // the birth date is one of the refusals the form does not speak for (minors.ts SILENT), which the submit never read, so the
    // whole waiver went up, photo and signature with it, and the API answered in English under the button ("This shop does not
    // tattoo clients under 18.", 6 in the 7 days to 2026-10-26, one of them followed by a signing at an older age typed). A
    // cosmetic tattoo keeps its own refusal below, in its own words.
    if (isMinor && !guardianRoute && !emancipated && !minorRefusalMessage && !isCosmetic) {
      refuse("minor_not_taken", copy.ageError);
      return;
    }
    // TW-0526, the client-form side (2026-10-29) — A FACT ABOUT THE CLIENT THE SHOP'S STATE'S RULE REQUIRES (Mississippi's sex as on
    // the ID), beside the birth date: the box is `required`, so the browser stops first and TW-0541's line says why; this is the
    // page's own refusal where a browser does not. The API refuses the same body (STATE_FIELD_UNANSWERED)
    const youMissing = youFields.find((box) => box.required && !fieldAnswered(box, fieldValue(box.key)));
    if (youMissing) {
      refuse("you_field_unanswered", lang === "es" ? youMissing.missingEs : youMissing.missingEn, youMissing.key);
      return;
    }
    // A shop with a list of artists gets a picker, and the browser's own `required` is not trusted
    // here any more than it is anywhere else on this page. A shop with no list is not asked: the
    // plain field has never had a gate of its own and this is not the change that adds one.
    if (askArtist && asksWhichArtist(artists) && !artistNameFor(artistChoice, form.nameOfArtist)) {
      refuse("no_artist", artistSearch ? showCopy.missing : artistCopy.missing);
      return;
    }
    // TW-0279 — A COSMETIC TATTOO NAMES ITS WORK: one procedure or more, and what this visit is, both under the artist on
    // the page. Refused here, never by the browser's `required`, because the procedures decide which questions and which
    // clauses are below them, and a record of a cosmetic tattoo naming no procedure is one Georgia's door refuses after the
    // client has signed (an unverifiable procedure, app.COSMETIC_PROCEDURE_UNVERIFIABLE).
    if (isCosmetic && !procedures.length) {
      refuse("no_procedure", T.needCosmeticProcedure);
      return;
    }
    if (isCosmetic && !COSMETIC_VISITS.some((visit) => visit.value === form.cosmeticVisit)) {
      refuse("no_cosmetic_visit", T.needCosmeticVisit);
      return;
    }
    // TW-0152 — the address is asked about once, then it is theirs. No submissionError: the offer
    // sits under the field they would fix, and an offer is not an error.
    const meant = suggestEmail(form.email);
    if (meant && !emailReoffered.current) {
      emailReoffered.current = true;
      setDidYouMean(meant);
      track("email_typo_offered", { lang });
      emailRef.current?.focus();
      emailRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
      return;
    }
    // TW-0225 — THE EAR QUESTION, where it is asked: a yes or a no, never nothing. In page order (it sits under the spot,
    // above the price), and ahead of the photo gates below, which its answer decides: a Michigan earlobe must not be
    // asked for the minor's ID that a yes would have taken away.
    if (askEar && earAnswer === null) {
      refuse("ear_unanswered", earQuestion);
      return;
    }
    // 2026-09-23 — HOW THE CLIENT IS PAYING, when the shop asks: one of the three, or nothing is posted. It is the one
    // question in this block a shop may REQUIRE, and the browser's own `required` is not trusted on a phone here any
    // more than anywhere else on this page, so this line is the gate. It sits in page order: after the email offer
    // (the email is above it) and before who to call and the health questions (below it).
    if (askPaymentMethod && !isPaymentMethod(form.paymentMethod)) {
      refuse("no_payment_method", T.needPaymentMethod);
      return;
    }
    // WHO TO CALL (2026-09-11) — all four, in the order they sit on the page, when the section is asked.
    // Checked here rather than by the browser's own `required`, which this form has never trusted on a
    // phone. A number needs its digits: an emergency contact nobody can dial is not one.
    if (askEmergency) {
      if (!form.emergencyContactName.trim()) {
        refuse("no_emergency_name", T.needEmergencyName);
        return;
      }
      if (!form.emergencyContactRelationship.trim()) {
        refuse("no_emergency_relationship", T.needEmergencyRelationship);
        return;
      }
      if (form.emergencyContactPhone.replace(/\D/g, "").length < 7) {
        refuse("no_emergency_phone", T.needEmergencyPhone);
        return;
      }
      if (!form.emergencyContactAddress.trim()) {
        refuse("no_emergency_address", T.needEmergencyAddress);
        return;
      }
    }
    // Add form submission logic here
    // First see if any of the questionaire items are "yes". If so, show an error.
    let errorMessage = "";
    // which question the sentence is about, by its input's own name: where the refusal takes them
    let healthQuestion = "";
    // TW-0293 — a question this page did not ask is never a refusal: its answer stays the "" it started on
    if (askInfluence && form.questionnaire.influenceOfDrugsOrAlcohol !== "no") {
      errorMessage = T.noInfluence;
      healthQuestion = "influenceOfDrugsOrAlcohol";
    } else if (form.questionnaire.pregnantOrNursing === "yes" && !pregnancyRefusalOff) {
      errorMessage = T.noPregnant;
      healthQuestion = "pregnantOrNursing";
    } else if (
      askCommunicable &&
      form.questionnaire.communicableDisease === "yes" &&
      !form.communicableDiseaseDetails.trim()
    ) {
      errorMessage = T.needCommunicableDetails;
      healthQuestion = "communicableDiseaseDetails";
    } else if (form.questionnaire.skinConditions === "yes") {
      // No error message here...
    }
    if (errorMessage) {
      refuse("health_question", errorMessage, healthQuestion);
      return;
    }
    // TW-0628 — a pregnancy yes past the refusal its owner turned off: no stop, the skip beaconed (letThrough above)
    if (pregnancyRefusalOff && form.questionnaire.pregnantOrNursing === "yes") letThrough(PREGNANCY_REFUSAL, "pregnantOrNursing", "health");
    // TW-0279 — AN ANSWER THE SHOP'S STATE BARS A COSMETIC TATTOO ON (New Jersey's isotretinoin, N.J.A.C. 8:27-8.4(a)6):
    // the rule's own sentence, said under the question too, and the page taken to it. The same kind of stop as the two
    // just above (a yes the form cannot sign), and the API refuses the same body (COSMETIC_ANSWER_REFUSED).
    if (cosmeticAnswerBar) {
      refuse("cosmetic_answer_refused", lang === "es" ? cosmeticAnswerBar.refusal.es : cosmeticAnswerBar.refusal.en, cosmeticAnswerBar.question);
      return;
    }
    // 2026-10-01 — A QUESTION THE PAGE ASKED AND NOBODY ANSWERED (healthQuestions.ts: the all-states audit's X1). The
    // radios drop `required` on a phone, so this line is the gate: the first question asked, not optional, with neither a
    // yes nor a no. It comes after the three refusals above and keeps page order with them: theirs are about the first
    // three questions and the details under the third, so nothing they refuse sits below a question this one names. The
    // sentence names the question in the client's own language, and the page is taken to it. The API refuses the same
    // body (HEALTH_QUESTION_UNANSWERED), so a state's own question can no longer reach the signed waiver as "(N/A)".
    // 2026-10-01 — AND A YES WHOSE BOX WAS LEFT EMPTY (TW-0270; healthQuestions.ts DETAILS_BOX), in the same walk, so the
    // page is taken to whichever comes first on it: the box, with its cursor, and the sentence names the question it hangs
    // under. The API refuses the same body (HEALTH_DETAILS_UNANSWERED), so "Yes (unknown)" can no longer reach the paper.
    // The communicable disease box keeps its own sentence above, which it has had since 2023.
    // 2026-10-08 (TW-0279 slice 8, and that evening's correction) — A YES THAT ASKS FOR A PHOTO (healthQuestions.ts PHOTO_ON_YES:
    // Michigan's numbing product and the doctor's prescription for it) IS NEVER A GAP: the client signs with or without the
    // photo, the API writes on the record which it was, and the ARTIST's signature waits until the shop adds the photo from
    // its dashboard (the API's prescription.py). Nothing here may stop a client who filled everything in (Chris, 2026-09-10).
    const gap = firstHealthGap(
      healthQuestionsAsked({
        allergies: askAllergies,
        bleeding: askBleeding,
        eaten: askEaten,
        numbing: askNumbing,
        diabetes: askDiabetes,
        seizures: askSeizures,
        heart: askHeart,
        bloodThinners: askBloodThinners,
        pregnantRecently: askPregnantRecently,
        worded: wordedQuestions.map((question) => question.key),
        influence: askInfluence,
        communicable: askCommunicable,
        service,
        location: form.locationOnBody,
        procedures,
        state: client?.state,
        publicData: client?.public_data,
      }),
      form.questionnaire,
      form,
      optionalQuestions
    );
    if (gap?.kind === "unanswered") {
      refuse("health_unanswered", T.needHealthAnswer(healthQuestionLabel(gap.question)), gap.question);
      return;
    }
    if (gap?.kind === "details") {
      refuse("health_details", T.needHealthDetails(healthQuestionLabel(gap.question)), gap.box);
      return;
    }
    // TW-0412 — AND THE SHOP'S OWN QUESTIONS, drawn right after the health questions: the first one with neither a yes nor a
    // no, named in the shop's words in the health questions' own sentence, and the page taken to its radios. The API refuses
    // the same body (OWN_QUESTION_UNANSWERED). The line under an answer is never a gate.
    const ownUnanswered = firstOwnQuestionUnanswered(ownQuestions, ownQuestionAnswers);
    if (ownUnanswered) {
      refuse("own_question_unanswered", T.needHealthAnswer(ownUnanswered.text), ownUnanswered.id);
      return;
    }
    // TW-0526, the client-form side (2026-10-29) — AND A HEALTH BOX THE SHOP'S STATE'S RULE REQUIRES (Oklahoma's time the client
    // last ate), drawn after the state's questions: the browser's own stop first, this where a browser does not. The API refuses the
    // same body (STATE_FIELD_UNANSWERED)
    const healthMissing = healthFields.find((box) => box.required && !fieldAnswered(box, fieldValue(box.key)));
    if (healthMissing) {
      refuse("health_field_unanswered", lang === "es" ? healthMissing.missingEs : healthMissing.missingEn, healthMissing.key);
      return;
    }
    // Next, see if any of the acknowledgment and waiver items are false. If so, show an error.
    // The order and the sentences are the service's own (service.ts): a tattoo's are word for word
    // what this form has said since 2023, and a piercing has one more box than a tattoo does. A
    // cosmetic tattoo's are the ones this visit shows (acksShown: its state's lines, its procedures).
    let untickedClause = "";
    for (const key of acksShown) {
      if (!form.acknowledgmentAndWaiver[key]) {
        errorMessage = ackError(key, service, client ? client.name : "the shop", lang);
        untickedClause = key;
        break;
      }
    }
    if (errorMessage) {
      refuse("acknowledgment_unticked", errorMessage, untickedClause);
      return;
    }
    // TW-0170 — a state notice the client ticks is a clause like the ones above: unticked, the waiver is not
    // signed. South Carolina's rule says the blood-donor notice "shall be signed by the client".
    const unticked = notices.find((n) => n.acknowledge && !noticeTicks[n.id]);
    if (unticked) {
      // 2026-10-26 — with the notice's id, so the page is taken to its box (refusal.ts BY_ID) and the beacon names it
      refuse("notice_unticked", (lang === "es" ? unticked.error_es : unticked.error_en) ?? "", unticked.id);
      return;
    }
    // 2026-09-24 — and the shop's own clauses: every one is a clause the signature depends on, named by its
    // opening words when it is missed, so a client with five boxes knows which one.
    const untickedOwn = ownClauses.find((clause) => !ownClauseTicks[clause.id]);
    if (untickedOwn) {
      refuse("own_clause_unticked", ownClauseError(untickedOwn, lang), untickedOwn.id);
      return;
    }
    // 2026-10-02 — and the card-fee box, where the shop has it on: the signature depends on it like every clause
    if (cashPreferred && !cashPreferredTicked) {
      refuse("cash_preferred_unticked", T.needCashPreferred);
      return;
    }
    // 2026-10-08 — and the practice's Notice of Privacy Practices, where a shop in HIPAA mode set one: the signature depends on
    // the client saying they received it (45 CFR 164.520(c)(2)(ii)); the API refuses the same body (NPP_NOT_ACKNOWLEDGED)
    if (privacyNotice && !privacyNoticeTicked) {
      refuse("privacy_notice_unticked", T.needPrivacyNotice);
      return;
    }
    // 2026-09-22 — THE TERMS BOX IS REQUIRED ON A PHONE TOO. It is the one box in this section that is
    // not in ACK_ORDER (it is the shop's terms, not a service clause), so the loop above never read it,
    // and `required` on the input is the attribute this form has never trusted on a phone — so at the
    // counter, on the device every client actually signs on, a waiver could be signed with the terms
    // unticked. A shop owner found it on her first day (Piercinks, Miami, 2026-09-22: "you can submit
    // the waiver without checking accepting the terms"). The API refuses the same body (app.py). The
    // two boxes under it stay optional: TW-0113's split, the signature never depends on them.
    if (!form.acknowledgmentAndWaiver.termsAndConditions) {
      refuse("terms_unticked", T.needTerms);
      return;
    }
    // TW-0488 (2026-10-26) — WHICH ID IT IS AND ITS NUMBER, where the shop's state's rule keeps them (idType.ts), in page order:
    // the choice, the number under it, then the photo. The number is never judged by its shape, only whether it has a letter
    // or a digit in it. The API refuses the same body (ID_TYPE_UNANSWERED, ID_NUMBER_UNANSWERED).
    if (mustPickIdType && !isIdType(idType)) {
      refuse("no_id_type", T.needIdType);
      return;
    }
    if (askIdNumber && !idNumberGiven(form.idNumber)) {
      refuse("no_id_number", T.needIdNumber);
      return;
    }
    if (!photoOfDriversLicense) {
      refuse("no_license_photo", T.needIdPhoto);
      return;
    }
    // TW-0153 — a state that asks for both gets both, or the signing does not go through: a file
    // with the parent's ID and not the minor's is the gap this exists to close
    if (askMinorId && !photoOfMinorId) {
      refuse("no_minor_id_photo", T.minorIdPhoto);
      return;
    }
    // 2026-09-24 — the birth certificate or guardianship papers, where asked: the API refuses the same body
    if (askGuardianProof && !photoOfGuardianProof) {
      refuse("no_guardian_proof_photo", T.needGuardianProofPhoto);
      return;
    }
    // TW-0270 slice 5 — each paper the state's rule keeps with a minor's record, where asked: the API refuses the same body
    // (MINOR_PAPER_MISSING). The paper's id says WHICH block to take the person to, because one signing can ask for two.
    const paperMissing = papers.find((paper) => !paperPhotos[paper.id]);
    if (paperMissing) {
      refuse("no_paper_photo", T.needPaperPhoto(paperMissing), paperMissing.id);
      return;
    }
    // TW-0279 slice 10 — whether the scalp work covers scars, where asked (it sits under the ID photo): the API refuses the
    // same body (SCALP_SCARS_UNANSWERED), because the answer decides whether the work is camouflage at all.
    // 2026-10-08 (slices 8 and 10 made one shape) — AND THE DOCTOR'S WRITTEN INSTRUCTIONS ARE NEVER A GAP: the client signs with
    // or without the photo, the API writes on the record which it was, and the ARTIST's signature waits until the shop adds the
    // photo from its dashboard (the API's prescription.py). Nothing here may stop a client who filled everything in (Chris,
    // 2026-09-10), the numbing prescription's correction the same night.
    if (askScalpScars && scalpScarsAnswer === null) {
      refuse("scalp_scars_unanswered", T.needScalpScarsAnswer);
      return;
    }
    if (signatureRef.current.isEmpty()) {
      refuse("no_signature", T.needSignature);
      return;
    }
    // TW-0128 — the guardian's own three. Checked here rather than with the browser's `required`,
    // which this form has never trusted on a phone, and checked LAST among the signature gates so
    // the client's own missing signature is still the first thing they are told about.
    if (askCoverup && form.coveringExisting !== "yes") {
      // an unanswered question and a "no" both stop here; the screen says which
      refuse("coverup_unanswered", coverupRefusalMessage || T.minorCoverupAsk);
      return;
    }
    if (guardianRoute) {
      if (!form.guardianName.trim()) {
        refuse("no_guardian_name", T.needGuardianName);
        return;
      }
      if (!form.guardianRelationship.trim()) {
        refuse("no_guardian_relationship", T.needGuardianRelationship);
        return;
      }
      // TW-0526, the client-form side (2026-10-29) — the parent's phone, under their relationship: required where the shop's state's
      // rule names it (Utah's R392-701-16(2)(c)(iv)), the browser's own stop first. The API refuses the same body
      // (STATE_FIELD_UNANSWERED), and a number it cannot read wherever one was typed (GUARDIAN_PHONE_NOT_VALID)
      const guardianBoxMissing = guardianFields.find((box) => box.required && !fieldAnswered(box, fieldValue(box.key)));
      if (guardianBoxMissing) {
        refuse("guardian_field_unanswered", lang === "es" ? guardianBoxMissing.missingEs : guardianBoxMissing.missingEn, guardianBoxMissing.key);
        return;
      }
      // TW-0267 — the state's own statement, where its rule names one: in page order, between the relationship and the
      // parent's signature. The API refuses the same body (GUARDIAN_CERTIFICATION_NOT_ACCEPTED).
      if (certification && !guardianCertified) {
        refuse("no_guardian_certification", T.needGuardianCertification(stateNameOf(client?.state ?? undefined)));
        return;
      }
      // TW-0526, the client-form side — …and every statement after it, in the rule's order, each its own box: the refusal names the
      // statement's id, which takes the parent to that box (refusal.ts BLOCK_BY_DETAIL). The API refuses the same body
      const unticked = furtherStatements.find((statement) => statementTicks[statement.id] !== true);
      if (unticked) {
        refuse("no_guardian_certification", T.needGuardianCertification(stateNameOf(client?.state ?? undefined)), unticked.id);
        return;
      }
      if (guardianSignatureRef.current.isEmpty()) {
        refuse("no_guardian_signature", T.needGuardianSignature);
        return;
      }
      // TW-0227 — the other parent's written permission, where the city's or county's rule wants it: one of the ways
      // offered, and what that way needs. After the parent's own three, which are the first thing a parent is told.
      // The API refuses the same body (app.py second_parent_answered), and a body carrying only a photo where both
      // have to be here.
      if (askSecondParent) {
        if (!secondParentChoice) {
          refuse("no_second_parent_choice", T.needSecondParentChoice);
          return;
        }
        if (secondParentChoice === "signed" && !form.secondParentName.trim()) {
          refuse("no_second_parent_name", T.needSecondParentName);
          return;
        }
        if (secondParentChoice === "signed" && (!secondParentSignatureRef.current || secondParentSignatureRef.current.isEmpty())) {
          refuse("no_second_parent_signature", T.needSecondParentSignature);
          return;
        }
        if (secondParentChoice === "photo" && !photoOfSecondParentPermission) {
          refuse("no_second_parent_photo", T.needSecondParentPhoto);
          return;
        }
      }
    }
    // A minor this state, this shop or this placement does not allow is refused at the submit too,
    // not only under the field: the sentence under the picker can be scrolled past, and the API
    // refuses the same signing anyway (state_forms.minor_piercing_refusal).
    if (minorRefusalMessage || placementRefusalMessage) {
      refuse("minor_or_placement_refused", minorRefusalMessage || placementRefusalMessage);
      return;
    }
    // TW-0279 — A COSMETIC TATTOO TAKES NO MINOR, IN ANY STATE (docs/COSMETIC.md "Adults only"): refused here on the age
    // read off the birth date (TW-0503), which is the number the API reads (app.client_age, birth_date.py), in the words the API
    // refuses it with (app.COSMETIC_MINOR_REFUSAL, the cosmetic copy's ageError), never a tattoo's sentence and never a piercing's law.
    if (isCosmetic && isMinor) {
      refuse("minor_or_placement_refused", copy.ageError);
      return;
    }
    // THE TWO LOT PHOTOS, WHERE THE SHOP REQUIRES THEM (2026-10-31, lotPhotos.ts). The batch-number photos used to return
    // here for EVERY shop that asked for them, so a client who had filled in every field and signed could not submit without
    // photographing a needle wrapper they did not have. Chris, 2026-09-10: "We should never have it so if someone signs and
    // fills out everything they can't finish. Please make sure this doesn't exist and never exists." and "please no
    // destructive friction." Both were offered-and-optional from that day. Then White Lotus Body Arts (Ventura), 2026-10-30:
    // "we keep getting clients submitting the form before that is put in. And in our area, having that in is super
    // important." So a photo is required only where the shop's own row says so (both required at the press that turns the
    // ask on, each untickable after; a shop whose ask was on before keeps its photos optional until its owner ticks a box),
    // the refusal names which one and takes the client to its spot, and the API refuses the same body. Every shop that never
    // asked is exactly as it was. Nothing else below the signature may refuse a signing.
    const lotPhotoMissing = missingLotPhoto(requiredLotPhotos, { needle: photoOfNeedleBatchNumber, ink: photoOfInkBatchNumber });
    if (lotPhotoMissing === "needle") {
      refuse("no_needle_photo", T.needNeedlePhoto, "photoOfNeedleBatchNumber");
      return;
    }
    if (lotPhotoMissing === "ink") {
      refuse("no_ink_photo", T.needInkPhoto, "photoOfInkBatchNumber");
      return;
    }
    // Get the signature image:
    const trimmedSignatureJpeg = signatureRef.current
      .getTrimmedCanvas()
      .toDataURL("image/jpeg");
    const trimmedSignaturePng = signatureRef.current
      .getTrimmedCanvas()
      .toDataURL("image/png");
    setSubmissionError("");
    setSubmitting(true);
    // TW-0059 + the artist picker: the form as this service posts it, and — ONLY for a shop that
    // sent a list of artists — the name that was picked, with the artist's id beside it. A shop
    // with no list posts an object byte-for-byte identical to the one it posted yesterday: no
    // `artistId` key, no rewritten name. artists.ts says why the name and not the id is the field.
    // TW-0082 — and the language the client read it in, ONLY when it was not English: the API
    // prints the Spanish consent sentences on the PDF when this key says so, and an English
    // signing's body carries no key at all (withLanguage hands back the same object).
    // TW-0279 — and the shop's cosmetic key, its state and what its picker offered, which a cosmetic signing's body follows
    // TW-0503 — AND THE AGE THE PAGE READ OFF THE BIRTH DATE, where the typed one rode (after the name, so the body is the one
    // this form has always posted, key for key): the signing door from before that day refused a body without an age
    // (FORM_FIELD_MISSING), so the page and the door can go out in either order. The door since reads its own age off the
    // posted birth date the same way and never this one (birth_date.py), and the two are the same number by construction.
    const { firstName, lastName, ...afterTheName } = form;
    const withTheAgeRead = { firstName, lastName, age: age === null ? "" : `${age}`, ...afterTheName };
    const posted = withLanguage(
      withArtistPicked(
        submissionForm(withTheAgeRead, type, service, form.locationOnBody, {
          cosmetic: client.public_data?.cosmetic,
          state: client.state ?? null,
          offered: offeredValues,
          // TW-0279 slice 9 — the shop's own switches, which can take a state's question off the page (and so off the body)
          publicData: client.public_data,
          // 2026-10-08 — a shop in HIPAA mode posts no portfolio clause (hipaaForm.ts)
          ...(client.hipaa === true ? { hipaa: true } : {}),
        }),
        artists,
        artistChoice
      ),
      lang
    );
    // TW-0152 — whitespace around an address is never meaningful and a domain is case-insensitive
    // by spec, so both are fixed here without asking. Two client rows are stored today as
    // ` icloud.com` and ` yahoo.com`: a leading space that survived into the table and makes every
    // send to that client bounce. This is the ONLY thing done to an address unasked.
    posted.email = tidyEmail(posted.email);
    // TW-0422 — a page that did not ask who did the work names nobody: the key stays (the API's signing door reads it) and says
    // "", and no id rides beside it, whatever a picker on another service's page held before the client switched
    if (!askArtist) {
      posted.nameOfArtist = "";
      delete posted.artistId;
    }
    // TW-0128 — the guardian's fields are posted ONLY when a minor signed with a parent present.
    // `submissionForm` spreads the whole form, so without this every adult's body would grow two
    // empty keys and stop being the one this form has posted since 2023.
    if (!askCoverup) delete posted.coveringExisting;
    // TW-0153 — a question that was never asked is not posted, so the record carries only what the
    // client actually answered and every other waiver's body is the one it has been since 2023
    if (!askEmancipated) delete posted.emancipatedMinor;
    // TW-0225 — the ear answer rides ONLY where the question was asked, as a boolean (the gate above made sure there is
    // one); the API reads it against the shop's own state and the spot, never a flag that says "exempt"
    if (askEar) posted.earPiercing = earAnswer === true;
    else delete posted.earPiercing;
    // TW-0279 slice 10 — the scalp answer rides ONLY where it was asked, as a boolean (the gate above made sure there is
    // one); submissionForm took the form's string out of every body (service.ts COSMETIC_ONLY_FIELDS)
    if (askScalpScars) posted.scalpCoversScars = scalpScarsAnswer === true;
    if (!guardianRoute) {
      delete posted.guardianName;
      delete posted.guardianRelationship;
    }
    // TW-0267 — the parent's tick on the state's own statement, and which statement it was, ONLY where the box was shown.
    // The tick is posted as it stands (the gate above made sure it is ticked, and the API refuses anything but a literal
    // true). Everywhere else the body is the one it has been: no key at all.
    if (certification) {
      posted.acknowledgmentAndWaiver = { ...posted.acknowledgmentAndWaiver, guardianCertification: guardianCertified === true };
      posted.guardianCertificationId = certification.id;
    }
    // TW-0526, the client-form side (2026-10-29) — every statement after the first the page showed, a tick each by its id, ONLY
    // where shown (the gate above made sure each is ticked; the API refuses anything but a literal true). A signing with one
    // statement, or none, posts the body it always posted: no key at all
    const postedBody = posted as unknown as Record<string, unknown>;
    if (furtherStatements.length) {
      postedBody.guardianStatements = Object.fromEntries(furtherStatements.map((statement) => [statement.id, statementTicks[statement.id] === true]));
    }
    // …and a box a state's rule names rides ONLY where the page drew it, as the client gave it, its ends trimmed: every other key
    // of the kind leaves the body, so every other waiver is the body it has been
    for (const key of STATE_FIELD_FORM_KEYS) delete postedBody[key];
    for (const box of [...youFields, ...healthFields, ...guardianFields]) postedBody[box.key] = fieldValue(box.key).trim();
    // TW-0227 — the other parent's written permission rides ONLY where it was asked: the way picked, the tick's answer as
    // a boolean (`soleParent`, true on "none"), and the other parent's name only when they signed here. Everywhere else
    // the body is the one it has been: `submissionForm` spreads the whole form, so without this every waiver would grow
    // two empty keys.
    if (askSecondParent) {
      posted.secondParentChoice = secondParentChoice;
      posted.soleParent = secondParentChoice === "none";
      if (secondParentChoice !== "signed") delete posted.secondParentName;
    } else {
      delete posted.secondParentChoice;
      delete posted.secondParentName;
    }
    // TW-0104 — a state question that was not asked is not posted, so the record (and the signed
    // waiver the API prints from it) carries only what the client actually answered.
    if (!askAllergies) {
      delete posted.questionnaire?.allergies;
      delete posted.allergiesDetails;
    }
    if (!askBleeding) {
      delete posted.questionnaire?.bleedingDisorders;
      delete posted.bleedingDisordersDetails;
    }
    if (!askEaten) delete posted.questionnaire?.eatenRecently;
    // TW-0293 — the two questions every form used to ask: a page that did not ask one posts no key for it, and no details
    // box, so the signed waiver prints nothing about a question nobody was asked
    if (!askInfluence) delete posted.questionnaire?.influenceOfDrugsOrAlcohol;
    if (!askCommunicable) {
      delete posted.questionnaire?.communicableDisease;
      delete posted.communicableDiseaseDetails;
    }
    // 2026-10-08 (TW-0279 slice 8) — and Michigan's numbing question: a page that did not ask it posts no key, so every
    // other state's body is the one it has been
    if (!askNumbing) delete posted.questionnaire?.numbingProduct;
    // TW-0270 slice 4 (2026-10-16) — and the five a state's rule can list: a page that did not ask one posts no key for it, so
    // every other state's body is the one it has been
    if (!askDiabetes) delete posted.questionnaire?.diabetesHistory;
    if (!askSeizures) delete posted.questionnaire?.seizuresOrFainting;
    if (!askHeart) delete posted.questionnaire?.heartOrBloodPressure;
    if (!askBloodThinners) delete posted.questionnaire?.bloodThinnersRecently;
    if (!askPregnantRecently) delete posted.questionnaire?.pregnantRecently;
    // TW-0526 (M04) — nor a worded question this page did not ask (a service changed after one was answered)
    for (const key of wordedQuestionKeys()) {
      if (!wordedQuestions.some((question) => question.key === key)) delete (posted.questionnaire as Record<string, unknown> | undefined)?.[key];
    }
    // TW-0526 (M05) — and the box a worded question's yes opens goes with its question
    for (const box of wordedQuestionDetailsKeys()) {
      if (!wordedQuestions.some((question) => question.details === box)) delete (posted as Record<string, unknown>)[box];
    }
    // TW-0154 — the same rule for the price: a shop that never turned it on posts the object it
    // posted yesterday, with no `price` key at all. `submissionForm` spreads the whole form, so
    // without this every waiver in the product would grow one empty key.
    if (!askPrice) delete posted.price;
    // 2026-09-23 — and for how the client is paying: a shop that did not ask posts no `paymentMethod` key at all, and a
    // shop that did posts exactly one of the three codes (the gate above makes sure there is one)
    if (!askPaymentMethod || !isPaymentMethod(posted.paymentMethod)) delete posted.paymentMethod;
    // …and for who to call: a shop that never asked, in a state that does not require it, posts the
    // body it posted yesterday.
    if (!askEmergency) {
      delete posted.emergencyContactName;
      delete posted.emergencyContactRelationship;
      delete posted.emergencyContactPhone;
      delete posted.emergencyContactAddress;
    }
    // …and for the client's physician (2026-09-25): a shop that did not ask on this service posts the body it
    // posted yesterday. A shop that asked posts all three, blanks included: the signed waiver says "(None given)".
    if (!askPhysician) {
      for (const key of PHYSICIAN_FIELDS) delete posted[key];
    }
    // TW-0170 — the state notices this client was shown, id -> true (a ticked one is true because the form
    // refuses it unticked; the grievance procedure is true because it was on the page). The key rides ONLY
    // where there were any, so every other waiver's body is the one it has always been. The API prints the
    // contract's own words for each id, in the language signed. TW-0526 — a ticked one posts its TICK, which the gate above
    // makes `true` on every body that gets here, so the signing door's own check of it (STATE_NOTICE_UNTICKED) reads what the
    // client did rather than a constant: the body is byte for byte what it was.
    if (notices.length) {
      Object.assign(posted, {
        stateNotices: Object.fromEntries(notices.map((n) => [n.id, n.acknowledge ? noticeTicks[n.id] === true : true])),
      });
    }
    // TW-0412 — the shop's own questions this page drew, id -> "yes" or "no" (the gate above refuses a blank). The key rides
    // ONLY where the shop has any, so every other waiver's body is the one it has always been; the API keeps each answer
    // beside the shop's words for that id, from the row, and prints both.
    if (ownQuestions.length) {
      Object.assign(posted, { ownQuestions: ownQuestionsPosted(ownQuestions, ownQuestionAnswers) });
    }
    // 2026-09-24 — the shop's own clauses this client ticked, id -> true (every one is true because the gate above
    // refuses the signing otherwise). The key rides ONLY where the shop has any, so every other waiver's body is
    // the one it has always been; the API prints the shop's own words for each id, from the row.
    if (ownClauses.length) {
      Object.assign(posted, { ownClauses: Object.fromEntries(ownClauses.map((clause) => [clause.id, true])) });
    }
    // 2026-10-02 — the card-fee box this client ticked, id -> true (the gate above refuses the signing otherwise). The
    // key rides ONLY where the shop has it on; the API prints the row's sentence for that id, in the language signed.
    if (cashPreferred) {
      Object.assign(posted, { cashPreferred: { [cashPreferred.id]: true } });
    }
    // 2026-10-08 — a shop in HIPAA mode: the notice the client said they received, by the version they were shown, and their
    // answer to the photo release, signed or not, with the period they read. Each key rides ONLY where the shop has it.
    if (privacyNotice) {
      Object.assign(posted, { privacyNotice: { received: true, version: privacyNotice.version } });
    }
    if (photoRelease) {
      Object.assign(posted, { photoRelease: { signed: photoReleaseSigned, years: photoRelease.years } });
    }
    // TW-0171 — the signed waiver says so when an ID photo was a SAVED one, chosen from the phone's
    // library: the key rides ONLY then, so a camera photo's body is the one it has always been.
    Object.assign(
      posted,
      photoSourceFields({
        idPhoto: photoOfDriversLicense,
        idSource: idPhotoSource,
        minorAsked: askMinorId,
        minorPhoto: photoOfMinorId,
        minorSource: minorIdPhotoSource,
        proofAsked: askGuardianProof,
        proofPhoto: photoOfGuardianProof,
        proofSource: guardianProofSource,
        permissionAsked: askSecondParent && secondParentChoice === "photo",
        permissionPhoto: photoOfSecondParentPermission,
        permissionSource: secondParentPermissionSource,
        prescriptionAsked: askNumbing && form.questionnaire.numbingProduct === "yes",
        prescriptionPhoto: photoOfNumbingPrescription,
        prescriptionSource: numbingPrescriptionSource,
        providerAsked: wantProviderInstructions,
        providerPhoto: photoOfProviderInstructions,
        providerSource: providerInstructionsSource,
      })
    );
    // TW-0488 — which ID the photo is of, when the client picked one or the rule required one, and its number where the page asked:
    // the keys ride ONLY then, so every other body is the one it has always been
    delete posted.idNumber;
    Object.assign(posted, idFieldsPosted({ type: idType, number: form.idNumber, typeRequired: mustPickIdType, numberAsked: askIdNumber }));
    // TW-0270 slice 5 — and a paper's photo chosen from the phone's saved photos, by the paper's id: the key rides ONLY then
    const papersFromLibrary = papers.filter((paper) => paperPhotos[paper.id] && paperSources[paper.id] === "library");
    if (papersFromLibrary.length) {
      Object.assign(posted, { minorPaperSources: Object.fromEntries(papersFromLibrary.map((paper) => [paper.id, "library"])) });
    }
    // 2026-10-08 — A SHOP IN HIPAA MODE WHOSE STATE'S RULE HANDS THE CLIENT A COPY (clientCopy.ts): the copy the done screen
    // shows, built here from the body just posted and the page's own words for what it says was ticked, given and answered,
    // in the language signed. It is drawn only once the waiver is saved. Nothing is built for any other shop.
    const copyOfWhatWasSigned = showsClientCopy(client)
      ? clientCopyOf(
          {
            lang,
            shop: client.name,
            title: copy.title,
            clauses: acksShown.map((key) => ({ key, text: ackLabel(key, service, client.name, lang) })),
            notices: notices.map((notice) => {
              const line = noticeText(notice, lang, client.public_data);
              if (notice.acknowledge || !notice.sections_en) return { id: notice.id, acknowledge: notice.acknowledge, text: line };
              // a sheet, named the way NoticeSheet names it: its line, the client's own site after it, then its sections
              const site = sheetSite(notice);
              return {
                id: notice.id,
                acknowledge: false,
                text: site ? `${line} (${site})` : line,
                sheet: {
                  intro: (lang === "es" ? notice.intro_es : notice.intro_en) ?? "",
                  // the client's own procedures' sections, their placement's (TW-0526) and every client's (stateForms.ts sectionsFor)
                  sections: sectionsFor(notice, lang, procedures, form.locationOnBody),
                },
              };
            }),
            ownClauses: ownClauses.map((clause) => ({ id: clause.id, text: clause.text })),
            // TW-0412 — the shop's own questions the page drew, in its words, each with the line it shows under the answer
            ownQuestions: ownQuestions.map((question) => ({ id: question.id, text: question.text, when: question.when, note: question.note })),
            cashPreferred: cashPreferred ? { id: cashPreferred.id, text: cashPreferredSentence(cashPreferred, lang) } : null,
            privacyNoticeAck: privacyNotice ? T.privacyNoticeAck : null,
            photoRelease: photoRelease ? releaseWords(T, client.name, photoRelease.years) : null,
            terms: `${T.acceptTerms}${T.termsLink}${T.acceptTermsEnd}`,
            optional: [
              { key: "hearFromShop", text: T.hearFromShop(client.name) },
              { key: "shopMarketing", text: T.shopMarketing(client.name) },
            ],
            certification: certification ? certificationWords : null,
            // TW-0526, the client-form side — and every statement after the first, as the page showed it
            statements: furtherStatements.map((statement) => ({
              id: statement.id,
              text: statementWords(statement),
              lang: statement.exact === false || statement.translated === true ? lang : "en",
            })),
            health: healthQuestionsAsked({
              allergies: askAllergies,
              bleeding: askBleeding,
              eaten: askEaten,
              numbing: askNumbing,
              diabetes: askDiabetes,
              seizures: askSeizures,
              heart: askHeart,
              bloodThinners: askBloodThinners,
              pregnantRecently: askPregnantRecently,
              worded: wordedQuestions.map((question) => question.key),
              influence: askInfluence,
              communicable: askCommunicable,
              service,
              location: form.locationOnBody,
              procedures,
              state: client.state,
              // TW-0279 slice 9 — the shop's own switches, which can take a state's question off the page: the record of
              // what the client saw lists what the page asked
              publicData: client.public_data,
            }).map((key) => ({ key, question: healthQuestionLabel(key), details: DETAILS_BOX[key] })),
            notes: [
              { key: "medicalHistoryDetails", label: T.medicalHistory },
              ...(askMedications ? [{ key: "medicationAndBloodThinners", label: medicationsLabel }] : []),
            ],
          },
          posted
        )
      : null;
    // Post everything:
    // TW-0131 — the client pressed the button and we are posting. Fired HERE and not at the top of
    // handleSubmit, so it counts attempts that got past the form's own gates rather than every tap
    // on a disabled-looking button; the gates above return early and are their own story.
    // TW-0501 — and HELD, to leave in the same request as the door's answer (beacon.ts hold has the measurement: 61 of 608
    // signings on an iPhone carried no press of their own), or alone if no answer comes.
    const press = hold("form_submit", { shop: client.subdomain, lang, service });
    // TW-0501 — and the draft in the tab is set aside while the post is out (pauseDraft has why)
    pauseDraft();
    /** the door answered (any status): a catch below with none is a post that never came back */
    let answered = false;

    fetch(`${API_URL}/submit-waiver`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        subdomain: client.subdomain,
        form: posted,
        // TW-0064 — `internal: true` ONLY when this browser is marked as ours (`internal.ts`), or since
        // TW-0352 driven by a script, which posts `scripted: true` beside it, so a client's body is
        // byte-for-byte the one it has been since 2023. The API stamps `internal` on the row and the
        // stream consumer counts the waiver on `owner_waiver_count`, which is what keeps it out of the
        // shop's CLIENT count (the count the trial pauses at, too). The shop is still emailed and the
        // PDF is still saved.
        ...internalFields(),
        signaturePng: trimmedSignaturePng,
        signatureJpeg: trimmedSignatureJpeg,
        photoOfDriversLicense,
        // TW-0153 — the minor's own, beside the parent's, and ONLY where the state asks for both:
        // every other waiver's body is byte for byte the one it has been since 2023
        ...(askMinorId && photoOfMinorId ? { photoOfMinorId } : {}),
        // 2026-09-24 — the birth certificate or guardianship papers, and ONLY where the form asked for them
        ...(askGuardianProof && photoOfGuardianProof ? { photoOfGuardianProof } : {}),
        // TW-0270 slice 5 — a photo of each paper the state's rule keeps with a minor's record, by the paper's id, and ONLY where
        // the form asked for them: every other waiver's body is the one it has been
        ...(papers.length
          ? { minorPapers: Object.fromEntries(papers.filter((paper) => paperPhotos[paper.id]).map((paper) => [paper.id, paperPhotos[paper.id]])) }
          : {}),
        // 2026-10-08 (TW-0279 slice 8) — the doctor's prescription for a numbing product, ONLY under a yes to the state's
        // numbing question: every other waiver's body is the one it has been
        ...(askNumbing && form.questionnaire.numbingProduct === "yes" && photoOfNumbingPrescription
          ? { photoOfNumbingPrescription }
          : {}),
        // TW-0279 slice 10 — the client's doctor's written instructions, and ONLY where this signing wanted them
        ...(wantProviderInstructions && photoOfProviderInstructions ? { photoOfProviderInstructions } : {}),
        // TW-0128 — the parent's own signature, a top-level image beside the client's, and ONLY
        // when a minor signed with them present. An adult's body carries no such key.
        ...(guardianRoute
          ? {
              guardianSignaturePng: guardianSignatureRef.current
                .getTrimmedCanvas()
                .toDataURL("image/png"),
            }
          : {}),
        // TW-0227 — the other parent's signature, or the photo of the permission they signed, for the way picked and
        // ONLY where it was asked: every other waiver's body is the one it has been
        ...(askSecondParent && secondParentChoice === "signed" && secondParentSignatureRef.current
          ? {
              secondParentSignaturePng: secondParentSignatureRef.current
                .getTrimmedCanvas()
                .toDataURL("image/png"),
            }
          : {}),
        ...(askSecondParent && secondParentChoice === "photo" && photoOfSecondParentPermission
          ? { photoOfSecondParentPermission }
          : {}),
        ...(wantLotPhotos ? {
          photoOfNeedleBatchNumber,
          photoOfInkBatchNumber
        } : {}),
      }),
    })
      .then((res) => {
        answered = true;
        if (res.status === 200) {
          // Success (TW-0501: with the press it answers, in one request)
          press.withAnswer("form_done", { shop: client.subdomain, lang, service });
          // TW-0500 — signed: the draft in the tab goes, so "Submit another waiver" opens the next client's form empty
          endDraft();
          if (copyOfWhatWasSigned) setClientCopy(copyOfWhatWasSigned);
          setSubmittedSuccessfully(true);
          res
            .json()
            .then((data) => {
              setDownloadLink(data?.data?.download_link);
              setSubmitting(false);
            })
            .catch((err) => {
              // The same handled-rejection swallow as WaiverDetail's, on the path that matters
              // most: the waiver IS saved (a 200 came back, form_done already fired and the
              // success screen is up), but without `setSubmitting(false)` the button sits in its
              // submitting state and no download link ever arrives. So: report it, and release
              // the button. Deliberately NOT an error message — nothing failed for the client,
              // and telling them it did about a waiver that saved would be the worse lie.
              trackError((err as Error)?.message || "submit: the body did not parse", {
                source: "Form.parseSubmitResponse",
                stack: (err as Error)?.stack,
              });
              setSubmitting(false);
            });
        } else {
          // The status, never the message: an error string can quote what the client typed. And since 2026-10-26 the API's
          // own CODE and the box it names (`field`, or `details`, `question`, `paper` where a refusal names its place under
          // those), read off its answer, with the page's language and the shop's state: the read of refusals at the signing
          // door (`./run.sh refusals`). Until this day a refusal at the door reached the beacon as a bare 400, and which one
          // lived only in the API's log, kept seven days: "Phone number was not valid" was found there, 20 of 39 in a week.
          let toldTheBeacon = false;   // once per answer: the .catch below can follow a .then that threw
          // TW-0501 — the door answered and did not sign: the draft in the tab is the page again, written at once
          resumeDraft(true);
          const doorRefused = (data?: { error?: Record<string, unknown> }) => {
            if (toldTheBeacon) return;
            toldTheBeacon = true;
            const said = data && typeof data.error === "object" && data.error ? data.error : undefined;
            const named = said ? [said.field, said.details, said.question, said.paper].find((v) => typeof v === "string" && v) : undefined;
            // TW-0501 — with the press it answers, in one request (beacon.ts hold)
            press.withAnswer("form_error", {
              shop: client.subdomain,
              lang,
              formLang: lang,
              service,
              status: res.status,
              code: typeof said?.code === "string" ? said.code : undefined,
              field: typeof named === "string" ? named : undefined,
              step: "door",
              state: client.state ?? undefined,
            });
          };
          // Error. Try to get the JSON from this error.
          res
            .json()
            .then((data) => {
              doorRefused(data);
              // the status the door answered with, never a fixed 501 (every error with no sentence of its own said "(501)")
              let errorMessage = T.submitError(res.status);
              if (data?.error && typeof data?.error === "string") {
                errorMessage = data.error;
              } else if (data?.error?.message && typeof data?.error?.message === "string") {
                errorMessage = data.error.message;
              }
              setSubmissionError(errorMessage);
              setSubmitting(false);
              // 2026-10-01 — the API found a health question the page asked left blank (HEALTH_QUESTION_UNANSWERED), and
              // names it by its input's own name: the page is taken to it, as the form's own refusal of it does
              const question = data?.error?.code === "HEALTH_QUESTION_UNANSWERED" ? data?.error?.question : "";
              if (typeof question === "string" && question) {
                setRefusedFor("health_unanswered");
                setRefusedDetail(question);
                goToRefusal("health_unanswered", question);
              }
              // …and a yes whose box it found empty (HEALTH_DETAILS_UNANSWERED), named by the box's own input name
              const box = data?.error?.code === "HEALTH_DETAILS_UNANSWERED" ? data?.error?.details : "";
              if (typeof box === "string" && box) {
                setRefusedFor("health_details");
                setRefusedDetail(box);
                goToRefusal("health_details", box);
              }
              // TW-0412 — …and one of the shop's own questions it found left blank (OWN_QUESTION_UNANSWERED), named by its id
              const ownQuestion = data?.error?.code === "OWN_QUESTION_UNANSWERED" ? data?.error?.question : "";
              if (typeof ownQuestion === "string" && ownQuestion) {
                setRefusedFor("own_question_unanswered");
                setRefusedDetail(ownQuestion);
                goToRefusal("own_question_unanswered", ownQuestion);
              }
              // TW-0526 — …and a state notice the client ticks that it found unticked (STATE_NOTICE_UNTICKED: the aftercare
              // received, a blood-donor or cautionary notice), named by the notice's id: to its box, as the form's own refusal goes
              const notice = data?.error?.code === "STATE_NOTICE_UNTICKED" ? data?.error?.field : "";
              if (typeof notice === "string" && notice) {
                setRefusedFor("notice_unticked");
                setRefusedDetail(notice);
                goToRefusal("notice_unticked", notice);
              }
              // 2026-10-08 — …and the practice's notice the client did not say they received (NPP_NOT_ACKNOWLEDGED): to its box
              if (data?.error?.code === "NPP_NOT_ACKNOWLEDGED") {
                setRefusedFor("privacy_notice_unticked");
                goToRefusal("privacy_notice_unticked");
              }
              // TW-0270 slice 5 — …and a paper the state's rule keeps with a minor's record that the API found missing
              // (MINOR_PAPER_MISSING), named by the paper's id: to that paper's block
              const paper = data?.error?.code === "MINOR_PAPER_MISSING" ? data?.error?.paper : "";
              if (typeof paper === "string" && paper) {
                setRefusedFor("no_paper_photo");
                setRefusedDetail(paper);
                goToRefusal("no_paper_photo", paper);
              }
              // TW-0279 slice 10 — …and the scalp question the API found unanswered: to its place
              if (data?.error?.code === "SCALP_SCARS_UNANSWERED") {
                setRefusedFor("scalp_scars_unanswered");
                goToRefusal("scalp_scars_unanswered");
              }
              // TW-0275 — …and a yes to a question this page never signs a yes to (HEALTH_ANSWER_REFUSED: being under the
              // influence, being pregnant or nursing), which the submit above refuses first; to the question, as its own refusal goes
              const refusedYes = data?.error?.code === "HEALTH_ANSWER_REFUSED" ? data?.error?.question : "";
              if (typeof refusedYes === "string" && refusedYes) {
                setRefusedFor("health_question");
                setRefusedDetail(refusedYes);
                goToRefusal("health_question", refusedYes);
              }
              // TW-0488 — …and the type or the number of the ID the API found missing where the state's rule keeps it, and a phone
              // number it could not read (PHONE_NOT_VALID): the phone box is near the top of the page and this answer comes after
              // the signature at the bottom, so it is the one that most needs to take the person to the box
              const idOrPhone: Record<string, string> = {
                ID_TYPE_UNANSWERED: "no_id_type",
                ID_NUMBER_UNANSWERED: "no_id_number",
                PHONE_NOT_VALID: "phone_not_valid",
              };
              const idOrPhoneReason = typeof data?.error?.code === "string" ? idOrPhone[data.error.code] : undefined;
              if (idOrPhoneReason) {
                setRefusedFor(idOrPhoneReason);
                setRefusedDetail("");
                goToRefusal(idOrPhoneReason);
              }
              // TW-0526, the client-form side (2026-10-29) — …and a box a state's rule requires that the API found empty
              // (STATE_FIELD_UNANSWERED), named by the box's key: to the box, under the refusal of its own part of the page; and the
              // parent's phone it could not read (GUARDIAN_PHONE_NOT_VALID), to the parent's box
              const stateBox = data?.error?.code === "STATE_FIELD_UNANSWERED" ? data?.error?.field : "";
              if (typeof stateBox === "string" && stateBox) {
                const place = [...youFields, ...healthFields, ...guardianFields].find((box) => box.key === stateBox)?.place;
                const fieldReason = place === "you" ? "you_field_unanswered" : place === "guardian" ? "guardian_field_unanswered" : "health_field_unanswered";
                setRefusedFor(fieldReason);
                setRefusedDetail(stateBox);
                goToRefusal(fieldReason, stateBox);
              }
              if (data?.error?.code === "GUARDIAN_PHONE_NOT_VALID") {
                setRefusedFor("guardian_phone_not_valid");
                setRefusedDetail("guardianPhone");
                goToRefusal("guardian_phone_not_valid");
              }
              // TW-0275 — …and a box the API found longer than it takes (FORM_FIELD_TOO_LONG), named by the box's own input
              // name: every box stops typing at that length, so only a value the page was handed can reach this
              const longBox = data?.error?.code === "FORM_FIELD_TOO_LONG" ? data?.error?.field : "";
              if (typeof longBox === "string" && longBox) {
                setRefusedFor("field_too_long");
                setRefusedDetail(longBox);
                goToRefusal("field_too_long", longBox);
              }
              // TW-0501 — …and every other door code a page can meet, to its box (refusal.ts DOOR_REASONS): a minor's age, the
              // parent's block, the minor's own ID photo, the papers that say who the parent is, the spot, the terms box, the pad
              const doorReason = typeof data?.error?.code === "string" ? doorReasonOf(data.error.code) : undefined;
              if (doorReason) {
                setRefusedFor(doorReason);
                setRefusedDetail("");
                const went = refusalTarget(doorReason);
                setDoorPlace(went?.getAttribute("name") || "");
                goToRefusal(doorReason);
              }
              // TW-0526, the client-form side — a statement after the first that the API found unticked names its id
              // (GUARDIAN_CERTIFICATION_MISSING with `field`): to that statement's box
              const statementBox = data?.error?.code === "GUARDIAN_CERTIFICATION_MISSING" ? data?.error?.field : "";
              if (typeof statementBox === "string" && statementBox) {
                setRefusedFor("no_guardian_certification");
                setRefusedDetail(statementBox);
                goToRefusal("no_guardian_certification", statementBox);
              }
            })
            .catch((err) => {
              // a body that did not parse (a 5xx's page, a gateway's): the status alone, as before
              doorRefused();
              console.warn("err", err);
              setSubmissionError(T.submitError(res.status));
              setSubmitting(false);
            });
        }
      })
      .catch((err) => {
        // the post never reached us (a dropped line, a phone off the signal): nothing was saved and nothing on the page is lost,
        // so the client is told exactly that in their own language, never the browser's English ("Load failed")
        console.warn("err", err);
        if (!answered) {
          // TW-0501 — AND IT LEAVES A ROW (the form audit § 6: 54 visits pressed Submit and never signed, and for about two in
          // three of them no answer was recorded at all): `form_error` with status 0, the press with it, and no code, because
          // the door said nothing (scripts/lib_refusals.py reads status 0 as POST_NOT_ANSWERED). The draft in the tab waits for
          // the client's next keystroke: the door may have signed it, and the answer been lost on the way back.
          press.withAnswer("form_error", {
            shop: client.subdomain,
            lang,
            formLang: lang,
            service,
            status: 0,
            step: "door",
            state: client.state ?? undefined,
          });
          resumeDraft(false);
        }
        setSubmissionError(T.submitOffline);
        setSubmitting(false);
      });
  };

  const onSignatureStart = () => {
    setSignatureIsEmpty(false);
    if (submissionError === T.needSignature) {
      setSubmissionError("");
    }
  };

  const onGuardianSignatureStart = () => {
    setGuardianSignatureIsEmpty(false);
    if (submissionError === T.needGuardianSignature) {
      setSubmissionError("");
    }
  };

  const onGuardianSignatureEnd = () => {
    setGuardianSignatureIsEmpty(guardianSignatureRef.current.isEmpty());
  };

  // TW-0227 — the other parent's pad, the same two
  const onSecondParentSignatureStart = () => {
    setSecondParentSignatureIsEmpty(false);
    if (submissionError === T.needSecondParentSignature) {
      setSubmissionError("");
    }
  };

  const onSecondParentSignatureEnd = () => {
    setSecondParentSignatureIsEmpty(Boolean(secondParentSignatureRef.current?.isEmpty()));
  };

  const onSignatureEnd = () => {
    setSignatureIsEmpty(signatureRef.current.isEmpty());
    return;
    // For troubleshooting the signature:
    /*
    const trimmedSignature = signatureRef.current
      .getTrimmedCanvas()
      .toDataURL("image/jpeg");
    console.log("trimmedSignature: ", trimmedSignature);
    // Add an img to the page to see the signature:
    let existingSignature = document.getElementById(
      "signature"
    ) as HTMLImageElement;
    if (!existingSignature) {
      existingSignature = document.createElement("img");
      existingSignature.id = "signature";
      existingSignature.style.width = "100%";
      existingSignature.style.height = "100%";
      document.body.appendChild(existingSignature);
    }
    existingSignature.src = trimmedSignature;
    */
  };

  // ── THE PIECES' DOORS AND PROPS (TW-0504; FormItems.tsx) ─────────────────────────────────────────────────────────────
  //
  // The question, box, clause and sheet pieces are memoized on their own props, so each holds one steady function per door,
  // and a call runs the handler this page wrote on its last render: word for word what each piece ran when it was made anew
  // on every render, so an answer, a tick and the sentence a tick takes away do exactly what they did.
  const answerQuestion = useStableHandler(handleRadioChange);
  const tickClause = useStableHandler(handleAcknowledgmentAndWaiverChange);
  const tickNotice = useStableHandler((id: string, checked: boolean) => setNoticeTicks({ ...noticeTicks, [id]: checked }));
  // TW-0526, the client-form side — a box a state's rule names, typed or picked: the page's own change, held steady for the piece
  const changeField = useStableHandler((e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => handleInputChange(e));
  /** One box's piece (FormItems.tsx StateFieldBox): its answer, TW-0541's mark while the browser's stop named it and it is still
   *  unanswered, and the page's or the door's sentence under it while that refusal is about this box. */
  const stateFieldPiece = (box: StateField) => {
    const value = fieldValue(box.key);
    const answered = fieldAnswered(box, value);
    const placeReason = box.place === "you" ? "you_field_unanswered" : box.place === "guardian" ? "guardian_field_unanswered" : "health_field_unanswered";
    const refusedHere =
      Boolean(submissionError) &&
      ((refusedFor === placeReason && refusedDetail === box.key && !answered) ||
        (refusedFor === "guardian_phone_not_valid" && box.key === "guardianPhone"));
    return {
      field: box,
      lang,
      value,
      chooseWord: T.chooseOne,
      optionalWord: T.optional,
      missed: nativeMissedHere(box.key, !answered),
      missedLine: nativeLine(box.input === "select" ? "pick" : "fill"),
      refusedLine: refusedHere ? submissionError : "",
      onChange: changeField,
    };
  };
  const tickOwnClause = useStableHandler((id: string, checked: boolean) => setOwnClauseTicks({ ...ownClauseTicks, [id]: checked }));
  const answerOwnQuestion = useStableHandler((id: string, value: OwnAnswer) =>
    setOwnQuestionAnswers({ ...ownQuestionAnswers, [id]: value })
  );
  const tickCashPreferred = useStableHandler((checked: boolean) => {
    setCashPreferredTicked(checked);
    if (checked && submissionError === T.needCashPreferred) setSubmissionError("");
  });
  const tickPrivacyNotice = useStableHandler((checked: boolean) => {
    setPrivacyNoticeTicked(checked);
    if (checked && submissionError === T.needPrivacyNotice) setSubmissionError("");
  });
  /** One health question's piece, beside its own words at the call: its answer, the refusal's sentence while it is the
   *  question left blank (2026-10-01: said under the question until a yes or a no is picked), the page's words, the phone,
   *  and its door. */
  const questionPiece = (name: keyof typeof form.questionnaire) => ({
    name,
    value: form.questionnaire[name],
    unanswered: refusedFor === "health_unanswered" && refusedDetail === name && !form.questionnaire[name] ? submissionError : "",
    T,
    isMobile,
    onAnswer: answerQuestion,
  });
  /** One of the shop's own questions' piece (TW-0412): the same, in the owner's words, and the owner's line under the answer
   *  it names. */
  const ownQuestionPiece = (question: OwnQuestion) => {
    const answer = ownQuestionAnswers[question.id];
    const answered = answer === "yes" || answer === "no";
    return {
      id: question.id,
      text: question.text,
      answer,
      note: ownQuestionNoteShown(question, answer),
      unanswered: refusedFor === "own_question_unanswered" && refusedDetail === question.id && !answered ? submissionError : "",
      T,
      isMobile,
      onAnswer: answerOwnQuestion,
    };
  };
  /** One of the waiver's own boxes' piece (a clause, the terms, the two optional boxes): its tick, whether a box refusal is
   *  up, the page's words, the phone, and its door. */
  const clausePiece = (name: keyof typeof form.acknowledgmentAndWaiver) => ({
    name,
    checked: form.acknowledgmentAndWaiver[name],
    boxRefusalUp,
    T,
    isMobile,
    onTick: tickClause,
  });
  // The terms box's words carry the link to the shop's consent page: an element, made again only when its words or its link
  // move, so the terms box is not drawn again on every keystroke for a label that is the same each time.
  // TW-0138 — ON THE DEMO, AND ONLY THE DEMO, THE DIALS RIDE THE TERMS URL.
  // This anchor is `target="_blank" rel="noreferrer"`, so `document.referrer`
  // is EMPTY on the page it opens: the consent page cannot recover the demo's
  // configuration from anywhere, and the only way to carry it is on the URL
  // itself. That is what makes the "Back to the waiver" link over there able to
  // return a reader to the demo they built rather than a fresh one.
  // ⚠ A REAL SHOP'S TERMS URL IS BYTE-FOR-BYTE WHAT IT HAS ALWAYS BEEN — the
  // gate is the shop, not the query string, and withDemoQuery returns the href
  // untouched when there is nothing to carry anyway. The A2P campaign filing
  // names the bare https://demo.waivers.tattoo/terms-and-conditions and that
  // address is unchanged: this adds a query string to a link, never a route.
  const termsLinkHref =
    client && isDemoShop(client.subdomain) ? withDemoQuery(termsHref(lang), window.location.search) : termsHref(lang);
  const termsLabel = useMemo(
    () => (
      <div>
        {T.acceptTerms}
        <a href={termsLinkHref} className="underline" target="_blank" rel="noreferrer">
          {T.termsLink}
        </a>
        {T.acceptTermsEnd}
      </div>
    ),
    [T, termsLinkHref]
  );

  if (!client) {
    return <></>
  }

  return (
    <div>
      {/* THE TOP ROW — the returning-client action at the LEFT (TW-0195) and the language switch at
          the RIGHT (TW-0082), each the other's mirror, in ONE row so a shop with both turned on
          pays no more height above its mark than a shop with one (Chris, 2026-09-21: "could be a
          top left action, if top right is localization"). Neither turned on and the row is not
          drawn at all — the page a client opens is byte for byte the page they have always opened. */}
      {(showLookupAction || enabled.length > 1) && (
        <div className="flex items-start justify-between gap-2 mb-3">
          {showLookupAction ? (
            <button
              type="button"
              onClick={toggleLookup}
              aria-expanded={lookupOpen}
              aria-controls="been-here-before"
              className={`inline-flex items-center px-2 py-0.5 text-xs font-semibold border-2 rounded-md ${
                lookupOpen ? "bg-black text-white border-black" : "bg-white text-gray-700 border-gray-300"
              }`}
            >
              {T.beenHereBefore}
            </button>
          ) : (
            /* nothing on the left, so `justify-between` still puts the switch on the right — which
               is exactly where it has been since TW-0082, at exactly the size it was */
            <span />
          )}
          {/* TW-0082 — ONLY when the shop has turned Spanish on in Settings. Two short buttons, the
              flag and the code — EN / ES — because the full names were "Way too wide on mobile"
              (Chris, 2026-09-08: "it can be en/es"); the language's own name is each button's
              accessible name. It changes this visit only. */}
          {enabled.length > 1 ? (
            <div className="flex" role="group" aria-label={T.switchLabel}>
              {enabled.map((option, index) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => {
                    langTouched.current = true;
                    setLang(option);
                  }}
                  aria-pressed={lang === option}
                  aria-label={T.languageName[option]}
                  title={T.languageName[option]}
                  className={`inline-flex items-center px-2 py-0.5 text-xs font-semibold border-2 ${
                    lang === option ? "bg-black text-white border-black" : "bg-white text-gray-700 border-gray-300"
                  } ${index === 0 ? "rounded-l-md" : "rounded-r-md -ml-0.5"}`}
                >
                  <img
                    src={`${process.env.PUBLIC_URL}/flags/${FLAG[option]}.svg`}
                    alt=""
                    width={16}
                    height={12}
                    className="me-1 rounded-sm"
                    style={{ width: 16, height: 12 }}
                  />
                  {option.toUpperCase()}
                </button>
              ))}
            </div>
          ) : (
            <span />
          )}
        </div>
      )}
      {client.public_data?.logo_url ? (
        <img
          src={client.public_data.logo_url}
          alt=""
          className="mx-auto mb-3"
          style={{ width: 200 }}
        />
      ) : (
        /* No logo on file (a shop minted by the site, until it uploads one, TW-0048): a lettermark of
           the shop's initials, so the page still opens on the shop and not on a bare title (Chris,
           2026-09-02). Ink tile, paper letters, the space the six's logos occupy. */
        <div
          aria-hidden
          className={`mx-auto mb-4 flex items-center justify-center rounded-2xl font-bold select-none ${client.placeholder ? "bg-gray-200" : "bg-black text-white"}`}
          style={{ width: 88, height: 88, fontSize: 36, letterSpacing: 1 }}
        >
          {/* while neither the config file nor the API has answered (App.tsx), a neutral tile: the
              shop's mark is on the way, and a wrong lettermark would be worse than none */}
          {client.placeholder ? "" : initials(client.name)}
        </div>
      )}
      <div className="text-center">
        <div className="px-4">
          <h1 className="text-3xl font-bold">
            {copy.title}
          </h1>
          <h2 className="text-2xl text-gray-500 mt-2">
            {client.name}
          </h2>
        </div>
      </div>
      {/* TW-0195 — the panel the action opens. An inline disclosure and never a modal: a dialog that
          traps focus on a phone at a counter is a waiver somebody cannot finish, and this one has to
          be abandonable at any moment. It sits UNDER the shop's mark so that opening it never pushes
          the mark off the screen, and OUTSIDE the <form> below so that nothing typed in it is ever
          posted — its two values reach the waiver only by being carried into the form's own phone
          and email boxes. Those boxes are the form's own type/autocomplete, so the phone offers the
          same keyboard and the same autofill card it offers below.
          ⚠ Open, there are genuinely TWO phone boxes and TWO email boxes on this page, which is why
          this is a labelled group: a test (or a screen reader) says WHICH by saying which group. */}
      {showLookupAction && lookupOpen && (
        <div
          id="been-here-before"
          role="group"
          aria-label={T.lookupLabel}
          className="mx-auto mt-4 max-w-xl border-2 border-gray-300 bg-gray-50 p-3"
        >
          <p className="mb-2 text-sm">{T.lookupIntro}</p>
          {/* TW-0195 — the demo, and only the demo. One extra line saying what the tap is about to
              do, because on demo.waivers.tattoo the two boxes above are already filled in and the
              client they find is invented. A real shop's panel is byte for byte what it was. */}
          {demoLookup && <p className="mb-2 text-sm text-gray-600">{T.lookupDemoHint}</p>}
          <div className="flex flex-col space-y-2 sm:flex-row sm:space-x-2 sm:space-y-0">
            <input
              className="w-full border-2 p-2"
              placeholder={T.phonePlaceholder}
              name="lookupPhone"
              maxLength={FIELD_LIMITS.phoneNumber}
              aria-label={T.phone}
              type="tel"
              autoComplete="tel"
              value={lookupPhone}
              onChange={(e) => setLookupPhone(e.target.value)}
            />
            <input
              className="w-full border-2 p-2"
              placeholder={T.emailPlaceholder}
              name="lookupEmail"
              maxLength={FIELD_LIMITS.email}
              aria-label={T.email}
              type="email"
              autoComplete="email"
              value={lookupEmail}
              onChange={(e) => setLookupEmail(e.target.value)}
            />
          </div>
          <div className="mt-2 flex items-center space-x-4">
            <button
              type="button"
              onClick={findMe}
              disabled={!lookupReady || lookupBusy}
              className="border-2 border-black bg-black px-4 py-2 font-semibold text-white disabled:border-gray-300 disabled:bg-gray-300 disabled:text-gray-600"
            >
              {lookupBusy ? T.lookupFinding : T.lookupFind}
            </button>
            <button
              type="button"
              onClick={() => setLookupOpen(false)}
              className="underline underline-offset-2"
            >
              {T.lookupClose}
            </button>
          </div>
        </div>
      )}
      {submittedSuccessfully ? (
        <>
          <SuccessScreen downloadLink={downloadLink} lang={lang} shop={client?.name ?? ""} clientCopy={clientCopy} />
          <div>
            <div
              className="text-center mb-8 underline cursor-pointer"
              onClick={() => {
                window.location.reload();
              }}
            >
              {T.submitAnother}
            </div>
          </div>
        </>
      ) : (
        <form
          ref={formRef}
          onSubmit={handleSubmit}
          // TW-0131 — the first touch of ANY field on this page: the moment a scan became an
          // attempt, which is the number that separates "nobody scanned the counter QR" from
          // "people scan it and give up". One capture handler on the form rather than a call on
          // every input, because a per-input hook is a list somebody will add a field to and
          // forget. It reads no value and receives none — `startedRef` is a boolean and the event
          // is never inspected — so the field a client is typing into cannot leak through it.
          // ⚠ THIS REF IS THE CHEAP HALF AND NOT THE INVARIANT (TW-0149). It stops the call on
          // every later keystroke of THIS mount; what stops a SECOND mount from reporting a second
          // attempt is the once-per-visit guard in `beacon.ts`, because the visit is the unit
          // `form_start` counts in and a ref only lives as long as the tree. Prod said 29 opens and
          // 463 starts on the demo shop with this ref in place and working.
          onChangeCapture={() => {
            // TW-0500 — and the moment of it, which is what a draft is stamped with (draft.ts: 30 minutes from the last one)
            noteTouch();
            if (startedRef.current) return;
            startedRef.current = true;
            track("form_start", { shop: client.subdomain, lang, service });
          }}
        >
          <div className="mx-auto p-2 sm:p-8">
            {/* TW-0505 — the shop's state's rules never came (its file and the fallback both failed, stateRules.ts): said at the
                top of the form, in the client's language, with a way to ask again, instead of a form that reads as whole and
                would be refused at the door. A Submit says the same and asks again. */}
            {rules?.status === "failed" && (
              <div
                className="mb-6 flex flex-wrap items-baseline gap-x-4 gap-y-1 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded"
                role="alert"
                data-refusal="state_rules_failed"
              >
                <span>{T.stateRulesFailed}</span>
                <button type="button" onClick={rules.retry} className="font-semibold underline underline-offset-2">
                  {T.stateRulesTryAgain}
                </button>
              </div>
            )}
            {/* TW-0500 — a draft put back after a reload (draft.ts): one line in the client's language, first on the form, and
                the way to an empty form beside it. A status, as the welcome-back line is, so a screen reader says it. */}
            {draftRestored && (
              <div
                className="mb-6 flex flex-wrap items-baseline gap-x-4 gap-y-1 border-l-4 border-gray-800 bg-gray-100 p-3"
                role="status"
                data-draft-restored=""
              >
                <span>{T.draftKept}</span>
                <button type="button" onClick={startOver} className="font-semibold underline underline-offset-2">
                  {T.draftStartOver}
                </button>
              </div>
            )}
            <div>
              <h2 className="text-2xl font-bold mb-4">
                {T.clientInformation}
              </h2>
              {/* ⚠ `role="status"` is the ONE thing on this page that TW-0195 changed for a shop that
                  never turned the action on. This line appears with no tap of the client's and rewrites
                  five boxes under their thumb, so a screen reader ought to have been announcing it since
                  TW-0037 and was not; it is also the answer the panel's "Find me" gives, and an answer
                  that is never announced is not one. Nothing else about an un-flagged shop's form moved. */}
              {welcomeBack && (
                <div className="mb-6 border-l-4 border-gray-800 bg-gray-100 p-3" role="status">
                  <span className="font-semibold">{T.welcomeBack}</span>{T.welcomeBackCheck}
                </div>
              )}
              {/* TW-0195 — THE ONE ANSWER TO EVERY NO, and it is deliberately one answer. The API
                  answers a half match (a phone on file here with the wrong email) byte for byte the
                  way it answers a miss, so that a stranger cannot stand at the counter and ask a
                  shop's own form whether a number is one of its clients. A rate-limited lookup and a
                  dropped connection read the same way, because a client cannot tell those apart and
                  neither should anybody else. `T.lookupMissed` is written ONCE and rendered ONCE. */}
              {lookupMissed && !welcomeBack && (
                <div className="mb-6 border-l-4 border-gray-400 bg-gray-100 p-3" role="status">
                  {T.lookupMissed}
                </div>
              )}
              <div className="flex flex-col space-y-2 mb-4 sm:flex-row sm:space-x-4 sm:space-y-0">
                <div className="w-full sm:w-1/3 text-xl">{T.name}</div>
                <div className="w-full sm:w-2/3">
                  <div className="flex space-x-4">
                    <div className="flex flex-col w-full">
                      <div className="flex space-x-4">
                        <div className="flex flex-col w-full">
                          {/* The autocomplete tokens are what a phone's autofill keys on: iOS
                              offers the client's own first and last name, number, email and
                              address off their contact card only when the field says which it
                              is — the field's `name` is a hint at best (Chris, 2026-09-07, on
                              his phone: "i didn't see my first name suggested"). One token per
                              field, from the HTML standard's list; a wrong one autofills wrong. */}
                          <input
                            ref={firstNameRef}
                            className="w-full border-2 p-2"
                            placeholder={T.firstName}
                            name="firstName"
                            maxLength={FIELD_LIMITS.firstName}
                            aria-label={T.firstName}
                            autoComplete="given-name"
                            value={form.firstName}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                        <div className="flex flex-col w-full">
                          <input
                            className="w-full border-2 p-2"
                            placeholder={T.lastName}
                            name="lastName"
                            maxLength={FIELD_LIMITS.lastName}
                            aria-label={T.lastName}
                            autoComplete="family-name"
                            value={form.lastName}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex flex-col space-y-2 mb-4 sm:flex-row sm:space-x-4 sm:space-y-0">
                <div className="w-full sm:w-1/3 text-xl">{T.birthDate}</div>
                <div className="w-full sm:w-2/3">
                  <div className="flex space-x-4">
                    <div className="flex flex-col w-full">
                      <div className="flex space-x-4">
                        <div className="flex flex-col w-full">
                          <input
                            className="w-full border-2 p-2"
                            placeholder={T.mm}
                            name="birthDateMonth"
                            aria-label={T.month}
                            value={form.birthDateMonth}
                            onChange={advanceAfterTwoDigits(birthDayRef)}
                            required
                            type="number"
                            inputMode="numeric"
                            onFocus={() => {
                              setAnyAgeInputIsFocused(true);
                            }}
                            onBlur={() => {
                              setAnyAgeInputIsFocused(false);
                            }}
                            autoComplete="bday-month"
                          />
                          <div className="ps-1">
                            <span className="text-sm text-gray-500">{T.month}</span>
                          </div>
                        </div>
                        <div className="flex flex-col w-full">
                          <input
                            className="w-full border-2 p-2"
                            ref={birthDayRef}
                            placeholder={T.dd}
                            name="birthDateDay"
                            aria-label={T.day}
                            value={form.birthDateDay}
                            onChange={advanceAfterTwoDigits(birthYearRef)}
                            required
                            type="number"
                            inputMode="numeric"
                            onFocus={() => {
                              setAnyAgeInputIsFocused(true);
                            }}
                            onBlur={() => {
                              setAnyAgeInputIsFocused(false);
                            }}
                            autoComplete="bday-day"
                          />
                          <div className="ps-1">
                            <span className="text-sm text-gray-500">{T.day}</span>
                          </div>
                        </div>
                        <div className="flex flex-col w-full">
                          <input
                            className="w-full border-2 p-2"
                            ref={birthYearRef}
                            placeholder={T.yyyy}
                            name="birthDateYear"
                            aria-label={T.year}
                            aria-describedby="birth-date-age"
                            value={form.birthDateYear}
                            onChange={handleInputChange}
                            required
                            type="number"
                            inputMode="numeric"
                            onFocus={() => {
                              setAnyAgeInputIsFocused(true);
                            }}
                            onBlur={() => {
                              setAnyAgeInputIsFocused(false);
                            }}
                            autoComplete="bday-year"
                          />
                          <div className="ps-1">
                            <span className="text-sm text-gray-500">{T.year}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* TW-0503 (2026-10-29) — THE AGE THE RECORD WILL SAY, read off the birth date, in the page's language, and nobody's
                      to type: the age box that stood above these three is gone (birthDate.ts). Always drawn, so a screen reader
                      hears the age the moment there is one. */}
                  <p id="birth-date-age" className={age !== null ? "mt-2 text-lg" : undefined} aria-live="polite">
                    {age !== null ? T.youAre(age) : ""}
                  </p>
                  {/* TW-0501 — a door refusal about a minor that took the client to the birth date (refusal.ts DOOR_FALLS_TO), said
                      here too: it comes after the signature, at the bottom of the page, the phone box's reason */}
                  {refusedFor.startsWith("door_") && doorPlace === "birthDateMonth" && submissionError && (
                    <div className="mt-1 text-base" role="alert">
                      <span className="text-red-700 font-semibold">{submissionError}</span>
                    </div>
                  )}
                  {/* 2026-10-26 (the form audit) — and while a birth date refusal is up it stays said with the cursor in the boxes:
                      both take the client there (refusal.ts), and a sentence hidden while a box has the focus said nothing to the
                      client it had just been brought to, the only other copy under the Submit button, off screen */}
                  {(!anyAgeInputIsFocused || ageRefusalUp) && birthDateIsValidErrorMessage && (
                    <ErrorMessage message={birthDateIsValidErrorMessage} />
                  )}
                  {/* TW-0128 — the state's own refusal, in the state's own words, where the 18+ one
                      used to be. Only ever one of the two: the validator was handed null for the
                      age check when this has something to say. */}
                  {!anyAgeInputIsFocused && minorRefusalMessage && (
                    <ErrorMessage message={minorRefusalMessage} />
                  )}
                </div>
              </div>
              {/* TW-0526, the client-form side (2026-10-29) — a fact about the client the shop's state's rule names, beside the birth
                  date (Mississippi's sex as on the ID): drawn only where the rule names it, required there (FormItems.tsx) */}
              {youFields.map((box) => (
                <StateFieldBox key={box.key} {...stateFieldPiece(box)} />
              ))}
              <div className="flex flex-col space-y-2 mb-4 sm:flex-row sm:space-x-4 sm:space-y-0">
                <div className="w-full sm:w-1/3 text-xl">{T.phone}</div>
                <div className="w-full sm:w-2/3">
                  <input
                    className="w-full border-2 p-2"
                    placeholder={T.phonePlaceholder}
                    name="phoneNumber"
                    maxLength={FIELD_LIMITS.phoneNumber}
                    aria-label={T.phone}
                    type="tel"
                    autoComplete="tel"
                    value={form.phoneNumber}
                    onChange={handleInputChange}
                    required
                  />
                  {/* TW-0488 — the door's answer to a number it could not read, said here too: it comes after the signature, at
                      the bottom of the page, and the page is taken up to this box (refusal.ts phone_not_valid) */}
                  {refusedFor === "phone_not_valid" && submissionError && (
                    <div className="mt-1 text-base" role="alert">
                      <span className="text-red-700 font-semibold">{submissionError}</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-col space-y-2 mb-4 sm:flex-row sm:space-x-4 sm:space-y-0">
                <div className="w-full sm:w-1/3 text-xl">{T.email}</div>
                <div className="w-full sm:w-2/3">
                  <div className="flex flex-col w-full">
                    {/* The example lives in the field like every other field's, not on a line
                        under an empty box (Chris, 2026-09-07: "i don't see an email address
                        placeholder on desktop which seems inconsistent"). type=email is the @
                        keyboard on a phone and a "put an @ in it" check in the browser before
                        anything is posted; the API prints the address on the PDF and matches a
                        returning client on it, so a typo caught here is one that never lands. */}
                    <input
                      ref={emailRef}
                      className="w-full border-2 p-2"
                      placeholder={T.emailPlaceholder}
                      name="email"
                      maxLength={FIELD_LIMITS.email}
                      aria-label={T.email}
                      type="email"
                      autoComplete="email"
                      value={form.email}
                      aria-describedby={didYouMean ? "email-did-you-mean" : undefined}
                      onChange={(e) => {
                        if (didYouMean) setDidYouMean(null);
                        handleInputChange(e);
                      }}
                      onBlur={(e) => {
                        const meant = suggestEmail(e.target.value);
                        setDidYouMean(meant);
                        if (meant) track("email_typo_offered", { lang });
                      }}
                      required
                    />
                    {/* TW-0152 — an OFFER, not an error, so it is not red: the heavy left rule and
                        the underlined address carry it, which is what survives a colourblind read.
                        Full tap height — the person this exists for is on a phone at a counter. */}
                    {didYouMean && (
                      <button
                        type="button"
                        id="email-did-you-mean"
                        className="mt-1 w-full border-l-8 border-black bg-gray-100 px-3 py-3 text-left text-lg hover:bg-gray-200"
                        onClick={() => {
                          setForm({ ...form, email: didYouMean });
                          setDidYouMean(null);
                          track("email_typo_taken", { lang });
                          emailRef.current?.focus();
                        }}
                      >
                        {T.didYouMeanBefore}
                        <span className="font-bold underline underline-offset-2">{didYouMean}</span>
                        {T.didYouMeanAfter}
                      </button>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex flex-col space-y-2 mb-4 sm:flex-row sm:space-x-4 sm:space-y-0">
                <div className="w-full sm:w-1/3 text-xl">{T.address}</div>
                <div className="w-full sm:w-2/3">
                  {/* Each box says what it is in its own placeholder; the caption that used to repeat
                      it underneath ("Address 1" over "Street Address", "City" over "City") is gone,
                      and the second line says it is optional, which nothing said before. */}
                  <div className="flex space-x-4 mb-3">
                    {/* TW-0084 — the street box is the search box; the list drops under it, over the
                        boxes below rather than pushing them, and goes away on a tap, Escape, or a blur. */}
                    <div className="relative flex flex-col w-full">
                      <input
                        className="w-full border-2 p-2"
                        placeholder={T.streetAddress}
                        name="address1"
                        maxLength={FIELD_LIMITS.address1}
                        aria-label={T.streetAddress}
                        autoComplete="address-line1"
                        role="combobox"
                        aria-autocomplete="list"
                        aria-expanded={suggestions.length > 0}
                        aria-controls="address-suggestions"
                        value={form.address1}
                        onChange={handleStreetChange}
                        onKeyDown={handleStreetKeyDown}
                        onBlur={() => setTimeout(closeSuggestions, 150)}
                        required
                      />
                      {suggestions.length > 0 && (
                        <ul
                          id="address-suggestions"
                          role="listbox"
                          aria-label={T.addressSuggestions}
                          className="absolute left-0 right-0 top-full z-20 mt-1 max-h-96 overflow-y-auto border-2 bg-white shadow-lg"
                          onMouseDown={(e) => e.preventDefault()}
                        >
                          {suggestions.map((s, i) => (
                            <li
                              key={s.id}
                              role="option"
                              aria-selected={i === highlighted}
                              className={`cursor-pointer px-3 py-3 ${i > 0 ? "border-t" : ""} ${i === highlighted ? "bg-gray-100" : ""}`}
                              onClick={() => pickSuggestion(s)}
                            >
                              <div className="text-lg leading-tight">{s.main}</div>
                              {s.secondary && <div className="text-sm text-gray-600">{s.secondary}</div>}
                            </li>
                          ))}
                          <li className="px-3 py-1 text-right text-xs text-gray-500" aria-hidden="true">
                            {T.poweredByGoogle}
                          </li>
                        </ul>
                      )}
                    </div>
                  </div>
                  <div className="flex space-x-4 mb-3">
                    <div className="flex flex-col w-full">
                      <input
                        ref={aptRef}
                        className="w-full border-2 p-2"
                        placeholder={T.addressLine2}
                        name="address2"
                        maxLength={FIELD_LIMITS.address2}
                        aria-label={T.addressLine2}
                        autoComplete="address-line2"
                        value={form.address2}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                  <div className="flex space-x-4 mb-3">
                    <div className="flex flex-col w-full">
                      <div className="flex space-x-4">
                        <div className="flex flex-col w-full">
                          <input
                            className="w-full border-2 p-2"
                            placeholder={T.city}
                            name="city"
                            maxLength={FIELD_LIMITS.city}
                            aria-label={T.city}
                            autoComplete="address-level2"
                            value={form.city}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                        <div className="flex flex-col w-full">
                          <input
                            className="w-full border-2 p-2"
                            placeholder={T.state}
                            name="state"
                            maxLength={FIELD_LIMITS.state}
                            aria-label={T.state}
                            autoComplete="address-level1"
                            value={form.state}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div>
                    <input
                      className="w-full border-2 p-2"
                      placeholder={T.zip}
                      name="zip"
                      maxLength={FIELD_LIMITS.zip}
                      aria-label={T.zip}
                      autoComplete="postal-code"
                      value={form.zip}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
              </div>
            </div>
            {/* WHO TO CALL (2026-09-11) — only when the shop asks or its state requires it. Four plain
                boxes in the client block's own shape. `autoComplete="off"` on every one of them, because
                the browser's own suggestions for "name", "tel" and "address" are the CLIENT's, and a
                client whose own details land here has an emergency contact who is themselves. */}
            {askEmergency && (
              <div>
                <h2 className="text-2xl font-bold mt-6 mb-2">{T.emergencySection}</h2>
                <p className="mb-4 text-gray-600">{T.emergencyWhy(client?.name || "the shop")}</p>
                <div className="flex flex-col space-y-2 mb-4 sm:flex-row sm:space-x-4 sm:space-y-0">
                  <div className="w-full sm:w-1/3 text-xl">{T.emergencyName}</div>
                  <div className="w-full sm:w-2/3">
                    <input
                      className="w-full border-2 p-2"
                      placeholder={T.emergencyNamePlaceholder}
                      name="emergencyContactName"
                      aria-label={`${T.emergencySection}: ${T.emergencyName}`}
                      autoComplete="off"
                      maxLength={FIELD_LIMITS.emergencyContactName}
                      value={form.emergencyContactName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
                <div className="flex flex-col space-y-2 mb-4 sm:flex-row sm:space-x-4 sm:space-y-0">
                  <div className="w-full sm:w-1/3 text-xl">{T.emergencyRelationship}</div>
                  <div className="w-full sm:w-2/3">
                    <input
                      className="w-full border-2 p-2"
                      placeholder={T.emergencyRelationshipPlaceholder}
                      name="emergencyContactRelationship"
                      aria-label={`${T.emergencySection}: ${T.emergencyRelationship}`}
                      autoComplete="off"
                      maxLength={FIELD_LIMITS.emergencyContactRelationship}
                      value={form.emergencyContactRelationship}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
                <div className="flex flex-col space-y-2 mb-4 sm:flex-row sm:space-x-4 sm:space-y-0">
                  <div className="w-full sm:w-1/3 text-xl">{T.emergencyPhone}</div>
                  <div className="w-full sm:w-2/3">
                    <input
                      className="w-full border-2 p-2"
                      placeholder={T.phonePlaceholder}
                      name="emergencyContactPhone"
                      aria-label={`${T.emergencySection}: ${T.emergencyPhone}`}
                      type="tel"
                      autoComplete="off"
                      maxLength={FIELD_LIMITS.emergencyContactPhone}
                      value={form.emergencyContactPhone}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
                <div className="flex flex-col space-y-2 mb-4 sm:flex-row sm:space-x-4 sm:space-y-0">
                  <div className="w-full sm:w-1/3 text-xl">{T.emergencyAddress}</div>
                  <div className="w-full sm:w-2/3">
                    <input
                      className="w-full border-2 p-2"
                      placeholder={T.emergencyAddressPlaceholder}
                      name="emergencyContactAddress"
                      aria-label={`${T.emergencySection}: ${T.emergencyAddress}`}
                      autoComplete="off"
                      maxLength={FIELD_LIMITS.emergencyContactAddress}
                      value={form.emergencyContactAddress}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
              </div>
            )}
            <div>
              <h2 className="text-2xl font-bold mt-6 mb-4">
                {asksWhichService(services) && !service ? T.whatToday : askArtist || !service ? copy.section : sectionWithoutArtist(service, lang)}
              </h2>
              {/* TW-0059 — a shop that does both asks once, here, and the rest of the form follows
                  the answer. Two big targets rather than the small radios the questionnaire uses:
                  this is the one choice on the page that decides which document gets signed.
                  TW-0279 — the targets are the shop's own services (servicesFor), never a fixed two: a
                  shop that also does cosmetic tattooing offers it here, and three stack on a phone. */}
              {asksWhichService(services) && (
                <div
                  className="flex flex-col space-y-2 mb-6 sm:flex-row sm:space-x-4 sm:space-y-0"
                  data-refusal="no_service"
                >
                  <div className="w-full sm:w-1/3 text-xl">{T.todayIm}</div>
                  <div className="w-full sm:w-2/3">
                    <div className={services.length > 2 ? "flex flex-col space-y-2 sm:flex-row sm:space-x-4 sm:space-y-0" : "flex space-x-4"}>
                      {services.map((option) => (
                        <label
                          key={option}
                          className={`flex-1 flex items-center justify-center border-2 p-4 text-xl cursor-pointer ${
                            service === option
                              ? "border-blue-600 bg-blue-50 font-semibold"
                              : !service && refusedFor === "no_service" && submissionError
                                ? "border-red-700"
                                : "border-gray-300"
                          }`}
                        >
                          <input
                            type="radio"
                            className="form-radio h-5 w-5 text-gray-600 me-2"
                            name="serviceType"
                            value={option}
                            checked={service === option}
                            onChange={() => {
                              setService(option);
                              setSubmissionError("");
                              setRefusedFor("");
                            }}
                          />
                          {({ tattoo: T.aTattoo, piercing: T.aPiercing, cosmetic: T.aCosmetic } as Record<ServiceType, string>)[option]}
                        </label>
                      ))}
                    </div>
                    {/* 2026-09-29 — the refusal is said HERE too, under the two choices it is about, and the
                        page has been brought to them (refusal.ts). Under the Submit button alone it sent a new
                        shop's owner looking for a choice that was most of a page above him, seven times. The
                        same red and the same weight as the sentence under the button, which stays. */}
                    {!service && refusedFor === "no_service" && submissionError && (
                      <div className="mt-2" role="alert">
                        <span className="text-red-700 font-semibold">{submissionError}</span>
                      </div>
                    )}
                    {/* The caption under the two buttons is GONE (Chris, 2026-09-08: "'Pick one and
                        the rest of this form follows it.' can be struck from the form also btw, on
                        the demo and deployed forms."). Two buttons labelled Tattoo and Piercing, under
                        a heading that asks which — a line explaining that the form reacts is a line
                        explaining a thing the next tap demonstrates. handleSubmit still refuses
                        without a pick, and says so in the client's own language, so nothing about the
                        gate changed; only the sentence nobody needed. */}
                  </div>
                </div>
              )}
              {!service ? null : (
              <>
              {/* TW-0422 — the owner's "Ask which artist" / "Ask which piercer" (artists.ts asksArtistFor): off, the question is not
                  on this page at all */}
              {askArtist && (
              <div className="flex flex-col space-y-2 mb-4 sm:flex-row sm:space-x-4 sm:space-y-0">
                <div className="w-full sm:w-1/3 text-xl">{copy.artistLabel}</div>
                <div className="w-full sm:w-2/3">
                  <div className="flex space-x-4">
                    <div className="flex flex-col w-full">
                      <div className="flex space-x-4">
                        <div className="flex flex-col w-full">
                          {/* 2026-10-18 — A TATTOO CONVENTION's field is a search over the show's roster
                              (ArtistSearch.tsx); what it posts is the picker's own body, below. */}
                          {artistSearch ? (
                            <ArtistSearch
                              options={artists}
                              value={form.nameOfArtist}
                              choice={artistChoice}
                              service={service}
                              lang={lang}
                              maxLength={FIELD_LIMITS.nameOfArtist}
                              showLicense={showLicense}
                              licenseWord={T.license}
                              onChange={(text, choice) => {
                                setArtistChoice(choice);
                                setSubmissionError("");
                                setForm((was) => ({ ...was, nameOfArtist: text }));
                              }}
                            />
                          ) : (
                          <>
                          {/* A shop that keeps a list of its artists gets a picker; a shop that
                              does not gets the plain field it has had since 2023, unchanged.
                              artists.ts holds every decision, including why the NAME is still what
                              gets posted. */}
                          {asksWhichArtist(artists) && (
                            <>
                              <select
                                className="w-full border-2 p-2 bg-white"
                                name="artistChoice"
                                value={artistChoice}
                                onChange={(e) => {
                                  const choice = e.target.value;
                                  setArtistChoice(choice);
                                  setSubmissionError("");
                                  // The picked name IS the posted name, so it goes straight onto
                                  // the field. "Someone else" empties it and hands the client the
                                  // box below to type in.
                                  setForm((was) => ({ ...was, nameOfArtist: artistNameFor(choice, "") }));
                                }}
                                required
                                aria-invalid={nativeMissedHere("artistChoice", !artistChoice) || undefined}
                              >
                                <option value="">{artistCopy.prompt}</option>
                                {artists.map((artist) => (
                                  <option key={artist.id || artist.name} value={artist.name}>
                                    {artist.name}
                                    {/* TW-0104 — the license number beside the name where the state's rule
                                        wants the licensee's number on the record (Oregon); the shop fills
                                        it on the Artists screen, and it prints on the signed waiver too. */}
                                    {showLicense && artist.licenseNumber ? ` · ${T.license} ${artist.licenseNumber}` : ""}
                                  </option>
                                ))}
                                <option value={SOMEONE_ELSE}>{someoneElseLabel(lang)}</option>
                              </select>
                              {/* TW-0541 — the browser stopped the Submit here (218 of 432 in a week, almost all iPhones, which
                                  show nothing): the picker's own missing sentence, under the box, until one is picked */}
                              {nativeMissedHere("artistChoice", !artistChoice) && (
                                <div className="ps-1" role="alert">
                                  <span className={MISSED_LINE}>{artistCopy.missing}</span>
                                </div>
                              )}
                              {artistChoice !== SOMEONE_ELSE && (
                                <div className="ps-1">
                                  <span className="text-sm text-gray-500">{artistCopy.hint}</span>
                                </div>
                              )}
                            </>
                          )}
                          {(!asksWhichArtist(artists) || artistChoice === SOMEONE_ELSE) && (
                            <>
                              <input
                                className={`w-full border-2 p-2${asksWhichArtist(artists) ? " mt-4" : ""}`}
                                placeholder={copy.artistPlaceholder}
                                name="nameOfArtist"
                                maxLength={FIELD_LIMITS.nameOfArtist}
                                value={form.nameOfArtist}
                                onChange={handleInputChange}
                                required
                                aria-invalid={nativeMissedHere("nameOfArtist", !form.nameOfArtist) || undefined}
                                autoComplete="nameOfArtist"
                              />
                              {nativeMissedHere("nameOfArtist", !form.nameOfArtist) && (
                                <div className="ps-1" role="alert">
                                  <span className={MISSED_LINE}>{nativeLine("fill")}</span>
                                </div>
                              )}
                              {copy.artistHint && (
                                <div className="ps-1">
                                  <span className="text-sm text-gray-500">
                                    {copy.artistHint}
                                  </span>
                                </div>
                              )}
                            </>
                          )}
                          </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              )}
              {/* TW-0279 — A COSMETIC TATTOO'S WORK, under the artist: the procedures (one or more, boxes, the picker the
                  shop's state allows, with the state's own reason for any it leaves off standing under it), then what this
                  visit is. The English value is what is posted and printed, whatever language the client reads. Both are
                  refused at the submit without an answer. A tattoo's and a piercing's page draw none of this. */}
              {isCosmetic && (
                <>
                  <div className="flex flex-col space-y-2 mb-4 sm:flex-row sm:space-x-4 sm:space-y-0" data-refusal="no_procedure">
                    <div className="w-full sm:w-1/3 text-xl" id="cosmetic-procedures-label">
                      {T.cosmeticProcedures}
                    </div>
                    <div className="w-full sm:w-2/3">
                      <div role="group" aria-labelledby="cosmetic-procedures-label" className="flex flex-col">
                        {proceduresOffered.map((option) => (
                          <label key={option.value} className="inline-flex items-center mt-2">
                            <input
                              type="checkbox"
                              className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                              name="cosmeticProcedures"
                              value={option.value}
                              checked={form.cosmeticProcedures.includes(option.value)}
                              onChange={(e) => toggleProcedure(option.value, e.target.checked)}
                            />
                            <span className="ml-2 text-gray-700">{cosmeticLabel(option, lang)}</span>
                          </label>
                        ))}
                      </div>
                      <div className="ps-1 mt-1">
                        <span className="text-sm text-gray-500">{T.cosmeticProceduresHint}</span>
                      </div>
                      {cosmeticBars.map((bar) => (
                        <p key={bar.procedures.join("|")} className="text-sm text-gray-600 mt-2" data-cosmetic-bar="">
                          {lang === "es" ? bar.refusal.es : bar.refusal.en}
                        </p>
                      ))}
                      {cosmeticLifts.map((bar) => (
                        <p key={`lifted:${bar.procedures.join("|")}`} className="text-sm text-gray-600 mt-2" data-cosmetic-lift="">
                          {lang === "es" ? bar.lifted?.es : bar.lifted?.en}
                        </p>
                      ))}
                      {!procedures.length && refusedFor === "no_procedure" && submissionError && (
                        <div className="mt-2" role="alert">
                          <span className="text-red-700 font-semibold">{submissionError}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col space-y-2 mb-4 sm:flex-row sm:space-x-4 sm:space-y-0">
                    <div className="w-full sm:w-1/3 text-xl">{T.cosmeticVisit}</div>
                    <div className="w-full sm:w-2/3">
                      <select
                        className="w-full border-2 p-2 bg-white"
                        name="cosmeticVisit"
                        aria-label={T.cosmeticVisit}
                        value={form.cosmeticVisit}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">{T.chooseOne}</option>
                        {COSMETIC_VISITS.map((visit) => (
                          <option key={visit.value} value={visit.value}>
                            {cosmeticLabel(visit, lang)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </>
              )}
              <div className="flex flex-col space-y-2 mb-4 sm:flex-row sm:space-x-4 sm:space-y-0">
                <div className="w-full sm:w-1/3 text-xl">
                  {copy.descriptionLabel}
                </div>
                <div className="w-full sm:w-2/3">
                  <div className="flex space-x-4">
                    <div className="flex flex-col w-full">
                      <div className="flex space-x-4">
                        <div className="flex flex-col w-full">
                          <input
                            className="w-full border-2 p-2"
                            placeholder={copy.descriptionPlaceholder}
                            name="tattooDescription"
                            maxLength={FIELD_LIMITS.tattooDescription}
                            value={form.tattooDescription}
                            onChange={handleInputChange}
                            required
                            aria-invalid={nativeMissedHere("tattooDescription", !form.tattooDescription) || undefined}
                            autoComplete="tattooDescription"
                          />
                          {nativeMissedHere("tattooDescription", !form.tattooDescription) && (
                            <div className="ps-1" role="alert">
                              <span className={MISSED_LINE}>{nativeLine("fill")}</span>
                            </div>
                          )}
                          {copy.descriptionHint && (
                            <div className="ps-1">
                              <span className="text-sm text-gray-500">
                                {copy.descriptionHint}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex flex-col space-y-2 mb-4 sm:flex-row sm:space-x-4 sm:space-y-0">
                <div className="w-full sm:w-1/3 text-xl">
                  {copy.locationLabel}
                  {/* TW-0279 — a cosmetic tattoo's placement is the side, and lips have none: optional, and it says so */}
                  {isCosmetic && (
                    <>
                      {" "}
                      <span className="text-sm text-gray-500">{T.optional}</span>
                    </>
                  )}
                </div>
                <div className="w-full sm:w-2/3">
                  <div className="flex space-x-4">
                    <div className="flex flex-col w-full">
                      <div className="flex space-x-4">
                        <div className="flex flex-col w-full">
                          {isPiercing ? (
                            /* a list, not a box: whether the piercing is in or through the mouth
                               decides whether the dental question is asked, and a typed "labret"
                               cannot be read for that */
                            <select
                              className="w-full border-2 p-2 bg-white"
                              name="locationOnBody"
                              value={form.locationOnBody}
                              onChange={handleInputChange}
                              required
                              aria-invalid={nativeMissedHere("locationOnBody", !form.locationOnBody) || undefined}
                            >
                              <option value="">{T.chooseOne}</option>
                              {piercingSpots.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {locationLabel(option, lang)}
                                </option>
                              ))}
                            </select>
                          ) : isCosmetic ? (
                            /* TW-0279 — the same `locationOnBody`, for the side (the proposal's "Where"): the procedures
                               above already name the area. Never required: a lip blush has no side. */
                            <input
                              className="w-full border-2 p-2"
                              placeholder={copy.locationPlaceholder}
                              name="locationOnBody"
                              aria-label={copy.locationLabel}
                              value={form.locationOnBody}
                              onChange={handleInputChange}
                              maxLength={FIELD_LIMITS.locationOnBody}
                              autoComplete="locationOnBody"
                            />
                          ) : (
                            <input
                              className="w-full border-2 p-2"
                              // 2026-10-26 — the tattoo copy's own words, in the client's language: this was the one box written
                              // here in English, and a Spanish page said "Location on body" in it (service.ts locationPlaceholder)
                              placeholder={copy.locationPlaceholder ?? "Location on body"}
                              name="locationOnBody"
                              maxLength={FIELD_LIMITS.locationOnBody}
                              value={form.locationOnBody}
                              onChange={handleInputChange}
                              required
                              aria-invalid={nativeMissedHere("locationOnBody", !form.locationOnBody) || undefined}
                              autoComplete="locationOnBody"
                            />
                          )}
                          {nativeMissedHere("locationOnBody", !form.locationOnBody) && (
                            <div className="ps-1" role="alert">
                              <span className={MISSED_LINE}>{nativeLine(isPiercing ? "pick" : "fill")}</span>
                            </div>
                          )}
                          {copy.locationHint && (
                            <div className="ps-1">
                              <span className="text-sm text-gray-500">
                                {copy.locationHint}
                              </span>
                            </div>
                          )}
                          {/* TW-0128 — a site this state bars for a minor, or a spot we cannot
                              verify. It lives HERE and not with the age error because it is not a
                              dead end: picking another spot clears it. */}
                          {placementRefusalMessage && (
                            <ErrorMessage message={placementRefusalMessage} />
                          )}
                          {/* TW-0225 — THE EAR QUESTION, under the spot it is about, once that spot is one the
                              state's carve-out reaches. Worded from how far the carve-out reaches and whether it
                              holds only for a stud-and-clasp gun, which a needle through the lobe is not. */}
                          {askEar && (
                            <div className="mt-3 border-2 border-black p-3" data-testid="ear-question">
                              <div className="text-lg mb-1">{earQuestion}</div>
                              {earCarveOut.method === "stud_gun" && (
                                <div className="text-sm text-gray-600 mb-2">{T.earNeedleIsNo(earCarveOut.scope)}</div>
                              )}
                              <div className="flex space-x-4">
                                {(["yes", "no"] as const).map((choice) => (
                                  <label
                                    key={choice}
                                    className={`flex items-center border-2 p-2 cursor-pointer ${
                                      form.earPiercing === choice
                                        ? "border-blue-600 bg-blue-50 font-semibold"
                                        : "border-gray-300"
                                    }`}
                                  >
                                    <input
                                      type="radio"
                                      className="form-radio h-5 w-5 text-gray-600 me-2"
                                      name="earPiercing"
                                      value={choice}
                                      checked={form.earPiercing === choice}
                                      onChange={handleInputChange}
                                    />
                                    {choice === "yes" ? T.minorCoverupYes : T.minorCoverupNo}
                                  </label>
                                ))}
                              </div>
                              {earOutside && (
                                <p className="mt-2 text-sm">{T.earOutside(stateNameOf(client?.state ?? undefined))}</p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {isPiercing && (
                <>
                  <div className="flex flex-col space-y-2 mb-4 sm:flex-row sm:space-x-4 sm:space-y-0">
                    <div className="w-full sm:w-1/3 text-xl">{T.jewelryMaterial}</div>
                    <div className="w-full sm:w-2/3">
                      <div className="flex flex-col w-full">
                        {fixedJewelry ? (
                          /* the shop's one material, read rather than chosen (fixedJewelryMaterial
                             above); it is on the form so the client sees what their record says */
                          <div className="p-2 text-xl">{jewelryMaterialLabel(fixedJewelry, lang)}</div>
                        ) : (
                          <select
                            className="w-full border-2 p-2 bg-white"
                            name="jewelryMaterial"
                            value={form.jewelryMaterial}
                            onChange={handleInputChange}
                            required
                            aria-invalid={nativeMissedHere("jewelryMaterial", !form.jewelryMaterial) || undefined}
                          >
                            <option value="">{T.chooseOne}</option>
                            {JEWELRY_MATERIALS.map((material) => (
                              <option key={material} value={material}>
                                {jewelryMaterialLabel(material, lang)}
                              </option>
                            ))}
                          </select>
                        )}
                        {nativeMissedHere("jewelryMaterial", !form.jewelryMaterial) && (
                          <div className="ps-1" role="alert">
                            <span className={MISSED_LINE}>{nativeLine("pick")}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col space-y-2 mb-4 sm:flex-row sm:space-x-4 sm:space-y-0">
                    <div className="w-full sm:w-1/3 text-xl">
                      {T.jewelryNotes} <span className="text-sm text-gray-500">{T.optional}</span>
                    </div>
                    <div className="w-full sm:w-2/3">
                      <div className="flex flex-col w-full">
                        <input
                          className="w-full border-2 p-2"
                          placeholder={T.jewelryNotesPlaceholder}
                          name="jewelryNotes"
                          value={form.jewelryNotes}
                          onChange={handleInputChange}
                          maxLength={FIELD_LIMITS.jewelryNotes}
                        />
                        <div className="ps-1">
                          <span className="text-sm text-gray-500">
                            {T.jewelryNotesHint}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
              {/* TW-0154 — what the work costs, last in this section for both services because it is
                  the one line here that is about the bill rather than about the body. Only for a
                  shop that turned it on, OPTIONAL even then (the `optional` marker beside the label,
                  the same one Jewelry Notes carries), and no `inputMode`: a numeric pad has no dash
                  and no letters, and "150-200" and "TBD" are both real answers at a counter. */}
              {askPrice && (
                <div className="flex flex-col space-y-2 mb-4 sm:flex-row sm:space-x-4 sm:space-y-0">
                  <div className="w-full sm:w-1/3 text-xl">
                    {T.priceLabel(service)} <span className="text-sm text-gray-500">{T.optional}</span>
                  </div>
                  <div className="w-full sm:w-2/3">
                    {priceStyle === "free" ? (
                      <div className="flex flex-col w-full">
                        <input
                          className="w-full border-2 p-2"
                          placeholder={T.pricePlaceholder}
                          name="price"
                          aria-label={T.priceLabel(service)}
                          value={form.price}
                          onChange={handleInputChange}
                          maxLength={FIELD_LIMITS.price}
                          autoComplete="price"
                        />
                      </div>
                    ) : (
                      // 2026-09-11 — the choices, then only the boxes that choice needs. The numbers take the
                      // decimal pad: a range is two boxes, so nobody needs a dash, and "$" is added for them.
                      <div className="flex flex-col w-full space-y-2">
                        <select
                          className="w-full border-2 p-2 bg-white"
                          name="priceKind"
                          aria-label={T.priceLabel(service)}
                          value={priceKind}
                          onChange={(e) => {
                            const kind = e.target.value as PriceKind;
                            setPriceKind(kind);
                            if (kind === "hourly") setPriceParts((current) => prefillRate(current, shopRate));
                          }}
                        >
                          <option value="">{T.chooseOne}</option>
                          <option value="fixed">{T.priceKindFixed}</option>
                          <option value="range">{T.priceKindRange}</option>
                          <option value="hourly">{T.priceKindHourly}</option>
                        </select>
                        {priceKind === "fixed" && (
                          <input
                            className="w-full border-2 p-2"
                            aria-label={T.priceAmount}
                            placeholder={T.pricePlaceholder}
                            inputMode="decimal"
                            maxLength={PRICE_PART_MAX}
                            value={priceParts.amount}
                            onChange={(e) => setPriceParts({ ...priceParts, amount: e.target.value })}
                          />
                        )}
                        {priceKind === "range" && (
                          <div className="flex space-x-2">
                            <input
                              className="w-1/2 border-2 p-2"
                              aria-label={T.priceFrom}
                              placeholder={T.priceFrom}
                              inputMode="decimal"
                              maxLength={PRICE_PART_MAX}
                              value={priceParts.low}
                              onChange={(e) => setPriceParts({ ...priceParts, low: e.target.value })}
                            />
                            <input
                              className="w-1/2 border-2 p-2"
                              aria-label={T.priceTo}
                              placeholder={T.priceTo}
                              inputMode="decimal"
                              maxLength={PRICE_PART_MAX}
                              value={priceParts.high}
                              onChange={(e) => setPriceParts({ ...priceParts, high: e.target.value })}
                            />
                          </div>
                        )}
                        {priceKind === "hourly" && (
                          <input
                            className="w-full border-2 p-2"
                            aria-label={T.priceRate}
                            placeholder={T.priceRate}
                            inputMode="decimal"
                            maxLength={PRICE_PART_MAX}
                            value={priceParts.rate}
                            onChange={(e) => setPriceParts({ ...priceParts, rate: e.target.value })}
                          />
                        )}
                        {form.price ? <p className="text-sm text-gray-600">{T.priceOnWaiver(form.price)}</p> : null}
                      </div>
                    )}
                  </div>
                </div>
              )}
              {/* 2026-09-23 — how the client is paying, under the price because it is the other line here about the
                  bill (Cue at Piercinks: "a line for a form of payment"). Only for a shop that turned it on, and
                  REQUIRED there: no `optional` marker, no default, and handleSubmit refuses without a pick. The
                  questionnaire's own radios, side by side, a tap on the word picks it; they wrap on a narrow phone
                  rather than run off it. */}
              {askPaymentMethod && (
                <div className="flex flex-col space-y-2 mb-4 sm:flex-row sm:space-x-4 sm:space-y-0">
                  <div className="w-full sm:w-1/3 text-xl" id="payment-method-question">
                    {T.paymentMethodQuestion}
                  </div>
                  <div className="w-full sm:w-2/3">
                    <div
                      role="radiogroup"
                      aria-labelledby="payment-method-question"
                      className="w-full flex flex-row flex-wrap gap-x-8 ps-1"
                    >
                      {PAYMENT_METHODS.map((method: PaymentMethod) => (
                        <label key={method} className="inline-flex items-center mt-2">
                          <input
                            type="radio"
                            className="form-radio h-5 w-5 text-gray-600"
                            name="paymentMethod"
                            value={method}
                            checked={form.paymentMethod === method}
                            onChange={(e) => {
                              handleInputChange(e);
                              if (submissionError === T.needPaymentMethod) setSubmissionError("");
                            }}
                            required={!isMobile}
                          />
                          <span className="ml-2 text-gray-700">
                            {method === "cash" ? T.paymentCash : method === "card" ? T.paymentCard : T.paymentOther}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              </>
              )}
            </div>
            <div>
              <h2 className="text-2xl font-bold mt-6 mb-4">
                {T.questionnaire}
              </h2>
              {/* TW-0293 — drawn only where this shop's page asks it (askInfluence: the shop's switch, its state, or no state) */}
              {askInfluence && (
                <QuestionnaireItem
                  {...questionPiece("influenceOfDrugsOrAlcohol")}
                  label={T.qInfluence}
                  errorMessage={
                    form.questionnaire.influenceOfDrugsOrAlcohol === "yes" ? copy.influenceRefusal : ""
                  }
                />
              )}
              <QuestionnaireItem
                {...questionPiece("pregnantOrNursing")}
                label={T.qPregnant}
                optional={pregnancyOptional}
                errorMessage={
                  // TW-0628 — no sentence under a yes the shop's owner lets sign (the protection off): nothing new said, only no stop
                  form.questionnaire.pregnantOrNursing === "yes" && !pregnancyRefusalOff ? copy.pregnantRefusal : ""
                }
              />
              {/* TW-0293 — the same for the communicable-disease question and the box its yes opens */}
              {askCommunicable && (
                <QuestionnaireItem
                  {...questionPiece("communicableDisease")}
                  label={T.qCommunicable}
                />
              )}
              {askCommunicable && form.questionnaire.communicableDisease === "yes" && (
                <>
                  <div className="flex flex-col mt-6 mb-6">
                    <div className="w-full text-xl">
                      {T.communicableDetails}
                    </div>
                    <div className="w-full flex flex-col ps-1">
                      <div className="flex flex-col w-full mt-3">
                        <textarea
                          className="w-full border-2 p-2"
                          placeholder=""
                          aria-label={T.communicableDetails}
                          name="communicableDiseaseDetails"
                          value={form.communicableDiseaseDetails}
                          onChange={handleInputChange}
                          required={
                            form.questionnaire.communicableDisease === "yes"
                          }
                          ref={(box) => growWithText(box)}
                          maxLength={FIELD_LIMITS.communicableDiseaseDetails}
                        />
                      </div>
                      <div className="ps-1 relative">
                        <span className="text-sm text-gray-500">
                          {T.ifYesIdentify}
                        </span>
                        <EnteredCharactersCounter
                          value={form.communicableDiseaseDetails}
                          maxLength={FIELD_LIMITS.communicableDiseaseDetails}
                        />
                      </div>
                      {detailsRefusedHere("communicableDiseaseDetails") && (
                        <ErrorMessage message={detailsRefusedHere("communicableDiseaseDetails")} />
                      )}
                    </div>
                  </div>
                </>
              )}
              <QuestionnaireItem
                {...questionPiece("skinConditions")}
                label={isCosmetic ? T.qSkinCosmetic : T.qSkin}
              />
              {form.questionnaire.skinConditions === "yes" && (
                <>
                  <div className="flex flex-col mt-6">
                    <div className="w-full text-xl">
                      {T.skinDetails}
                    </div>
                    <div className="w-full flex flex-col ps-1">
                      <div className="flex flex-col w-full mt-3">
                        <textarea
                          className="w-full border-2 p-2"
                          placeholder=""
                          aria-label={T.skinDetails}
                          name="skinConditionsDetails"
                          value={form.skinConditionsDetails}
                          onChange={handleInputChange}
                          required={form.questionnaire.skinConditions === "yes"}
                          ref={(box) => growWithText(box)}
                          maxLength={FIELD_LIMITS.skinConditionsDetails}
                        />
                      </div>
                      <div className="ps-1 relative">
                        <span className="text-sm text-gray-500">
                          {T.ifYesIdentify}
                        </span>
                        <EnteredCharactersCounter
                          value={form.skinConditionsDetails}
                          maxLength={FIELD_LIMITS.skinConditionsDetails}
                        />
                      </div>
                      {detailsRefusedHere("skinConditionsDetails") && (
                        <ErrorMessage message={detailsRefusedHere("skinConditionsDetails")} />
                      )}
                    </div>
                  </div>
                </>
              )}
              {/* TW-0104 — the state's own questions, asked only where the shop's state requires them.
                  Oregon: sensitivities to medicines or topical solutions, and a bleeding-disorder
                  history (OAR 331-915-0085). Each is a yes/no with details on yes, the way the
                  questionnaire has always asked. */}
              {(
                [
                  askAllergies && { name: "allergies" as const, label: isCosmetic ? T.qAllergiesCosmetic : T.qAllergies, details: "allergiesDetails" as const, detailsLabel: T.allergiesDetailsLabel, detailsHint: T.ifYesIdentify },
                  askBleeding && { name: "bleedingDisorders" as const, label: T.qBleeding, details: "bleedingDisordersDetails" as const, detailsLabel: T.bleedingDetailsLabel, detailsHint: T.ifYesIdentify },
                  // Georgia's (3)(k), 2026-09-17: a yes or no with no details box — the answer that matters is "no"
                  askEaten && { name: "eatenRecently" as const, label: T.qEaten },
                  // Michigan's R 333.13109(5), 2026-10-08 (TW-0279 slice 8): a yes or no, and a yes asks for a photo of the
                  // doctor's prescription in place of a box
                  askNumbing && { name: "numbingProduct" as const, label: T.qNumbing, photo: true },
                  // TW-0270 slice 4 (2026-10-16) — the five a state's rule lists as its own questions (the audit's M04), each a yes
                  // or a no with no box, a yes recorded and never refused; the medical-history box below takes anything more
                  askDiabetes && { name: "diabetesHistory" as const, label: T.qDiabetesHistory },
                  askSeizures && { name: "seizuresOrFainting" as const, label: T.qSeizuresOrFainting },
                  askHeart && { name: "heartOrBloodPressure" as const, label: T.qHeartOrBloodPressure },
                  askBloodThinners && { name: "bloodThinnersRecently" as const, label: T.qBloodThinnersRecently },
                  askPregnantRecently && { name: "pregnantRecently" as const, label: T.qPregnantRecently },
                  // TW-0526 (2026-10-28, the audit's M04) — the questions a state's rule lists that the contract carries in their own
                  // words, after the five and in the state's order, the same yes or no with no box
                  // TW-0526 (M05) — and where one's yes opens a box (Arkansas's and Hawaii's armed forces), the box in its words
                  ...wordedQuestions.map((question) => ({
                    name: question.key as keyof typeof form.questionnaire,
                    label: lang === "es" ? question.es : question.en,
                    ...(question.details
                      ? { details: question.details as DetailsBox, detailsLabel: lang === "es" ? question.detailsEs : question.detailsEn }
                      : {}),
                  })),
                ].filter(Boolean) as { name: keyof typeof form.questionnaire; label: string; details?: DetailsBox; detailsLabel?: string; detailsHint?: string; photo?: boolean }[]
              ).map((question) => (
                <div key={question.name}>
                  <QuestionnaireItem {...questionPiece(question.name)} label={question.label} />
                  {/* 2026-10-08 (TW-0279 slice 8) — THE DOCTOR'S PRESCRIPTION, under a yes to the numbing question: the guardian
                      proof's uploader (the camera, or a saved photo where the shop allows one) and the rule in plain words under
                      its label. Posted as photoOfNumbingPrescription and printed on a page of its own on the signed waiver;
                      "Documentation of the prescription must be made part of the client record" (Mich. Admin. Code R
                      333.13109(5)). OPTIONAL since that evening, and said so beside its label: nothing about it stops the
                      signing, and the shop adds the photo from its dashboard before the artist can sign (the API's
                      prescription.py). The block is marked by the question it hangs under, for the tests and nothing else. */}
                  {question.photo && form.questionnaire[question.name] === "yes" && (
                    <div className="flex flex-col mt-6 mb-6" data-question-photo={question.name}>
                      <div className="w-full text-xl">
                        {T.numbingPrescriptionPhoto} <span className="text-sm text-gray-500">{T.optional}</span>
                        <div className="text-sm text-gray-500 mt-1">
                          {T.numbingPrescriptionNote(
                            STATE_NAMES[`${client?.state ?? ""}`.trim().toUpperCase()] ?? `${client?.state ?? ""}`.trim().toUpperCase()
                          )}
                        </div>
                      </div>
                      <div className="w-full mt-3">
                        <FormPhotoUploader
                          takingPhoto={takingPhotoOfNumbingPrescription}
                          setTakingPhoto={setTakingPhotoOfNumbingPrescription}
                          setSubmissionError={setSubmissionError}
                          photo={photoOfNumbingPrescription}
                          onCameraFailed={cameraFailed("photoOfNumbingPrescription", "health")}
                          setPhoto={setPhotoOfNumbingPrescription}
                          photoAltText="Photo of the doctor's prescription for the numbing product"
                          T={T}
                          allowLibrary={libraryOn}
                          onSource={setNumbingPrescriptionSource}
                          libraryTestId="numbing-prescription-photo-library-input"
                        />
                      </div>
                    </div>
                  )}
                  {question.details && form.questionnaire[question.name] === "yes" && (
                    <div className="flex flex-col mt-6 mb-6">
                      <div className="w-full text-xl">{question.detailsLabel}</div>
                      <div className="w-full flex flex-col ps-1">
                        <div className="flex flex-col w-full mt-3">
                          <textarea
                            className="w-full border-2 p-2"
                            placeholder=""
                            aria-label={question.detailsLabel}
                            name={question.details}
                            value={form[question.details]}
                            onChange={handleInputChange}
                            required
                            ref={(box) => growWithText(box)}
                            maxLength={FIELD_LIMITS[question.details]}
                          />
                        </div>
                        <div className="ps-1 relative">
                          {/* the condition's hint under the state's two health boxes; an armed-forces box's label says it all (TW-0526) */}
                          <span className="text-sm text-gray-500">{question.detailsHint ?? ""}</span>
                          <EnteredCharactersCounter value={form[question.details]} maxLength={FIELD_LIMITS[question.details]} />
                        </div>
                        {detailsRefusedHere(question.details) && <ErrorMessage message={detailsRefusedHere(question.details)} />}
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {/* TW-0526, the client-form side (2026-10-29) — a health box the shop's state's rule names that no yes or no carries,
                  after the state's questions (Oklahoma's "When did you last eat?", the phone's own time picker), required there */}
              {healthFields.map((box) => (
                <StateFieldBox key={box.key} {...stateFieldPiece(box)} />
              ))}
              {/* TW-0059 — a piercing's own three, asked only of a piercing, and the dental one
                  only when the piercing is in or through the mouth. What is not asked is not
                  posted (service.ts submissionForm), so an earlobe's record never carries a line
                  about dental work. */}
              {isPiercing &&
                piercingQuestionsFor(form.locationOnBody).map((question) => (
                  <div key={question.name}>
                    <QuestionnaireItem {...questionPiece(question.name)} label={questionWords(question, lang).label} />
                    {form.questionnaire[question.name] === "yes" && (
                      <div className="flex flex-col mt-6 mb-6">
                        <div className="w-full text-xl">{questionWords(question, lang).detailsLabel}</div>
                        <div className="w-full flex flex-col ps-1">
                          <div className="flex flex-col w-full mt-3">
                            <textarea
                              className="w-full border-2 p-2"
                              placeholder=""
                              aria-label={questionWords(question, lang).detailsLabel}
                              name={question.details}
                              value={form[question.details]}
                              onChange={handleInputChange}
                              required
                              ref={(box) => growWithText(box)}
                              maxLength={FIELD_LIMITS[question.details]}
                            />
                          </div>
                          <div className="ps-1 relative">
                            <span className="text-sm text-gray-500">
                              {T.tellYourPiercer}
                            </span>
                            <EnteredCharactersCounter value={form[question.details]} maxLength={FIELD_LIMITS[question.details]} />
                          </div>
                          {detailsRefusedHere(question.details) && <ErrorMessage message={detailsRefusedHere(question.details)} />}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              {/* TW-0279 — A COSMETIC TATTOO'S OWN QUESTIONS (cosmetic.ts COSMETIC_QUESTIONS), the ones on every cosmetic
                  form and those its picked procedures bring (the eyes for eyeliner, cold sores for lips), each a yes or a no
                  whose yes opens a box. What is not asked is not posted (service.ts submissionForm). An answer the shop's
                  state bars the work on is refused under its own question, in the rule's words (New Jersey's isotretinoin).
                  TW-0279 slice 9 — a state's own question in its record's words, with the rule's sentence under it where
                  the record gives one (California's herpes question at every procedure's site, the face question). */}
              {isCosmetic &&
                cosmeticQuestions.map((question) => (
                  <div key={question.name}>
                    <QuestionnaireItem
                      {...questionPiece(question.name)}
                      label={cosmeticQuestionWords(question, lang).label}
                      note={cosmeticQuestionWords(question, lang).why}
                      errorMessage={
                        cosmeticAnswerBar && cosmeticAnswerBar.question === question.name
                          ? lang === "es"
                            ? cosmeticAnswerBar.refusal.es
                            : cosmeticAnswerBar.refusal.en
                          : ""
                      }
                    />
                    {/* a state's own question opens no box: a yes to it is refused in its rule's words, or, California's
                        herpes question, recorded as its rule asks and nothing more (cosmetic.ts) */}
                    {question.details && form.questionnaire[question.name] === "yes" && !(cosmeticAnswerBar && cosmeticAnswerBar.question === question.name) && (
                      <div className="flex flex-col mt-6 mb-6">
                        <div className="w-full text-xl">{cosmeticQuestionWords(question, lang).detailsLabel}</div>
                        <div className="w-full flex flex-col ps-1">
                          <div className="flex flex-col w-full mt-3">
                            <textarea
                              className="w-full border-2 p-2"
                              placeholder=""
                              aria-label={cosmeticQuestionWords(question, lang).detailsLabel}
                              name={question.details}
                              value={form[question.details]}
                              onChange={handleInputChange}
                              required
                              ref={(box) => growWithText(box)}
                              maxLength={FIELD_LIMITS[question.details]}
                            />
                          </div>
                          <div className="ps-1 relative">
                            <span className="text-sm text-gray-500">{T.tellYourArtist}</span>
                            <EnteredCharactersCounter value={form[question.details]} maxLength={FIELD_LIMITS[question.details]} />
                          </div>
                          {detailsRefusedHere(question.details) && <ErrorMessage message={detailsRefusedHere(question.details)} />}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              {/* TW-0412 — THE SHOP'S OWN QUESTIONS (ownQuestions.ts), after every health question this page asks of its own
                  (the four, the state's, the service's) and before the written boxes: a Yes / No each in the owner's words,
                  with the owner's line under the answer it names. A shop that asks none draws nothing here. */}
              {ownQuestions.map((question) => (
                <OwnQuestionItem key={question.id} {...ownQuestionPiece(question)} />
              ))}
              <div className="flex flex-col mt-6">
                <div className="w-full text-xl">
                  {T.medicalHistory}
                </div>
                <div className="w-full flex flex-col ps-1">
                  <div className="flex flex-col w-full mt-3">
                    <textarea
                      className="w-full border-2 p-2"
                      placeholder=""
                      // 2026-10-26 (the form audit) — every written box is named by the question above it: until today all seven
                      // were nameless to a screen reader ("text area"), the question sitting in a div beside them
                      aria-label={T.medicalHistory}
                      name="medicalHistoryDetails"
                      value={form.medicalHistoryDetails}
                      onChange={handleInputChange}
                      ref={(box) => growWithText(box)}
                      maxLength={FIELD_LIMITS.medicalHistoryDetails}
                    />
                  </div>
                  <div className="ps-1 relative">
                    <span className="text-sm text-gray-500">
                      {T.ifYesIdentify}
                    </span>
                    <EnteredCharactersCounter
                      value={form.medicalHistoryDetails}
                      maxLength={FIELD_LIMITS.medicalHistoryDetails}
                    />
                  </div>
                </div>
              </div>
              {askMedications && <div className="flex flex-col mt-6">
                <div className="w-full text-xl">
                  {medicationsLabel}
                </div>
                <div className="w-full flex flex-col ps-1">
                  <div className="flex flex-col w-full mt-3">
                    <textarea
                      className="w-full border-2 p-2"
                      placeholder=""
                      aria-label={medicationsLabel}
                      name="medicationAndBloodThinners"
                      value={form.medicationAndBloodThinners}
                      onChange={handleInputChange}
                      ref={(box) => growWithText(box)}
                      maxLength={FIELD_LIMITS.medicationAndBloodThinners}
                    />
                  </div>
                  <div className="ps-1 relative">
                    <span className="text-sm text-gray-500">
                      {T.ifYesIdentify}
                    </span>
                    <EnteredCharactersCounter
                      value={form.medicationAndBloodThinners}
                      maxLength={FIELD_LIMITS.medicationAndBloodThinners}
                    />
                  </div>
                </div>
              </div>}
            </div>
            {/* THE CLIENT'S PHYSICIAN (2026-09-25) — after the health questions, which is where a doctor belongs on
                a form, and only when the shop asks for it on the service being signed. Three plain boxes in the
                emergency contact's shape, none of them required: a walk-in with no doctor still signs.
                `autoComplete="off"` on every one, because the browser's suggestions for "name", "tel" and
                "address" are the CLIENT's own. */}
            {askPhysician && (
              <div>
                <h2 className="text-2xl font-bold mt-6 mb-2">{T.physicianSection}</h2>
                <p className="mb-4 text-gray-600">{T.physicianWhy(client?.name || "the shop")}</p>
                <div className="flex flex-col space-y-2 mb-4 sm:flex-row sm:space-x-4 sm:space-y-0">
                  <div className="w-full sm:w-1/3 text-xl">{T.physicianName}</div>
                  <div className="w-full sm:w-2/3">
                    <input
                      className="w-full border-2 p-2"
                      placeholder={T.physicianNamePlaceholder}
                      name="physicianName"
                      aria-label={`${T.physicianSection}: ${T.physicianName}`}
                      autoComplete="off"
                      maxLength={FIELD_LIMITS.physicianName}
                      value={form.physicianName}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
                <div className="flex flex-col space-y-2 mb-4 sm:flex-row sm:space-x-4 sm:space-y-0">
                  <div className="w-full sm:w-1/3 text-xl">{T.physicianPhone}</div>
                  <div className="w-full sm:w-2/3">
                    <input
                      className="w-full border-2 p-2"
                      placeholder={T.phonePlaceholder}
                      name="physicianPhone"
                      aria-label={`${T.physicianSection}: ${T.physicianPhone}`}
                      type="tel"
                      autoComplete="off"
                      maxLength={FIELD_LIMITS.physicianPhone}
                      value={form.physicianPhone}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
                <div className="flex flex-col space-y-2 mb-4 sm:flex-row sm:space-x-4 sm:space-y-0">
                  <div className="w-full sm:w-1/3 text-xl">{T.physicianAddress}</div>
                  <div className="w-full sm:w-2/3">
                    <input
                      className="w-full border-2 p-2"
                      placeholder={T.physicianAddressPlaceholder}
                      name="physicianAddress"
                      aria-label={`${T.physicianSection}: ${T.physicianAddress}`}
                      autoComplete="off"
                      maxLength={FIELD_LIMITS.physicianAddress}
                      value={form.physicianAddress}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              </div>
            )}
            <div className="consent-items">
              <h2 className="text-2xl font-bold mt-6 mb-4">
                {T.acknowledgment}
              </h2>
              {/* TW-0059 — the service's own clauses, in the service's own order (service.ts). A
                  tattoo's are word for word what this form has shown since 2023; a piercing's say
                  piercing, and add the one a tattoo has no equivalent of: migration and rejection.
                  Before a `both` shop's client has picked there is nothing honest to show here. */}
              {!service ? (
                <p className="text-base text-gray-600 mb-4">
                  {T.chooseServiceForAcks(serviceNouns)}
                </p>
              ) : client.placeholder ? null : (
                // Until the shop is known (the placeholder client, App.tsx) no sentence names it: the first paint of a
                // single-service shop's form read "be used for 's portfolio showcase" and "I acknowledge that does not
                // offer refunds." before the config landed (seen on prod 2026-10-08). The same guard sits on the two
                // optional boxes below and on the guardian line.
                acksShown.map((key: AckKey) => (
                  <AcknowledgmentItem key={key} {...clausePiece(key)} label={ackLabel(key, service, client.name, lang)} />
                ))
              )}
              {/* TW-0170 — THE STATE'S OWN SENTENCES, after the service's clauses and before the terms: a box
                  for the one the client signs (South Carolina's blood-donor notice, R.60-111 § 500.C) and plain
                  words for the one they are given (its grievance procedure, § 1002). */}
              {notices.map((notice) =>
                notice.acknowledge ? (
                  <NoticeItem
                    key={notice.id}
                    noticeId={notice.id}
                    text={noticeText(notice, lang, client?.public_data)}
                    ticked={Boolean(noticeTicks[notice.id])}
                    boxRefusalUp={boxRefusalUp}
                    T={T}
                    isMobile={isMobile}
                    onTick={tickNotice}
                  />
                ) : notice.sections_en ? (
                  <NoticeSheet
                    key={notice.id}
                    notice={notice}
                    lang={lang}
                    line={noticeText(notice, lang, client?.public_data)}
                    site={sheetSite(notice)}
                    procedures={procedures}
                    placement={form.locationOnBody}
                    open={Boolean(sheetsOpen[notice.id])}
                    onToggle={toggleSheet}
                  />
                ) : (
                  // 2026-09-24 — a notice the client is GIVEN sits among boxes they tick, so it is set on the
                  // clauses' own text column with a thin rule at its left: a note, not a stray paragraph (Chris,
                  // reading Shop 53's form under Maryland's risk disclosure: "i'm surprised it's here in-between
                  // checkboxes"). The words and their place are unchanged.
                  // TW-0526 (M03) — and one the rule wants PROMINENT (Louisiana's inks, § XXVIII-107.A) is set in bold on a
                  // darker rule: the words and their place are the same as any other
                  <p
                    key={notice.id}
                    className={`text-base mb-4 ml-9 pl-3 border-l-2 ${notice.prominent ? "border-gray-800 font-bold" : "border-gray-300"}`}
                    data-state-notice={notice.id}
                    data-prominent={notice.prominent ? "true" : undefined}
                  >
                    {noticeText(notice, lang, client?.public_data)}
                  </p>
                )
              )}
              {/* 2026-09-24 — the shop's own clauses, each a box in the owner's words, after the state's sentences
                  and before the terms. Shown as written in either language: they are the shop's words. */}
              {ownClauses.map((clause) => (
                <OwnClauseItem
                  key={clause.id}
                  clauseId={clause.id}
                  text={clause.text}
                  ticked={Boolean(ownClauseTicks[clause.id])}
                  boxRefusalUp={boxRefusalUp}
                  T={T}
                  isMobile={isMobile}
                  onTick={tickOwnClause}
                />
              ))}
              {/* 2026-10-02 — cash preferred, the card-fee box, after the shop's own clauses and before the terms
                  (Reckless Heart Tattoo). Drawn only for a shop that turned it on. */}
              {cashPreferred && (
                <CashPreferredItem
                  settingId={cashPreferred.id}
                  sentence={cashPreferredSentence(cashPreferred, lang)}
                  ticked={cashPreferredTicked}
                  boxRefusalUp={boxRefusalUp}
                  T={T}
                  isMobile={isMobile}
                  onTick={tickCashPreferred}
                />
              )}
              {/* 2026-10-08 — a shop in HIPAA mode: the practice's notice of privacy practices and its box, then the photo
                  release, each its own section, before the terms and the signature. Nothing for any other shop. */}
              {client.placeholder || !privacyNotice ? null : (
                <PrivacyNoticeItem
                  title={privacyNotice.title}
                  text={privacyNotice.text}
                  version={privacyNotice.version}
                  ticked={privacyNoticeTicked}
                  T={T}
                  isMobile={isMobile}
                  onTick={tickPrivacyNotice}
                />
              )}
              {client.placeholder || !photoRelease ? null : (
                <PhotoReleaseItem
                  shopName={client.name}
                  years={photoRelease.years}
                  signed={photoReleaseSigned}
                  T={T}
                  onTick={setPhotoReleaseSigned}
                />
              )}
              {/* TW-0113 (2026-09-08) — TWO BOXES, AND ONLY THE FIRST IS REQUIRED. Until today
                  this was one required box whose sentence ran "…the terms and conditions, including
                  hearing from <shop> by text, email or social media…", so nobody could sign without
                  agreeing to be marketed to. That is consent as a condition of service: not the
                  prior express written consent the TCPA asks for, and the exact thing an A2P 10DLC
                  campaign reviewer rejects. The terms box is now just the terms; hearing from the
                  shop is its own box, unticked, and the signature does not depend on it. */}
              <AcknowledgmentItem {...clausePiece("termsAndConditions")} label={termsLabel} />
              {!client.placeholder && (
                <AcknowledgmentItem
                  {...clausePiece("hearFromShop")}
                  optional
                  label={T.hearFromShop(client.name)}
                />
              )}
              {!client.placeholder && (
                <AcknowledgmentItem
                  {...clausePiece("shopMarketing")}
                  optional
                  label={T.shopMarketing(client.name)}
                />
              )}
              {/* TW-0111 — THE POLICY LINKS, DIRECTLY UNDER THE CONSENT BOXES. Three A2P 10DLC
                  filings were rejected 30908 on MESSAGE_FLOW ("a compliant privacy policy can not be
                  verified") while the privacy page was live and correct every time. The route was the
                  fault: this page captures the consent and linked only the shop's own consent page,
                  so the word "privacy" never appeared where a reviewer looks. These point at the two
                  URLs the campaign filing itself names (config.tsx), so the reviewer's click and the
                  filing agree. Also in the noscript block of public/index.html, for a reviewer or
                  scanner that does not run JavaScript and would otherwise receive an empty page. */}
              <p className="text-sm text-gray-600 mt-4">
                {T.policiesLead}
                <a href={SITE_PRIVACY_URL} className="underline" target="_blank" rel="noreferrer">
                  {T.privacyLink}
                </a>
                {T.policiesJoin}
                <a href={SITE_TERMS_URL} className="underline" target="_blank" rel="noreferrer">
                  {T.siteTermsLink}
                </a>
              </p>
            </div>
            {/* TW-0153 — AN EMANCIPATED MINOR SIGNS FOR THEMSELVES, in the states whose rule says
                so. Above the guardian section rather than inside it, because a yes CLOSES that
                section: there is no parent to sign, which is what emancipation means. Before this
                the form handed an emancipated 17-year-old in Reno a form they could not complete. */}
            {askEmancipated && (
              <div className="mt-8 border-2 border-black p-4">
                <div className="text-xl mb-2">{T.minorEmancipatedAsk}</div>
                <div className="flex space-x-4">
                  {(["yes", "no"] as const).map((choice) => (
                    <label
                      key={choice}
                      className={`flex items-center border-2 p-2 cursor-pointer ${
                        form.emancipatedMinor === choice
                          ? "border-blue-600 bg-blue-50 font-semibold"
                          : "border-gray-300"
                      }`}
                    >
                      <input
                        type="radio"
                        className="form-radio h-5 w-5 text-gray-600 me-2"
                        name="emancipatedMinor"
                        value={choice}
                        checked={form.emancipatedMinor === choice}
                        onChange={handleInputChange}
                      />
                      {choice === "yes" ? T.minorCoverupYes : T.minorCoverupNo}
                    </label>
                  ))}
                </div>
                {emancipated && (
                  <p className="mt-2 text-sm">
                    {T.minorEmancipatedNote(stateNameOf(client?.state ?? undefined))}
                  </p>
                )}
              </div>
            )}
            {/* TW-0128 — A MINOR'S PIERCING WITH A PARENT PRESENT. Shown only when all three
                gates opened. The state's own conditions are quoted rather than summarised: they are
                what the parent is consenting to, they differ in every state, and the shop is the
                one who has to satisfy them. */}
            {guardianRoute && (
              <div className="mt-8 border-2 border-black p-4">
                <h2 className="text-2xl font-bold mb-2">{T.guardianSection}</h2>
                {!client.placeholder && <p className="mb-4">{T.guardianWhy(client.name)}</p>}
                {askCoverup && (
                  <div className="mb-4">
                    <div className="text-xl mb-2">{T.minorCoverupAsk}</div>
                    <div className="flex space-x-4">
                      {(["yes", "no"] as const).map((choice) => (
                        <label
                          key={choice}
                          className={`flex items-center border-2 p-2 cursor-pointer ${
                            form.coveringExisting === choice
                              ? "border-blue-600 bg-blue-50 font-semibold"
                              : "border-gray-300"
                          }`}
                        >
                          <input
                            type="radio"
                            className="form-radio h-5 w-5 text-gray-600 me-2"
                            name="coveringExisting"
                            value={choice}
                            checked={form.coveringExisting === choice}
                            onChange={handleInputChange}
                          />
                          {choice === "yes" ? T.minorCoverupYes : T.minorCoverupNo}
                        </label>
                      ))}
                    </div>
                    {coverupRefusalMessage && (
                      <div className="mt-2">
                        <ErrorMessage message={coverupRefusalMessage} />
                      </div>
                    )}
                  </div>
                )}
                {guardianConditions && (
                  <div className="mb-4 bg-gray-100 p-3">
                    <div className="font-semibold">
                      {guardianConditionsHeading}
                    </div>
                    <p className="text-sm mt-1">{guardianConditions}</p>
                  </div>
                )}
                <div className="flex flex-col space-y-2 mb-4 sm:flex-row sm:space-x-4 sm:space-y-0">
                  <div className="w-full sm:w-1/3 text-xl">{T.guardianName}</div>
                  <div className="w-full sm:w-2/3">
                    <input
                      className="w-full border-2 p-2"
                      placeholder={T.guardianNamePlaceholder}
                      name="guardianName"
                      aria-label={T.guardianName}
                      value={form.guardianName}
                      onChange={handleInputChange}
                      maxLength={FIELD_LIMITS.guardianName}
                    />
                  </div>
                </div>
                <div className="flex flex-col space-y-2 mb-4 sm:flex-row sm:space-x-4 sm:space-y-0">
                  <div className="w-full sm:w-1/3 text-xl">{T.guardianRelationship}</div>
                  <div className="w-full sm:w-2/3">
                    <input
                      className="w-full border-2 p-2"
                      placeholder={T.guardianRelationshipPlaceholder}
                      name="guardianRelationship"
                      aria-label={T.guardianRelationship}
                      value={form.guardianRelationship}
                      onChange={handleInputChange}
                      maxLength={FIELD_LIMITS.guardianRelationship}
                    />
                  </div>
                </div>
                {/* TW-0526, the client-form side (2026-10-29) — the parent's or guardian's phone, under their relationship, on every
                    minor's route: optional, and required where the shop's state's rule names it (Utah's) */}
                {guardianFields.map((box) => (
                  <StateFieldBox key={box.key} {...stateFieldPiece(box)} />
                ))}
                {/* TW-0267 — THE STATE'S OWN STATEMENT, ABOVE THE SIGNATURE THAT SIGNS IT. Tennessee's rule sets the words
                    ("a statement with the exact wording as follows", 1200-23-06-.03(3)(c)), so they are printed verbatim
                    and in English whatever language the page is in; the line above them says so in the reader's own.
                    Required: the submit refuses it unticked, and so does the API. */}
                {certification && (
                  <div className="mb-4 border-2 border-gray-300 p-3" data-refusal="no_guardian_certification">
                    <div className="font-semibold mb-2">{statementLead(certification)}</div>
                    {/* AcknowledgmentItem's markup, aligned to the top: the statement runs to several lines on a phone */}
                    <div className="flex items-start">
                      <span className="me-2 h-100 mt-1">
                        <input
                          id="guardian-certification"
                          name="guardianCertification"
                          type="checkbox"
                          className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                          checked={guardianCertified}
                          onChange={(e) => setGuardianCertified(e.target.checked)}
                          required={!isMobile}
                        />
                      </span>
                      <label
                        htmlFor="guardian-certification"
                        className="ml-2 text-base font-medium"
                        lang={certification.exact === false || certification.translated === true ? lang : "en"}
                        data-guardian-certification={certification.id}
                      >
                        {certificationWords}
                      </label>
                    </div>
                  </div>
                )}
                {/* TW-0526, the client-form side (2026-10-29) — EVERY STATEMENT AFTER THE FIRST the rule has the parent tick, each its
                    own box in the first's markup, in the rule's order (Illinois's word that they sign in front of the operator or body
                    artist, and its provision for a piercing in the mouth, drawn only for a spot in the mouth). Required: the submit
                    refuses one unticked, naming it, and so does the API. */}
                {furtherStatements.map((statement) => (
                  <div
                    key={statement.id}
                    className="mb-4 border-2 border-gray-300 p-3"
                    data-refusal={`no_guardian_certification.${statement.id}`}
                  >
                    <div className="font-semibold mb-2">{statementLead(statement)}</div>
                    <div className="flex items-start">
                      <span className="me-2 h-100 mt-1">
                        <input
                          id={`guardian-statement-${statement.id}`}
                          name={`guardianStatements.${statement.id}`}
                          type="checkbox"
                          className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                          checked={statementTicks[statement.id] === true}
                          onChange={(e) => setStatementTicks({ ...statementTicks, [statement.id]: e.target.checked })}
                          required={!isMobile}
                        />
                      </span>
                      <label
                        htmlFor={`guardian-statement-${statement.id}`}
                        className="ml-2 text-base font-medium"
                        lang={statement.exact === false || statement.translated === true ? lang : "en"}
                        data-guardian-certification={statement.id}
                      >
                        {statementWords(statement)}
                      </label>
                    </div>
                  </div>
                ))}
                <div className="flex flex-col space-y-2 sm:flex-row sm:space-x-4 sm:space-y-0">
                  <div className="w-full sm:w-1/3 text-xl">{T.guardianSignature}</div>
                  <div className="w-full sm:w-2/3">
                    <SignatureCanvasContainer
                      className="flex flex-col w-full relative"
                      style={{ height: 200 }}
                    >
                      <SignatureCanvas
                        ref={guardianSignatureRef}
                        penColor="black"
                        canvasProps={{ className: "sigCanvas" }}
                        backgroundColor="white"
                        clearOnResize={false}
                        onBegin={onGuardianSignatureStart}
                        onEnd={onGuardianSignatureEnd}
                      />
                      {guardianSignatureIsEmpty && (
                        <div className="absolute top-0 right-0 bottom-0 left-0 flex flex-col justify-center items-center pointer-events-none">
                          <span className="text-lg text-gray-500">{T.signHere}</span>
                        </div>
                      )}
                    </SignatureCanvasContainer>
                    <div className="w-full flex justify-end mt-2">
                      <button
                        type="button"
                        className="bg-transparent hover:bg-blue-700 text-blue-700 font-semibold hover:text-white py-2 px-4 border border-blue-500 hover:border-transparent rounded"
                        onClick={() => {
                          guardianSignatureRef.current.clear();
                          setGuardianSignatureIsEmpty(true);
                        }}
                      >
                        {T.clear}
                      </button>
                    </div>
                  </div>
                </div>
                {/* TW-0227 — THE OTHER PARENT'S WRITTEN PERMISSION, where the shop's city or county wants both parents
                    (Hammond, East Chicago) or both parents here (Delaware County). One of three ways, picked by the
                    parent who is here; where both have to be here the photo is not one of them. The signed waiver
                    prints which way it was, and the other parent's signature beside this parent's. */}
                {askSecondParent && (
                  <div className="mt-6 border-t-2 border-black pt-4" data-testid="second-parent">
                    <h3 className="text-xl font-bold mb-2">{T.secondParentTitle}</h3>
                    <p className="mb-3">
                      {secondParentHere ? T.secondParentWhyPresent(secondParentWho) : T.secondParentWhy(secondParentWho)}
                    </p>
                    <div role="radiogroup" aria-label={T.secondParentTitle} className="flex flex-col space-y-2 mb-4">
                      {secondParentWays.map((way) => (
                        <label
                          key={way}
                          className={`flex items-start border-2 p-2 cursor-pointer ${
                            secondParentChoice === way ? "border-blue-600 bg-blue-50 font-semibold" : "border-gray-300"
                          }`}
                        >
                          <input
                            type="radio"
                            className="form-radio h-5 w-5 text-gray-600 me-2 mt-1 shrink-0"
                            name="secondParentChoice"
                            value={way}
                            checked={secondParentChoice === way}
                            onChange={handleInputChange}
                          />
                          {way === "signed" ? T.secondParentSigns : way === "photo" ? T.secondParentPhotoOption : T.soleParent}
                        </label>
                      ))}
                    </div>
                    {secondParentChoice === "signed" && (
                      <>
                        <div className="flex flex-col space-y-2 mb-4 sm:flex-row sm:space-x-4 sm:space-y-0">
                          <div className="w-full sm:w-1/3 text-xl">{T.secondParentName}</div>
                          <div className="w-full sm:w-2/3">
                            <input
                              className="w-full border-2 p-2"
                              placeholder={T.secondParentNamePlaceholder}
                              name="secondParentName"
                              aria-label={T.secondParentName}
                              value={form.secondParentName}
                              onChange={handleInputChange}
                              maxLength={FIELD_LIMITS.secondParentName}
                            />
                          </div>
                        </div>
                        <div className="flex flex-col space-y-2 sm:flex-row sm:space-x-4 sm:space-y-0">
                          <div className="w-full sm:w-1/3 text-xl">{T.secondParentSignature}</div>
                          <div className="w-full sm:w-2/3">
                            <SignatureCanvasContainer
                              className="flex flex-col w-full relative"
                              style={{ height: 200 }}
                            >
                              <SignatureCanvas
                                ref={secondParentSignatureRef}
                                penColor="black"
                                canvasProps={{ className: "sigCanvas", "aria-label": T.secondParentSignature }}
                                backgroundColor="white"
                                clearOnResize={false}
                                onBegin={onSecondParentSignatureStart}
                                onEnd={onSecondParentSignatureEnd}
                              />
                              {secondParentSignatureIsEmpty && (
                                <div className="absolute top-0 right-0 bottom-0 left-0 flex flex-col justify-center items-center pointer-events-none">
                                  <span className="text-lg text-gray-500">{T.signHere}</span>
                                </div>
                              )}
                            </SignatureCanvasContainer>
                            <div className="w-full flex justify-end mt-2">
                              <button
                                type="button"
                                className="bg-transparent hover:bg-blue-700 text-blue-700 font-semibold hover:text-white py-2 px-4 border border-blue-500 hover:border-transparent rounded"
                                onClick={() => {
                                  secondParentSignatureRef.current?.clear();
                                  setSecondParentSignatureIsEmpty(true);
                                }}
                              >
                                {T.clear}
                              </button>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                    {secondParentChoice === "photo" && (
                      <div className="flex flex-col space-y-2 sm:flex-row sm:space-x-4 sm:space-y-0">
                        <div className="w-full sm:w-1/3 text-xl">
                          {T.secondParentPhoto}
                          <div className="text-sm text-gray-500 mt-1">{T.secondParentPhotoNote}</div>
                        </div>
                        <div className="w-full sm:w-2/3">
                          <FormPhotoUploader
                            takingPhoto={takingPhotoOfSecondParentPermission}
                            setTakingPhoto={setTakingPhotoOfSecondParentPermission}
                            setSubmissionError={setSubmissionError}
                            photo={photoOfSecondParentPermission}
                            onCameraFailed={cameraFailed("photoOfSecondParentPermission", "minor")}
                            setPhoto={setPhotoOfSecondParentPermission}
                            photoAltText="Photo of the other parent's signed permission"
                            T={T}
                            allowLibrary={libraryOn}
                            onSource={setSecondParentPermissionSource}
                            libraryTestId="second-parent-photo-library-input"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            <div className="mt-4">
              <div className="flex flex-col space-y-2 mb-4 sm:flex-row sm:space-x-4 sm:space-y-0">
                <div className="w-full sm:w-1/3 text-xl">{T.signedDate}</div>
                <div className="w-full sm:w-2/3">
                  <div className="flex flex-col w-full">
                    <input
                      className="w-full border-2 p-2"
                      placeholder="MM-DD-YYYY"
                      name="signedDate"
                      aria-label={T.signedDate}
                      value={form.signedDate}
                      onChange={handleInputChange}
                      required
                      type="date"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-4">
              <div className="flex flex-col space-y-2 mb-4 sm:flex-row sm:space-x-4 sm:space-y-0" data-refusal="no_license_photo">
                {/* TW-0128 — whose ID this is changes when a minor is signing: every state that
                    allows this wants the PARENT's photo ID on file, and the minor usually has no
                    licence at all. One uploader, one meaning, said out loud.
                    TW-0488 (2026-10-26) — and it is the client's ID, whatever kind: "Driver's License" stood here until a
                    Florida shop's visitors from abroad read it as a card they did not have (idType.ts). */}
                <div className="w-full sm:w-1/3 text-xl">
                  {guardianRoute ? T.guardianIdPhoto : T.idPhoto}
                  <div className="text-sm text-gray-500 mt-1">{guardianRoute ? T.guardianIdNote : T.idPhotoNote}</div>
                </div>
                <div className="w-full sm:w-2/3">
                  {/* TW-0488 — WHICH ID IT IS: four choices on every page, a refusal only where the shop's state's rule keeps the
                      answer (Kansas, Texas, Maine, Missouri). Radios sharing one name, so a refusal finds them (refusal.ts). */}
                  <fieldset className="mb-3" data-testid="id-type">
                    <legend className="text-base mb-1">{T.idTypeQuestion}</legend>
                    <div className="flex flex-wrap gap-x-4 gap-y-1">
                      {ID_TYPES.map((code) => (
                        <label key={code} className="inline-flex items-center gap-2 py-1 text-base">
                          <input
                            type="radio"
                            name="idType"
                            value={code}
                            checked={idType === code}
                            onChange={() => {
                              setIdType(code);
                              if (submissionError === T.needIdType) setSubmissionError("");
                            }}
                          />
                          {T.idTypeNames[code]}
                        </label>
                      ))}
                    </div>
                    {refusedFor === "no_id_type" && !isIdType(idType) && submissionError && (
                      <div className="mt-1 text-base" role="alert">
                        <span className="text-red-700 font-semibold">{submissionError}</span>
                      </div>
                    )}
                  </fieldset>
                  {/* TW-0488 — AND ITS NUMBER, only where the shop's state's rule keeps it (Maine's serial number, Missouri's
                      license or picture ID number), taken as typed: never a refusal for a shape, only for a box with no letter
                      or digit in it. The hint follows the ID picked. */}
                  {askIdNumber && (
                    <div className="mb-3">
                      <label className="block text-base mb-1" htmlFor="id-number">{T.idNumber}</label>
                      <input
                        id="id-number"
                        className="w-full border-2 p-2"
                        name="idNumber"
                        placeholder={T.idNumberPlaceholder[idType]}
                        maxLength={FIELD_LIMITS.idNumber}
                        autoComplete="off"
                        autoCapitalize="characters"
                        spellCheck={false}
                        value={form.idNumber}
                        onChange={(e) => {
                          handleInputChange(e);
                          if (submissionError === T.needIdNumber) setSubmissionError("");
                        }}
                      />
                      <div className="text-sm text-gray-500 mt-1">{T.idNumberWhy(stateNameOf(client?.state ?? undefined))}</div>
                      {refusedFor === "no_id_number" && !idNumberGiven(form.idNumber) && submissionError && (
                        <div className="mt-1 text-base" role="alert">
                          <span className="text-red-700 font-semibold">{submissionError}</span>
                        </div>
                      )}
                    </div>
                  )}
                  <FormPhotoUploader
                    takingPhoto={takingPhotoOfDriversLicense}
                    setTakingPhoto={setTakingPhotoOfDriversLicense}
                    setSubmissionError={setSubmissionError}
                    photo={photoOfDriversLicense}
                    onCameraFailed={cameraFailed("photoOfDriversLicense", "id")}
                    setPhoto={setPhotoOfDriversLicense}
                    photoAltText="Photo of the ID"
                    T={T}
                    allowLibrary={libraryOn}
                    onSource={setIdPhotoSource}
                  />
                </div>
              </div>
            </div>
            {/* TW-0153 — AND THE MINOR'S OWN, where the state or county asks for both. The uploader
                above is the PARENT's on a guardian route, so without this a shop in Florida or
                Nevada took the parent's ID twice over and the minor's never. Each one says whose it
                is; a shop filing these has to be able to tell them apart a year later. */}
            {askMinorId && (
              <div className="mt-4">
                {/* TW-0501 — marked, so a refusal of it (the form's own, or the door's MINOR_ID_PHOTO_MISSING) takes the client here */}
                <div className="flex flex-col space-y-2 mb-4 sm:flex-row sm:space-x-4 sm:space-y-0" data-refusal="no_minor_id_photo">
                  <div className="w-full sm:w-1/3 text-xl">
                    {T.minorIdPhoto}
                    <div className="text-sm text-gray-500 mt-1">{T.minorIdNote}</div>
                  </div>
                  <div className="w-full sm:w-2/3">
                    <FormPhotoUploader
                      takingPhoto={takingPhotoOfMinorId}
                      setTakingPhoto={setTakingPhotoOfMinorId}
                      setSubmissionError={setSubmissionError}
                      photo={photoOfMinorId}
                      onCameraFailed={cameraFailed("photoOfMinorId", "minor")}
                      setPhoto={setPhotoOfMinorId}
                      photoAltText="Photo of the minor's ID"
                      T={T}
                      allowLibrary={libraryOn}
                      onSource={setMinorIdPhotoSource}
                    />
                  </div>
                </div>
              </div>
            )}
            {/* 2026-09-24 — THE PAPER THAT SAYS THIS ADULT IS THE PARENT OR GUARDIAN, where the shop or the
                state asks for it: the minor's birth certificate, or the adoption, custody or guardianship
                order. Michigan, Nebraska and Nevada say the file keeps a copy, and until this the signed
                waiver could not hold one. It prints on a page of its own, after the parent's consent page.
                Kelly Voris of The Tattoo Lady (Hammond IN), on the ad: "Is there a way to attach id's and
                or birth certificate to it?" */}
            {askGuardianProof && (
              <div className="mt-4">
                {/* TW-0501 — marked the same way, for its own refusal and the door's GUARDIAN_PROOF_MISSING */}
                <div className="flex flex-col space-y-2 mb-4 sm:flex-row sm:space-x-4 sm:space-y-0" data-refusal="no_guardian_proof_photo">
                  <div className="w-full sm:w-1/3 text-xl">
                    {T.guardianProofPhoto}
                    <div className="text-sm text-gray-500 mt-1">{T.guardianProofNote}</div>
                  </div>
                  <div className="w-full sm:w-2/3">
                    <FormPhotoUploader
                      takingPhoto={takingPhotoOfGuardianProof}
                      setTakingPhoto={setTakingPhotoOfGuardianProof}
                      setSubmissionError={setSubmissionError}
                      photo={photoOfGuardianProof}
                      onCameraFailed={cameraFailed("photoOfGuardianProof", "minor")}
                      setPhoto={setPhotoOfGuardianProof}
                      photoAltText="Photo of the birth certificate or guardianship papers"
                      T={T}
                      allowLibrary={libraryOn}
                      onSource={setGuardianProofSource}
                    />
                  </div>
                </div>
              </div>
            )}
            {/* TW-0270 slice 5 (2026-10-16) — THE PAPERS THE STATE'S RULE KEEPS WITH A MINOR'S RECORD, one photo each, in the
                guardian papers' shape (minors.ts papersAsked): Kentucky's written notarized statement, Florida's notarized
                consent on form DH 4146, Arkansas's Department-approved consent form, Texas's affidavit, St. Louis County's
                notarized consent, Nevada's certified copy of an emancipation decree. Each is labelled in the rule's own words
                and says whose rule it is, and prints on a page of its own in the signed waiver. The rule asks whatever the
                shop's switches say, and the API refuses a signing missing one (MINOR_PAPER_MISSING). Each block is marked with
                its paper's id, because one signing can ask for two (a Texas piercing), so a refusal takes the person to the
                one it is about, and says its sentence there too. */}
            {papers.map((paper) => (
              <div className="mt-4" key={paper.id}>
                <div
                  className="flex flex-col space-y-2 mb-4 sm:flex-row sm:space-x-4 sm:space-y-0"
                  data-refusal={`no_paper_photo.${paper.id}`}
                  data-testid={`paper-${paper.id}`}
                >
                  <div className="w-full sm:w-1/3 text-xl">
                    {T.paperPhoto(paper)}
                    <div className="text-sm text-gray-500 mt-1">
                      {T.paperNote(paper.place || stateNameOf(client?.state ?? undefined))}
                    </div>
                    {!paperPhotos[paper.id] && refusedFor === "no_paper_photo" && refusedDetail === paper.id && submissionError && (
                      <div className="mt-2 text-base" role="alert">
                        <span className="text-red-700 font-semibold">{submissionError}</span>
                      </div>
                    )}
                  </div>
                  <div className="w-full sm:w-2/3">
                    <FormPhotoUploader
                      takingPhoto={takingPhotoOfPaper === paper.id}
                      setTakingPhoto={(taking) => setTakingPhotoOfPaper(taking ? paper.id : null)}
                      setSubmissionError={setSubmissionError}
                      photo={paperPhotos[paper.id] ?? null}
                      onCameraFailed={cameraFailed(`minorPapers.${paper.id}`, "minor")}
                      setPhoto={(photo) =>
                        setPaperPhotos((all) => {
                          const next = { ...all };
                          if (photo) next[paper.id] = photo;
                          else delete next[paper.id];
                          return next;
                        })
                      }
                      photoAltText={`Photo of the ${paper.name}`}
                      T={T}
                      allowLibrary={libraryOn}
                      onSource={(source) => setPaperSources((all) => ({ ...all, [paper.id]: source }))}
                      libraryTestId={`paper-${paper.id}-library-input`}
                    />
                  </div>
                </div>
              </div>
            ))}
            {/* TW-0279 slice 10 — THE CLIENT'S HEALTH CARE PROVIDER'S WRITTEN INSTRUCTIONS, the guardian papers' way, where the
                shop's state's rule names them or its own switch asks (cosmetic.ts). New Jersey: areola restoration and
                camouflage are done "in accordance with specific written instructions issued by the client's health care
                provider, which the practitioner shall maintain with the client's application" (N.J.A.C. 8:27-4.4(a)), and its
                camouflage includes scars from surgery or a hair transplant (8:27-1.3). So a scalp pick asks first whether
                the work covers scars, and the photo is asked for areola or camouflage work, or scalp work that does, with the
                rule's own plain sentence under it (or the shop's, where its switch asks). It prints on a page of its own at
                the end of the signed waiver. OPTIONAL since the same night (slices 8 and 10 made one shape), and said so
                beside its label: nothing about it stops the signing, and the shop adds the photo from its dashboard before the
                artist can sign (the API's prescription.py). The block is marked for the tests and nothing else. */}
            {askScalpScars && (
              <div className="mt-4">
                <div
                  className="flex flex-col space-y-2 mb-4 sm:flex-row sm:space-x-4 sm:space-y-0"
                  data-refusal="scalp_scars_unanswered"
                  data-testid="scalp-scars-question"
                >
                  <div className="w-full sm:w-1/3 text-xl">{T.scalpScarsAsk}</div>
                  <div className="w-full sm:w-2/3">
                    <div className="flex space-x-4">
                      {(["yes", "no"] as const).map((choice) => (
                        <label
                          key={choice}
                          className={`flex items-center border-2 p-2 cursor-pointer ${
                            form.scalpCoversScars === choice ? "border-blue-600 bg-blue-50 font-semibold" : "border-gray-300"
                          }`}
                        >
                          <input
                            type="radio"
                            className="form-radio h-5 w-5 text-gray-600 me-2"
                            name="scalpCoversScars"
                            value={choice}
                            checked={form.scalpCoversScars === choice}
                            onChange={handleInputChange}
                          />
                          {choice === "yes" ? T.minorCoverupYes : T.minorCoverupNo}
                        </label>
                      ))}
                    </div>
                    {!wantProviderInstructions && (
                      <p className="text-sm text-gray-600 mt-2" data-provider-instructions-why="">
                        {providerInstructionsWhy}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
            {wantProviderInstructions && (
              <div className="mt-4">
                <div
                  className="flex flex-col space-y-2 mb-4 sm:flex-row sm:space-x-4 sm:space-y-0"
                  data-provider-instructions-photo=""
                >
                  <div className="w-full sm:w-1/3 text-xl">
                    {T.providerInstructionsPhoto} <span className="text-sm text-gray-500">{T.optional}</span>
                    <div className="text-sm text-gray-500 mt-1">{T.providerInstructionsNote}</div>
                  </div>
                  <div className="w-full sm:w-2/3">
                    <FormPhotoUploader
                      takingPhoto={takingPhotoOfProviderInstructions}
                      setTakingPhoto={setTakingPhotoOfProviderInstructions}
                      setSubmissionError={setSubmissionError}
                      photo={photoOfProviderInstructions}
                      onCameraFailed={cameraFailed("photoOfProviderInstructions", "health")}
                      setPhoto={setPhotoOfProviderInstructions}
                      photoAltText="Photo of the doctor's written instructions"
                      T={T}
                      allowLibrary={libraryOn}
                      onSource={setProviderInstructionsSource}
                      libraryTestId="provider-instructions-library-input"
                    />
                    <p className="text-sm text-gray-600 mt-2" data-provider-instructions-why="">
                      {providerInstructionsWhy}
                    </p>
                  </div>
                </div>
              </div>
            )}
            <div className="mt-4">
              <div className="flex flex-col space-y-2 mb-4 sm:flex-row sm:space-x-4 sm:space-y-0" data-refusal="no_signature">
                <div className="w-full sm:w-1/3 text-xl">{T.clientSignature}</div>
                <div className="w-full sm:w-2/3">
                  <SignatureCanvasContainer
                    className="flex flex-col w-full relative"
                    style={{ height: 200 }}
                  >
                    <SignatureCanvas
                      ref={signatureRef}
                      penColor="black"
                      canvasProps={{
                        className: "sigCanvas",
                      }}
                      backgroundColor="white"
                      clearOnResize={false}
                      onBegin={onSignatureStart}
                      onEnd={onSignatureEnd}
                    />
                    {signatureIsEmpty && (
                      <div className="absolute top-0 right-0 bottom-0 left-0 flex flex-col justify-center items-center pointer-events-none">
                        <span className="text-lg text-gray-500">{T.signHere}</span>
                      </div>
                    )}
                  </SignatureCanvasContainer>
                  {/* Small clear button to the right */}
                  <div className="w-full flex justify-end mt-2">
                    <button
                      type="button"
                      className="bg-transparent hover:bg-blue-700 text-blue-700 font-semibold hover:text-white py-2 px-4 border border-blue-500 hover:border-transparent rounded"
                      onClick={() => {
                        signatureRef.current.clear();
                        setSignatureIsEmpty(true);
                      }}
                    >
                      {T.clear}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            {wantLotPhotos && <div className="additional-pictures">
              <h2 className="text-2xl font-bold mt-6 mb-4">
                {isCosmetic ? T.batchNumbersCosmetic : T.batchNumbers}
              </h2>
              <div className="mt-4">
                {/* 2026-10-31 — `data-refusal`: a required photo's refusal takes the client here (refusal.ts, lotPhotos.ts) */}
                <div className="flex flex-col space-y-2 mb-4 sm:flex-row sm:space-x-4 sm:space-y-0" data-refusal="no_needle_photo">
                  <div className="w-full sm:w-1/3 text-xl">
                    {T.needleBatch}
                    {requiredLotPhotos.needle ? <div className="text-sm text-gray-500 mt-1">{T.lotPhotoRequired}</div> : null}
                  </div>
                  <div className="w-full sm:w-2/3">
                    <FormPhotoUploader
                      takingPhoto={takingPhotoOfNeedleBatchNumber}
                      setTakingPhoto={setTakingPhotoOfNeedleBatchNumber}
                      setSubmissionError={setSubmissionError}
                      photo={photoOfNeedleBatchNumber}
                      onCameraFailed={cameraFailed("photoOfNeedleBatchNumber", "signature")}
                      setPhoto={setPhotoOfNeedleBatchNumber}
                      photoAltText="Photo of needle batch #"
                      T={T}
                    />
                  </div>
                </div>
              </div>
              <div className="mt-4">
                <div className="flex flex-col space-y-2 mb-4 sm:flex-row sm:space-x-4 sm:space-y-0" data-refusal="no_ink_photo">
                  <div className="w-full sm:w-1/3 text-xl">
                    {isCosmetic ? T.pigmentBatch : T.inkBatch}
                    {requiredLotPhotos.ink ? <div className="text-sm text-gray-500 mt-1">{T.lotPhotoRequired}</div> : null}
                  </div>
                  <div className="w-full sm:w-2/3">
                    <FormPhotoUploader
                      takingPhoto={takingPhotoOfInkBatchNumber}
                      setTakingPhoto={setTakingPhotoOfInkBatchNumber}
                      setSubmissionError={setSubmissionError}
                      photo={photoOfInkBatchNumber}
                      onCameraFailed={cameraFailed("photoOfInkBatchNumber", "signature")}
                      setPhoto={setPhotoOfInkBatchNumber}
                      photoAltText="Photo of ink batch #"
                      T={T}
                    />
                  </div>
                </div>
              </div>
            </div>}
          </div>
          <div className="mt-2 pb-8">
            {/* Submit button, centered */}
            <div className="text-center">
              {/* The one tap at the bottom of a long page: the full width of a phone, its own weight;
                  on a desktop the same button at its natural size. */}
              <button
                type="submit"
                className={`w-full sm:w-auto py-3 px-10 text-lg font-semibold bg-blue-600 text-white rounded-lg${submitting ? " opacity-50 cursor-not-allowed" : ""
                  }`}
                disabled={submitting}
              >
                {!submitting ? T.submit : T.submitting}
              </button>
            </div>
            {/* The sentence a client reads when the Submit tap did nothing, so it is the one
                sentence on the page that must never be hard to read: red-500 measured 3.76:1 on
                white (2026-09-07), red-700 is 6.5:1, and the weight carries the alarm for a reader
                who does not see the red at all. */}
            {submissionError && (
              <div className="text-center mt-4">
                <span className="text-red-700 font-semibold">{submissionError}</span>
              </div>
            )}
          </div>
        </form>
      )}
    </div>
  );
}

export default Form;
